import { useState, useRef, useEffect } from "react";
import { t, getLang, getLangName, setLang, useLang, LANGUAGES } from "./i18n";
import { supabase } from "./supabaseClient";

// Clean inline-SVG icon set (Feather/Lucide style) — replaces classic emojis across the UI
function Icon({ name, size = 16, color = "currentColor", strokeWidth = 1.5, style, fill = "none" }) {
  const p = {
    width: size,
    height: size,
    viewBox: "0 0 24 24",
    fill,
    stroke: color,
    strokeWidth,
    strokeLinecap: "round",
    strokeLinejoin: "round",
    style: { display: "block", ...style },
  };
  switch (name) {
    case "target":
      return (<svg {...p}><circle cx="12" cy="12" r="9" /><circle cx="12" cy="12" r="5" /><circle cx="12" cy="12" r="1.5" fill={color} stroke="none" /></svg>);
    case "odds":
      return (<svg {...p}><circle cx="12" cy="12" r="9" /><path d="M15 9.5a2.5 2 0 0 0-2.5-1.5h-1a2 2 0 0 0 0 4h1a2 2 0 0 1 0 4h-1A2.5 2 0 0 1 9 14.5" /><path d="M12 6.5v1.5M12 16v1.5" /></svg>);
    case "users":
      return (<svg {...p}><path d="M16 20v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2" /><circle cx="9" cy="7" r="4" /><path d="M22 20v-2a4 4 0 0 0-3-3.87" /><path d="M16 3.13a4 4 0 0 1 0 7.75" /></svg>);
    case "chart":
      return (<svg {...p}><line x1="6" y1="20" x2="6" y2="13" /><line x1="12" y1="20" x2="12" y2="4" /><line x1="18" y1="20" x2="18" y2="9" /></svg>);
    case "news":
      return (<svg {...p}><path d="M4 22h14a2 2 0 0 0 2-2V4a1 1 0 0 0-1-1H5a1 1 0 0 0-1 1v16a2 2 0 0 1-2 2 2 2 0 0 1-2-2v-9a1 1 0 0 1 1-1h2" /><path d="M16 6h-6v5h6V6Z" /><path d="M16 14h-6M16 18h-6" /></svg>);
    case "ticket":
      return (<svg {...p}><path d="M2 9a3 3 0 0 1 0 6v2a2 2 0 0 0 2 2h16a2 2 0 0 0 2-2v-2a3 3 0 0 1 0-6V7a2 2 0 0 0-2-2H4a2 2 0 0 0-2 2Z" /><path d="M13 5.5v2M13 16.5v2M13 11v2" /></svg>);
    case "search":
      return (<svg {...p}><circle cx="11" cy="11" r="7" /><line x1="21" y1="21" x2="16.65" y2="16.65" /></svg>);
    case "gem":
      return (<svg {...p}><path d="M6 3h12l4 6-10 12L2 9Z" /><path d="M11 3 8 9l4 12 4-12-3-6" /><path d="M2 9h20" /></svg>);
    case "zap":
      return (<svg {...p}><path d="M13 2 4 14h7l-1 8 9-12h-7l1-8Z" /></svg>);
    case "trophy":
      return (<svg {...p}><path d="M6 9H4.5a2.5 2.5 0 0 1 0-5H6" /><path d="M18 9h1.5a2.5 2.5 0 0 0 0-5H18" /><path d="M4 22h16" /><path d="M10 14.6V18c0 .6-.5 1-1.2 1.2C7.6 19.6 7 20.7 7 22" /><path d="M14 14.6V18c0 .6.5 1 1.2 1.2C16.4 19.6 17 20.7 17 22" /><path d="M18 2H6v7a6 6 0 0 0 12 0V2Z" /></svg>);
    case "ball":
      return (<svg {...p}><circle cx="12" cy="12" r="9" /><path d="m12 7 3 2.2-1.1 3.5h-3.8L9 9.2 12 7Z" /><path d="m12 7 .2-4M15 9.2l3.7-1.3M13.9 12.7l2.3 3.2M10.1 12.7l-2.3 3.2M9 9.2 5.3 7.9" /></svg>);
    case "camera":
      return (<svg {...p}><path d="M3 8a2 2 0 0 1 2-2h2l1.2-2h7.6L19 6h2a2 2 0 0 1 2 2v10a2 2 0 0 1-2 2H3a2 2 0 0 1-2-2V8Z" transform="translate(0 -1)" /><circle cx="12" cy="13" r="3.5" /></svg>);
    case "lock":
      return (<svg {...p}><rect x="4" y="10" width="16" height="10" rx="2" /><path d="M8 10V7a4 4 0 0 1 8 0v3" /></svg>);
    case "user":
      return (<svg {...p}><circle cx="12" cy="8" r="4" /><path d="M4 21v-1a6 6 0 0 1 6-6h4a6 6 0 0 1 6 6v1" /></svg>);
    case "card":
      return (<svg {...p}><rect x="2" y="5" width="20" height="14" rx="2" /><line x1="2" y1="10" x2="22" y2="10" /></svg>);
    case "arrow-up":
      return (<svg {...p}><line x1="12" y1="19" x2="12" y2="5" /><polyline points="6 11 12 5 18 11" /></svg>);
    case "refresh":
      return (<svg {...p}><path d="M21 12a9 9 0 1 1-3-6.7L21 8" /><polyline points="21 3 21 8 16 8" /></svg>);
    case "clipboard":
      return (<svg {...p}><rect x="5" y="4" width="14" height="17" rx="2" /><rect x="8" y="2" width="8" height="4" rx="1" /><path d="M9 11h6M9 15h6" /></svg>);
    case "live":
      return (<svg {...p} fill={color} stroke="none"><circle cx="12" cy="12" r="6" /></svg>);
    case "arrow-right":
      return (<svg {...p}><line x1="5" y1="12" x2="19" y2="12" /><polyline points="13 6 19 12 13 18" /></svg>);
    case "globe":
      return (<svg {...p}><circle cx="12" cy="12" r="9" /><path d="M3 12h18" /><path d="M12 3a14 14 0 0 1 0 18 14 14 0 0 1 0-18Z" /></svg>);
    case "check":
      return (<svg {...p}><polyline points="4 12 9 17 20 6" /></svg>);
    case "alert":
      return (<svg {...p}><path d="M10.3 3.2 1.8 18a2 2 0 0 0 1.7 3h17a2 2 0 0 0 1.7-3L13.7 3.2a2 2 0 0 0-3.4 0Z" /><line x1="12" y1="9" x2="12" y2="13" /><line x1="12" y1="17" x2="12" y2="17" /></svg>);
    case "thumbs-up":
      return (<svg {...p}><path d="M7 11v9a1 1 0 0 1-1 1H4a1 1 0 0 1-1-1v-8a1 1 0 0 1 1-1h3Z" /><path d="M7 11l4-7a2.5 2.5 0 0 1 2.4 3.2L12.5 10H19a2 2 0 0 1 2 2.3l-1.1 6A2 2 0 0 1 18 20H7" /></svg>);
    case "thumbs-down":
      return (<svg {...p}><path d="M17 13V4a1 1 0 0 1 1-1h2a1 1 0 0 1 1 1v8a1 1 0 0 1-1 1h-3Z" /><path d="M17 13l-4 7a2.5 2.5 0 0 1-2.4-3.2l.9-2.8H5a2 2 0 0 1-2-2.3l1.1-6A2 2 0 0 1 6 4h11" /></svg>);
    default:
      return null;
  }
}

// API base URL — empty in dev (uses CRA proxy), set in production via REACT_APP_API_URL
const API_ROOT = process.env.REACT_APP_API_URL || "";
const apiUrl = (path) => `${API_ROOT}${path}`;

const MASCOT_LARGE = "data:image/png;base64,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";
const MASCOT_SMALL = "data:image/png;base64,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";

/* ═══ API-Football v3 Integration (proxied via backend) ═══ */
const API_BASE = `${API_ROOT}/api/football`;

const ODDS_SPORTS = [
  { key: "soccer_fifa_world_cup", name: "World Cup" },
  { key: "soccer_fifa_club_world_cup", name: "Club World Cup" },
  { key: "soccer_epl", name: "Premier League" },
  { key: "soccer_spain_la_liga", name: "La Liga" },
  { key: "soccer_germany_bundesliga", name: "Bundesliga" },
  { key: "soccer_italy_serie_a", name: "Serie A" },
  { key: "soccer_france_ligue_one", name: "Ligue 1" },
  { key: "soccer_uefa_champs_league", name: "Champions League" },
  { key: "soccer_uefa_europa_league", name: "Europa League" },
];

const ALL_BOOKMAKERS_ODDS = [
  { key: "bet365", name: "Bet365", color: "#027B5B" },
  { key: "williamhill", name: "William Hill", color: "#1B3668" },
  { key: "betway", name: "Betway", color: "#00A826" },
  { key: "unibet_eu", name: "Unibet", color: "#147B45" },
  { key: "paddypower", name: "Paddy Power", color: "#004833" },
  { key: "betfair", name: "Betfair", color: "#FFB80C" },
  { key: "onexbet", name: "1xBet", color: "#1A5276" },
  { key: "bwin", name: "Bwin", color: "#F5A623" },
  { key: "pinnacle", name: "Pinnacle", color: "#C0392B" },
  { key: "draftkings", name: "DraftKings", color: "#53D769" },
];

const LEAGUE_IDS = new Set([39, 140, 78, 135, 61, 2, 3, 848, 5, 32, 33, 34, 1, 15, 4]); // PL, La Liga, Bundesliga, Serie A, Ligue 1, CL, EL, Conference League, Nations League, WC Qual Europe, WC Qual South America, WC Qual Asia, FIFA World Cup, FIFA Club World Cup, Euro

// API-Football leagueId → OddsPapi sportKey for cross-source enrichment
const LEAGUE_TO_SPORT_KEY = {
  1: "soccer_fifa_world_cup",
  2: "soccer_uefa_champs_league",
  3: "soccer_uefa_europa_league",
  4: "soccer_uefa_euro",
  5: "soccer_uefa_nations",
  15: "soccer_fifa_club_world_cup",
  39: "soccer_epl",
  61: "soccer_france_ligue_one",
  78: "soccer_germany_bundesliga",
  135: "soccer_italy_serie_a",
  140: "soccer_spain_la_liga",
  848: "soccer_uefa_europa_conference_league",
};

async function fetchOddsAPIData(sportKey) {
  const bookmakers = ALL_BOOKMAKERS_ODDS.map(b => b.key).join(",");
  // Expanded markets — all available on free tier: h2h, totals, btts, double_chance, draw_no_bet
  // Include alternate_totals so bookmakers with different default lines (2.0, 2.75, 3.0) still match
  const url = `${API_ROOT}/api/odds/${sportKey}?regions=eu,uk&markets=h2h,totals,btts,double_chance,draw_no_bet,alternate_totals&bookmakers=${bookmakers}`;
  console.log("[BetMan Odds API]", url);
  const res = await fetch(url);
  if (!res.ok) throw new Error(`Odds API error: ${res.status}`);
  return res.json();
}

async function footballApi(endpoint, params = {}) {
  // Route through cache server to save API calls
  const queryStr = Object.entries(params).map(([k, v]) => `${k}=${encodeURIComponent(v)}`).join("&");
  const url = `${API_BASE}${endpoint}${queryStr ? "?" + queryStr : ""}`;
  console.log("[BetMan API]", url);
  const res = await fetch(url);
  const data = await res.json();
  console.log("[BetMan API] status:", res.status, "results:", data.results, "cache:", res.headers.get("X-Cache"), "errors:", JSON.stringify(data.errors));
  if (data.errors && Object.keys(data.errors).length > 0) {
    const errMsg = JSON.stringify(data.errors);
    console.error("[BetMan API] Error:", errMsg);
    // Throw on rate limit or plan errors so callers can distinguish from "no data"
    if (errMsg.includes("request limit") || errMsg.includes("not have access")) {
      throw new Error(errMsg);
    }
  }
  return data.response || [];
}

function fmtDate(iso) {
  const d = new Date(iso);
  const days = ["Sun","Mon","Tue","Wed","Thu","Fri","Sat"];
  const months = ["Jan","Feb","Mar","Apr","May","Jun","Jul","Aug","Sep","Oct","Nov","Dec"];
  return `${days[d.getDay()]} ${d.getDate()} ${months[d.getMonth()]}`;
}

function fmtTime(iso) {
  return new Date(iso).toLocaleTimeString("en-GB", { hour: "2-digit", minute: "2-digit" });
}

function getUpcomingDates(n = 7) {
  // Look ahead up to 7 days to always show upcoming top matches
  const dates = [];
  for (let i = 0; i < n; i++) {
    const d = new Date();
    d.setDate(d.getDate() + i);
    dates.push(d.toISOString().split("T")[0]);
  }
  return dates;
}

// ─── Fixtures cache (localStorage) — instant render + background refresh ───
// Base fixture list is cached so the Matches tab paints immediately on app
// open (and on every revisit) instead of waiting for a full API round-trip;
// fresh odds/scores are refreshed in the background and re-cached on top.
const MATCHES_CACHE_KEY = "betman_matches_cache_v1";
const MATCHES_CACHE_TTL = 10 * 60 * 1000; // 10 min "fresh" window (still background-refreshes after)
function readMatchesCache() {
  try {
    const raw = localStorage.getItem(MATCHES_CACHE_KEY);
    if (!raw) return null;
    const { ts, data } = JSON.parse(raw);
    if (!Array.isArray(data)) return null;
    return { ts, data, fresh: Date.now() - ts < MATCHES_CACHE_TTL };
  } catch { return null; }
}
function writeMatchesCache(data) {
  try { localStorage.setItem(MATCHES_CACHE_KEY, JSON.stringify({ ts: Date.now(), data })); } catch {}
}

async function fetchLiveMatches() {
  try {
    // Fetch fixtures by date (1 call per day, no league filter = much fewer API calls)
    const dates = getUpcomingDates(14);
    let all = [];
    let anySuccess = false;

    for (const dt of dates) {
      try {
        const fixtures = await footballApi("/fixtures", { date: dt });
        anySuccess = true;
        // Filter to our target leagues
        const filtered = fixtures.filter(f => LEAGUE_IDS.has(f.league.id));
        all.push(...filtered);
        console.log(`[BetMan] ${dt}: ${fixtures.length} total, ${filtered.length} from top leagues`);
      } catch (e) {
        console.error(`[BetMan] Failed for ${dt}:`, e);
      }
    }

    // Always pull the FULL World Cup schedule (league 1) so every group's
    // complete fixture set is available to the stage/group filters — not just
    // the matches that happen to fall inside the 14-day date window above.
    try {
      const wcFixtures = await footballApi("/fixtures", { league: 1, season: 2026 });
      if (wcFixtures.length) {
        anySuccess = true;
        all.push(...wcFixtures);
        console.log(`[BetMan] World Cup full schedule: ${wcFixtures.length} fixtures`);
      }
    } catch (e) {
      console.error("[BetMan] Failed to fetch World Cup schedule:", e);
    }

    // De-duplicate by fixture id (WC matches inside the date window also appear
    // in the full-schedule pull above)
    {
      const seen = new Set();
      all = all.filter(f => {
        if (seen.has(f.fixture.id)) return false;
        seen.add(f.fixture.id);
        return true;
      });
    }

    // If no top-league matches found in the next 7 days, fetch next upcoming matches per league
    if (!all.length && anySuccess) {
      console.warn("[BetMan] No top-league fixtures in date range, fetching next upcoming per league...");
      const topLeagues = [39, 140, 78, 135, 61, 2, 3, 5, 32];
      for (const lid of topLeagues) {
        try {
          const fixtures = await footballApi("/fixtures", { league: lid, next: 3 });
          all.push(...fixtures);
          console.log(`[BetMan] League ${lid}: found ${fixtures.length} upcoming`);
        } catch (e) {
          console.error(`[BetMan] Failed league ${lid}:`, e);
        }
      }
    }

    // If no API call succeeded at all, return null (error state)
    if (!anySuccess) {
      console.error("[BetMan] All API calls failed (rate limit or network error)");
      return null;
    }

    // API worked but no matches found — genuinely no matches
    if (!all.length) {
      console.log("[BetMan] No fixtures found — no matches today");
      return [];
    }

    // sort by date and filter to only upcoming matches (not started)
    all = all.filter(f => {
      const status = f.fixture.status?.short;
      const kickoff = new Date(f.fixture.date);
      return status === "NS" || status === "TBD" || kickoff > new Date();
    });

    // All matches have started or finished — none left today
    if (!all.length) return [];
    all.sort((a, b) => new Date(a.fixture.date) - new Date(b.fixture.date));

    // Keep ALL World Cup fixtures (so every group's full set survives the cap),
    // and cap the other leagues to keep the list manageable.
    {
      const wc = all.filter(f => f.league.id === 1);
      const others = all.filter(f => f.league.id !== 1).slice(0, 60);
      all = [...wc, ...others].sort((a, b) => new Date(a.fixture.date) - new Date(b.fixture.date));
    }

    return all.map(f => ({
      id: f.fixture.id,
      league: f.league.name,
      leagueId: f.league.id,
      round: f.league.round || "",  // e.g. "Group A - 1", "Round of 16", "Quarter-finals"
      fixtureDate: f.fixture.date, // raw ISO for time-based logic (e.g. lineup polling)
      date: fmtDate(f.fixture.date),
      time: fmtTime(f.fixture.date),
      home: f.teams.home.name,
      away: f.teams.away.name,
      homeId: f.teams.home.id,
      awayId: f.teams.away.id,
      homeLogo: f.teams.home.logo,
      awayLogo: f.teams.away.logo,
      status: f.fixture.status?.short || "NS",
      homeGoals: f.goals?.home,
      awayGoals: f.goals?.away,
      homeForm: [], awayForm: [],
      stats: {
        home: { avgGoals: 0, avgConceded: 0, avgCorners: 0, avgBookings: 0, xG: 0, cleanSheets: 0 },
        away: { avgGoals: 0, avgConceded: 0, avgCorners: 0, avgBookings: 0, xG: 0, cleanSheets: 0 },
      },
      injuries: { home: [], away: [] },
      scoringPattern: { home: [0,0,0,0,0,0,0,0,0], away: [0,0,0,0,0,0,0,0,0] },
      odds: { match_winner: { home: {}, draw: {}, away: {} }, over_25: {}, btts: {}, over_15: {} },
      players: [],
      gk: {
        home: { name: "—", saves: 0, savePct: 0, cleanSheets: 0, xSaves: 0 },
        away: { name: "—", saves: 0, savePct: 0, cleanSheets: 0, xSaves: 0 },
      },
      detailsLoaded: false,
    }));
  } catch (err) {
    console.error("Fetch error:", err);
    return null;
  }
}

// Build the standard odds table entirely from The Odds API — the single source of truth for odds.
// 1. Resolve the event id from the cheap, cached sport-level listing (one call covers the whole league).
// 2. Pull event-level odds (all bookmakers + h2h / totals / btts / over 1.5) so every cell is correct.
// The Odds API carries each bookmaker's display title, so the table renders its columns dynamically.
async function loadOddsApiTable(leagueId, home, away) {
  const sportKey = LEAGUE_TO_SPORT_KEY[leagueId];
  if (!sportKey) return null;
  // Strip accents (NFD) so "Curaçao" matches "Curacao", "Türkiye" matches "Turkiye", etc.
  const norm = (s) => (s || "").toLowerCase().normalize("NFD").replace(/[\u0300-\u036f]/g, "").replace(/[^a-z0-9]/g, "");
  const wantH = norm(home), wantA = norm(away);
  try {
    // 1. Resolve the Odds API event id from the sport-level listing.
    const listRes = await fetch(`${API_ROOT}/api/odds/${sportKey}`);
    if (!listRes.ok) return null;
    const fixtures = await listRes.json();
    if (!Array.isArray(fixtures)) return null;
    const ev = fixtures.find(f =>
      (norm(f.home_team) === wantH && norm(f.away_team) === wantA) ||
      (norm(f.home_team) === wantA && norm(f.away_team) === wantH)
    );
    if (!ev?.id) return null;

    // 2. Event-level odds (all bookmakers, multiple markets).
    const detRes = await fetch(`${API_ROOT}/api/odds-deep/${sportKey}/${ev.id}?regions=eu,uk,us&markets=h2h,totals,btts,alternate_totals,double_chance`);
    let fx = ev;
    if (detRes.ok) {
      const det = await detRes.json();
      if (det?.bookmakers?.length) fx = det;
    }

    const out = { match_winner: { home: {}, draw: {}, away: {} }, over_25: {}, under_25: {}, over_15: {}, btts: {}, btts_no: {}, double_chance: { home_draw: {}, away_draw: {}, home_away: {} }, _titles: {} };
    const homeIsRealHome = norm(fx.home_team) === wantH;
    for (const bk of (fx.bookmakers || [])) {
      const key = bk.key;
      if (!key) continue;
      out._titles[key] = bk.title || key;
      for (const market of (bk.markets || [])) {
        const outcomes = market.outcomes || [];
        if (market.key === "h2h") {
          for (const o of outcomes) {
            const v = parseFloat(o.price);
            if (!isFinite(v)) continue;
            if (/draw/i.test(o.name)) out.match_winner.draw[key] = v;
            else if (norm(o.name) === norm(fx.home_team)) (homeIsRealHome ? out.match_winner.home : out.match_winner.away)[key] = v;
            else if (norm(o.name) === norm(fx.away_team)) (homeIsRealHome ? out.match_winner.away : out.match_winner.home)[key] = v;
          }
        } else if (market.key === "totals" || market.key === "alternate_totals") {
          for (const o of outcomes) {
            const v = parseFloat(o.price);
            const pt = Number(o.point);
            if (!isFinite(v)) continue;
            if (pt === 2.5) {
              if (/over/i.test(o.name)) out.over_25[key] = v;
              else if (/under/i.test(o.name)) out.under_25[key] = v;
            } else if (pt === 1.5 && /over/i.test(o.name)) {
              out.over_15[key] = v;
            }
          }
        } else if (market.key === "btts") {
          for (const o of outcomes) {
            const v = parseFloat(o.price);
            if (!isFinite(v)) continue;
            if (/yes/i.test(o.name)) out.btts[key] = v;
            else if (/no/i.test(o.name)) out.btts_no[key] = v;
          }
        } else if (market.key === "double_chance") {
          // Outcomes are named "{TeamA} or Draw", "{TeamB} or Draw", "{TeamA} or {TeamB}".
          for (const o of outcomes) {
            const v = parseFloat(o.price);
            if (!isFinite(v)) continue;
            const nm = o.name || "";
            if (/draw/i.test(nm)) {
              const team = nm.replace(/\s*or\s*draw/i, "").trim();
              const isRealHomeTeam = norm(team) === norm(fx.home_team);
              const slot = (homeIsRealHome === isRealHomeTeam) ? "home_draw" : "away_draw";
              out.double_chance[slot][key] = v;
            } else {
              out.double_chance.home_away[key] = v;
            }
          }
        }
      }
    }
    return out;
  } catch (err) {
    console.error("[OddsApi table error]", err);
    return null;
  }
}

// Polymarket: load WC outright AND group-winner probabilities for both teams.
// Group winner data (e.g. "Mexico 54% to win Group A") is MUCH more useful per-match
// signal than the WC outright winner (which is heavily skewed toward favorites).
// Returns { home: {outright: {...}, groupWin: {...}}, away: {...} }
async function loadPolymarketForMatch(home, away) {
  try {
    const r = await fetch(`${API_ROOT}/api/polymarket/soccer`);
    if (!r.ok) return null;
    const markets = await r.json();
    if (!Array.isArray(markets) || !markets.length) return null;

    // Strip non-ASCII + diacritics so "Côte d'Ivoire" matches "Cote dIvoire", "México" matches "Mexico", etc.
    const norm = (s) => (s || "").toLowerCase().normalize("NFD").replace(/[\u0300-\u036f]/g, "").replace(/[^a-z0-9 ]/g, " ").replace(/\s+/g, " ").trim();
    const teamMatches = (countryFromQ, teamName) => {
      const a = norm(countryFromQ), b = norm(teamName);
      if (!a || !b) return false;
      if (a === b) return true;
      if (a.includes(b) || b.includes(a)) return true;
      return false;
    };

    const findOutright = (name) => markets.find(m => {
      const q = (m.question || "").toLowerCase();
      if (!q.includes("win the 2026 fifa world cup")) return false;
      if (!q.startsWith("will ")) return false;
      const country = q.replace("will ", "").split(" win ")[0].trim();
      return teamMatches(country, name);
    });

    const findGroupWin = (name) => markets.find(m => {
      if (m.marketType !== "group_winner") return false;
      const q = (m.question || "").toLowerCase();
      if (!/win group [a-l]/i.test(q)) return false;
      if (!q.startsWith("will ")) return false;
      const country = q.replace("will ", "").split(" win group")[0].trim();
      // Skip "another team" placeholder market
      if (country.includes("another team")) return false;
      return teamMatches(country, name);
    });

    const extractOutright = (m) => m ? {
      country: (m.question || "").replace(/^Will\s+/i, "").split(" win ")[0].trim(),
      question: m.question,
      slug: m.slug,
      probYes: m.outcomePrices?.[0] || 0,
      probNo: m.outcomePrices?.[1] || 0,
      volume: m.volume,
      liquidity: m.liquidity,
    } : null;

    const extractGroup = (m) => m ? {
      country: (m.question || "").replace(/^Will\s+/i, "").split(" win group")[0].trim(),
      group: m.group || (m.question || "").match(/Group ([A-L])/i)?.[1] || "?",
      question: m.question,
      slug: m.slug,
      probYes: m.outcomePrices?.[0] || 0,
      probNo: m.outcomePrices?.[1] || 0,
      volume: m.volume,
      liquidity: m.liquidity,
    } : null;

    const buildSide = (name) => {
      const outright = extractOutright(findOutright(name));
      const groupWin = extractGroup(findGroupWin(name));
      if (!outright && !groupWin) return null;
      // Backward-compat fields (probYes/probNo/country/question/slug/volume/liquidity)
      // point at the BEST available signal: groupWin if present, else outright.
      const primary = groupWin || outright;
      return { ...primary, outright, groupWin };
    };

    const homeSide = buildSide(home);
    const awaySide = buildSide(away);
    if (!homeSide && !awaySide) return null;
    return { home: homeSide, away: awaySide };
  } catch (err) {
    console.error("[Polymarket fetch error]", err);
    return null;
  }
}

// Merge bookmaker-keyed odds objects (source) into target (preserves target where keys overlap).
// Compute median of odds object across bookmakers — more robust than picking first
function medianOdds(oddsObj) {
  const vals = Object.values(oddsObj).filter(v => isFinite(v));
  if (!vals.length) return null;
  vals.sort((a, b) => a - b);
  const mid = Math.floor(vals.length / 2);
  return vals.length % 2 ? vals[mid] : (vals[mid - 1] + vals[mid]) / 2;
}

async function loadTeamForm(teamId) {
  try {
    console.log(`[BetMan] Fetching form for team ${teamId}`);
    const data = await footballApi("/fixtures", { team: teamId, last: 5 });
    console.log(`[BetMan] Form results for ${teamId}:`, data.length, "matches");
    if (!data.length) return [];
    return data.map(f => {
      const isHome = f.teams.home.id === teamId;
      const winner = isHome ? f.teams.home.winner : f.teams.away.winner;
      const result = winner === true ? "W" : winner === false ? "L" : "D";
      return {
        result,
        fixtureId: f.fixture?.id,
        homeTeam: f.teams.home.name,
        awayTeam: f.teams.away.name,
        homeGoals: f.goals?.home ?? "?",
        awayGoals: f.goals?.away ?? "?",
        wasHome: isHome,
        opponent: isHome ? f.teams.away.name : f.teams.home.name,
        opponentLogo: isHome ? f.teams.away.logo : f.teams.home.logo,
        date: f.fixture?.date ? new Date(f.fixture.date).toLocaleDateString("en-GB", { day: "numeric", month: "short" }) : "",
        league: f.league?.name || "",
      };
    });
  } catch (err) {
    console.error(`[BetMan] Form fetch failed for ${teamId}:`, err);
    return [];
  }
}

// Accent-insensitive name key (events drop diacritics, squad lists keep them).
function normName(s) {
  return String(s || "").normalize("NFD").replace(/[\u0300-\u036f]/g, "").toLowerCase().trim();
}

// Aggregate which players scored (and how many times) for a team across its last-5 fixtures.
// Only players in the team's CURRENT squad are kept (a past scorer no longer called up is excluded).
async function loadTeamScorers(teamId, form) {
  const fixtureIds = (form || []).map(f => f.fixtureId).filter(Boolean);
  if (!fixtureIds.length) return [];
  try {
    const [squad, ...results] = await Promise.all([
      footballApi("/players/squads", { team: teamId }).catch(() => []),
      ...fixtureIds.map(id => footballApi("/fixtures/events", { fixture: id }).catch(() => [])),
    ]);
    const squadSet = new Set((squad?.[0]?.players || []).map(p => normName(p.name)));
    const tally = new Map(); // name -> { name, goals }
    for (const events of results) {
      for (const ev of (events || [])) {
        if (ev.type !== "Goal") continue;
        if (ev.team?.id !== teamId) continue;
        if (ev.detail === "Missed Penalty" || ev.detail === "Own Goal") continue;
        const name = ev.player?.name;
        if (!name) continue;
        // Keep only current-squad players (skip filter entirely if squad data is unavailable).
        if (squadSet.size && !squadSet.has(normName(name))) continue;
        const cur = tally.get(name) || { name, goals: 0 };
        cur.goals += 1;
        tally.set(name, cur);
      }
    }
    return Array.from(tally.values()).sort((a, b) => b.goals - a.goals);
  } catch (err) {
    console.error(`[BetMan] Scorers fetch failed for ${teamId}:`, err);
    return [];
  }
}

async function loadInjuries(fixtureId, homeTeam, awayTeam, homeId, awayId) {
  try {
    const data = await footballApi("/injuries", { fixture: fixtureId });
    const home = [], away = [];
    const seen = new Set(); // dedupe by team+player id
    for (const inj of data) {
      const pid = inj.player?.id;
      const tid = inj.team?.id;
      const key = `${tid}-${pid}`;
      if (pid && seen.has(key)) continue;
      if (pid) seen.add(key);
      const name = `${inj.player?.name || "Unknown"} (${inj.player?.reason || "injury"})`;
      if (tid === homeId) home.push(name);
      else if (tid === awayId) away.push(name);
    }
    return { home: home.length ? home : ["No injuries reported"], away: away.length ? away : ["No injuries reported"] };
  } catch { return { home: ["Data unavailable"], away: ["Data unavailable"] }; }
}

async function loadMatchDetails(match) {
  const [oddsTable, polymarket, homeForm, awayForm, injuries, predictions, lineups] = await Promise.all([
    loadOddsApiTable(match.leagueId, match.home, match.away),
    loadPolymarketForMatch(match.home, match.away),
    loadTeamForm(match.homeId),
    loadTeamForm(match.awayId),
    loadInjuries(match.id, match.home, match.away, match.homeId, match.awayId),
    loadPredictions(match.id),
    loadLineups(match.id),
  ]);
  // Odds come exclusively from The Odds API (single source of truth). Polymarket is kept separately
  // because it shows WC outright/group-winner probabilities, rendered as its own section below.

  // Build fallback form from predictions if API form calls failed
  let finalHomeForm = homeForm.length ? homeForm : [];
  let finalAwayForm = awayForm.length ? awayForm : [];

  if (!finalHomeForm.length && predictions?.teams?.home?.form) {
    console.log("[BetMan] Using predictions form fallback for home");
    finalHomeForm = predictions.teams.home.form.slice(-5).split("").map(c => ({
      result: c, opponent: "—", opponentLogo: null, homeGoals: "—", awayGoals: "—", wasHome: true, date: "", league: "",
    }));
  }
  if (!finalAwayForm.length && predictions?.teams?.away?.form) {
    console.log("[BetMan] Using predictions form fallback for away");
    finalAwayForm = predictions.teams.away.form.slice(-5).split("").map(c => ({
      result: c, opponent: "—", opponentLogo: null, homeGoals: "—", awayGoals: "—", wasHome: false, date: "", league: "",
    }));
  }

  // For pre-tournament / national-team matches the /predictions endpoint returns 0W-0D-0L because
  // there's no league data. Fall back to last-10 internationals + H2H + player form.
  const playedHome = predictions?.teams?.home?.played || 0;
  const playedAway = predictions?.teams?.away?.played || 0;
  const needsIntlFallback = playedHome === 0 && playedAway === 0;

  let intl = null;
  let scorerOdds = null;
  // Correct-score prices for the Predicted Score pick — fetched for EVERY match (the event
  // odds are served from a 15-min server cache, so this shares the fetch with scorer odds).
  const scoreOddsPromise = loadScoreOdds(LEAGUE_TO_SPORT_KEY[match.leagueId], match.home, match.away);
  if (needsIntlFallback) {
    console.log("[BetMan] Pre-tournament match detected — fetching international fixtures + H2H + player form");
    const [homeIntl, awayIntl, h2h, homePlayers, awayPlayers, scorers] = await Promise.all([
      loadRecentInternationals(match.homeId),
      loadRecentInternationals(match.awayId),
      loadH2H(match.homeId, match.awayId),
      loadTopPlayers(match.homeId),
      loadTopPlayers(match.awayId),
      loadScorerOdds(LEAGUE_TO_SPORT_KEY[match.leagueId], match.home, match.away),
    ]);
    intl = {
      home: { fixtures: homeIntl, players: homePlayers },
      away: { fixtures: awayIntl, players: awayPlayers },
      h2h,
    };
    scorerOdds = scorers;
  }
  const scoreOdds = await scoreOddsPromise;

  return {
    ...match,
    odds: oddsTable || match.odds,
    polymarket,
    homeForm: finalHomeForm,
    awayForm: finalAwayForm,
    injuries,
    predictions,
    lineups,
    intl,
    scorerOdds,
    scoreOdds,
    detailsLoaded: true,
  };
}

// Last 10 official fixtures for a team (regardless of league).
// For national teams this picks up Nations League, qualifiers, friendlies.
async function loadRecentInternationals(teamId) {
  try {
    const data = await footballApi("/fixtures", { team: teamId, last: 10 });
    return data.map(f => ({
      date: (f.fixture?.date || "").slice(0, 10),
      league: f.league?.name || "",
      homeId: f.teams?.home?.id, homeName: f.teams?.home?.name,
      awayId: f.teams?.away?.id, awayName: f.teams?.away?.name,
      homeGoals: f.goals?.home ?? null, awayGoals: f.goals?.away ?? null,
      isHome: f.teams?.home?.id === teamId,
    }));
  } catch { return []; }
}

async function loadH2H(homeId, awayId) {
  try {
    const data = await footballApi("/fixtures/headtohead", { h2h: `${homeId}-${awayId}`, last: 10 });
    return data.map(f => ({
      date: (f.fixture?.date || "").slice(0, 10),
      league: f.league?.name || "",
      homeName: f.teams?.home?.name, awayName: f.teams?.away?.name,
      homeGoals: f.goals?.home ?? null, awayGoals: f.goals?.away ?? null,
    }));
  } catch { return []; }
}

// Top 8 attackers/midfielders with their 2025/26 club season stats.
async function loadTopPlayers(teamId) {
  try {
    const squad = await footballApi("/players/squads", { team: teamId });
    if (!squad?.length || !squad[0]?.players?.length) return [];
    const attackers = squad[0].players
      .filter(p => /Attacker|Midfielder|Forward/i.test(p.position || ""))
      .slice(0, 8);
    const formData = await Promise.all(
      attackers.map(p => footballApi("/players", { id: p.id, season: 2025 }).catch(() => []))
    );
    const out = [];
    formData.forEach((stats, i) => {
      if (!stats?.length || !stats[0]?.player) return;
      const r = stats[0];
      let totalG = 0, totalA = 0, totalApps = 0, mainClub = "";
      (r.statistics || []).forEach(s => {
        const apps = s.games?.appearences ?? 0;
        if (apps > 0) {
          totalG += s.goals?.total ?? 0;
          totalA += s.goals?.assists ?? 0;
          totalApps += apps;
          if (!mainClub && s.team?.name) mainClub = s.team.name;
        }
      });
      if (totalApps > 0) {
        out.push({
          name: r.player.name,
          pos: attackers[i].position,
          club: mainClub,
          goals: totalG,
          assists: totalA,
          apps: totalApps,
          gPerGame: totalApps > 0 ? +(totalG / totalApps).toFixed(2) : 0,
        });
      }
    });
    // Sort by total contributions (G+A)
    return out.sort((a, b) => (b.goals + b.assists) - (a.goals + a.assists)).slice(0, 5);
  } catch { return []; }
}

async function loadPredictions(fixtureId) {
  try {
    const data = await footballApi("/predictions", { fixture: fixtureId });
    if (!data.length) return null;
    const p = data[0];
    return {
      winner: p.predictions?.winner?.name || null,
      winOrDraw: p.predictions?.win_or_draw || false,
      advice: p.predictions?.advice || null,
      goalHome: p.predictions?.goals?.home || "-",
      goalAway: p.predictions?.goals?.away || "-",
      percent: {
        home: p.predictions?.percent?.home || "0%",
        draw: p.predictions?.percent?.draw || "0%",
        away: p.predictions?.percent?.away || "0%",
      },
      comparison: p.comparison || {},
      teams: {
        home: {
          form: p.teams?.home?.league?.form || "",
          goals_for_avg: p.teams?.home?.league?.goals?.for?.average?.total || 0,
          goals_against_avg: p.teams?.home?.league?.goals?.against?.average?.total || 0,
          clean_sheet: p.teams?.home?.league?.clean_sheet?.total || 0,
          played: p.teams?.home?.league?.fixtures?.played?.total || 0,
          wins: p.teams?.home?.league?.fixtures?.wins?.total || 0,
          draws: p.teams?.home?.league?.fixtures?.draws?.total || 0,
          losses: p.teams?.home?.league?.fixtures?.loses?.total || 0,
        },
        away: {
          form: p.teams?.away?.league?.form || "",
          goals_for_avg: p.teams?.away?.league?.goals?.for?.average?.total || 0,
          goals_against_avg: p.teams?.away?.league?.goals?.against?.average?.total || 0,
          clean_sheet: p.teams?.away?.league?.clean_sheet?.total || 0,
          played: p.teams?.away?.league?.fixtures?.played?.total || 0,
          wins: p.teams?.away?.league?.fixtures?.wins?.total || 0,
          draws: p.teams?.away?.league?.fixtures?.draws?.total || 0,
          losses: p.teams?.away?.league?.fixtures?.loses?.total || 0,
        },
      },
    };
  } catch (err) {
    console.error("Predictions error:", err);
    return null;
  }
}

async function loadLineups(fixtureId) {
  try {
    const data = await footballApi("/fixtures/lineups", { fixture: fixtureId });
    if (!data.length) return null;
    return data.map(team => ({
      teamId: team.team?.id,
      teamName: team.team?.name,
      teamLogo: team.team?.logo,
      formation: team.formation,
      coach: team.coach?.name || "TBA",
      startXI: (team.startXI || []).map(p => ({
        name: p.player?.name,
        number: p.player?.number,
        pos: p.player?.pos,
      })),
      subs: (team.substitutes || []).map(p => ({
        name: p.player?.name,
        number: p.player?.number,
        pos: p.player?.pos,
      })),
    }));
  } catch (err) {
    console.error("Lineups error:", err);
    return null;
  }
}

// Accent-insensitive name helpers (shared by injury matching + scorer-odds lookup).
const normTxt = (s) => (s || "").toLowerCase().normalize("NFD").replace(/[\u0300-\u036f]/g, "").replace(/[^a-z ]/g, " ").replace(/\s+/g, " ").trim();
const surnameOf = (s) => { const p = normTxt(s).split(" ").filter(Boolean); return p.length ? p[p.length - 1] : ""; };

// Best-effort fetch of live anytime-goalscorer odds for this match, keyed by player surname.
// Prefers Bet365 (BetMan's primary book, widest market coverage), falls back to the best EU price.
// Returns null on any failure so the Picks tab still renders without odds.
async function loadScorerOdds(sportKey, home, away) {
  if (!sportKey) return null;
  try {
    const headers = { "Content-Type": "application/json" };
    if (supabase) {
      const { data: { session } } = await supabase.auth.getSession();
      if (session?.access_token) headers.Authorization = `Bearer ${session.access_token}`;
    }
    const res = await fetch(apiUrl("/api/match-odds-search"), {
      method: "POST", headers,
      body: JSON.stringify({ sportKey, home, away, market: "player_goal_scorer_anytime" }),
    });
    if (!res.ok) return null;
    const data = await res.json();
    if (!data.found || !data.rows?.length) return null;
    const map = {};
    for (const row of data.rows) {
      const name = row.description || row.name;
      const sn = surnameOf(name);
      if (!sn) continue;
      let bet365 = null;
      for (const [k, v] of Object.entries(row.prices || {})) {
        if (/bet365/i.test(k) && Number(v) > 0) { bet365 = Number(v); break; }
      }
      map[sn] = bet365 ? { odd: bet365, bk: "Bet365" } : (row.bestPrice ? { odd: row.bestPrice, bk: "Best" } : null);
    }
    return map;
  } catch { return null; }
}

// Correct-score prices for the Predicted Score pick. Bookmakers name outcomes "2-1" or "2:1"
// (home goals first); normalize both to "h-a" keys. Prefers bet365 like every other pick.
async function loadScoreOdds(sportKey, home, away) {
  if (!sportKey) return null;
  try {
    const headers = { "Content-Type": "application/json" };
    if (supabase) {
      const { data: { session } } = await supabase.auth.getSession();
      if (session?.access_token) headers.Authorization = `Bearer ${session.access_token}`;
    }
    const res = await fetch(apiUrl("/api/match-odds-search"), {
      method: "POST", headers,
      body: JSON.stringify({ sportKey, home, away, market: "correct_score" }),
    });
    if (!res.ok) return null;
    const data = await res.json();
    if (!data.found || !data.rows?.length) return null;
    const map = {};
    for (const row of data.rows) {
      const m = String(row.name || row.label || "").match(/(\d+)\s*[-:]\s*(\d+)/);
      if (!m) continue;
      const key = `${m[1]}-${m[2]}`;
      let bet365 = null;
      for (const [k, v] of Object.entries(row.prices || {})) {
        if (/bet365/i.test(k) && Number(v) > 0) { bet365 = Number(v); break; }
      }
      const entry = bet365 ? { odd: bet365, bk: "Bet365" } : (row.bestPrice ? { odd: row.bestPrice, bk: "Best" } : null);
      // Same score can arrive as separate rows ("2-1" and "2:1") from different books —
      // keep the bet365-priced one when available.
      if (entry && (!map[key] || (map[key].bk !== "Bet365" && entry.bk === "Bet365"))) map[key] = entry;
    }
    return map;
  } catch { return null; }
}

function buildPicks(match) {
  const pred = match.predictions;
  const odds = match.odds || {};
  const mw = odds.match_winner || {};
  if (!pred) return null;

  const picks = [];

  // Prefer Bet365's price (our primary book / widest market coverage); fall back to the best available.
  const best = (obj) => {
    const names = { bet365: "Bet365", unibet: "Unibet", betfair: "Betfair", william_hill: "W.Hill", williamhill: "W.Hill", paddypower: "Paddy P", pinnacle: "Pinnacle" };
    for (const [k, v] of Object.entries(obj || {})) {
      if (/bet365/i.test(k) && v > 0) return { odds: v, bk: "Bet365" };
    }
    let bk = "", val = 0;
    for (const [k, v] of Object.entries(obj || {})) {
      if (v > val) { val = v; bk = k; }
    }
    return { odds: val, bk: names[bk] || bk };
  };

  // ──────────────────────────────────────────────────────────────
  // SUBSTITUTE STATS FROM INTERNATIONAL DATA WHEN PRE-TOURNAMENT
  // The API-Football /predictions endpoint returns 0W-0D-0L for national-team / WC pre-tournament
  // fixtures because there are no league stats. If match.intl was loaded (see loadMatchDetails),
  // recompute the team-stats inputs from RECENT_INTERNATIONALS + H2H + player form.
  // ──────────────────────────────────────────────────────────────
  let th = pred.teams.home, ta = pred.teams.away;
  const intl = match.intl;
  const useIntl = intl && (th.played === 0 || th.played === undefined);
  if (useIntl) {
    const aggregate = (teamId, fixtures, teamName) => {
      let played = 0, wins = 0, draws = 0, losses = 0, goalsFor = 0, goalsAgainst = 0, cleanSheets = 0;
      const formChars = [];
      fixtures.forEach(f => {
        if (f.homeGoals == null || f.awayGoals == null) return;
        played++;
        const isHomeFixture = f.homeId === teamId;
        const own = isHomeFixture ? f.homeGoals : f.awayGoals;
        const opp = isHomeFixture ? f.awayGoals : f.homeGoals;
        goalsFor += own;
        goalsAgainst += opp;
        if (opp === 0) cleanSheets++;
        if (own > opp) { wins++; formChars.push("W"); }
        else if (own === opp) { draws++; formChars.push("D"); }
        else { losses++; formChars.push("L"); }
      });
      return {
        form: formChars.join(""),
        goals_for_avg: played > 0 ? +(goalsFor / played).toFixed(2) : 0,
        goals_against_avg: played > 0 ? +(goalsAgainst / played).toFixed(2) : 0,
        clean_sheet: cleanSheets,
        played, wins, draws, losses,
      };
    };
    th = aggregate(match.homeId, intl.home.fixtures, match.home);
    ta = aggregate(match.awayId, intl.away.fixtures, match.away);
  }

  let hp = parseInt(pred.percent.home) || 0;
  let dp = parseInt(pred.percent.draw) || 0;
  let ap = parseInt(pred.percent.away) || 0;
  const homeGF = parseFloat(th.goals_for_avg) || 0;
  const awayGF = parseFloat(ta.goals_for_avg) || 0;
  const homeGA = parseFloat(th.goals_against_avg) || 0;
  const awayGA = parseFloat(ta.goals_against_avg) || 0;
  const totalAvg = homeGF + awayGF;

  // When using international data (pre-tournament / national-team match) the win probabilities
  // can't lean on league stats. Instead of using ONLY this cycle's W/D/L rates — which on a tiny,
  // friendly-heavy sample push almost every match toward an even, draw-modal split — we blend
  // THREE signals so a genuinely stronger nation separates from a weaker one:
  //   (1) recent international form (last ~10 results across all competitions),
  //   (2) squad club quality — how the country's top attackers actually performed at their clubs
  //       last season (goals+assists per appearance), a strong proxy for raw talent, and
  //   (3) head-to-head history between these two nations.
  // The draw term also shrinks as the strength gap widens (mismatches draw far less often).
  if (useIntl) {
    const homeWinRate = th.played > 0 ? th.wins / th.played : 0.33;
    const awayWinRate = ta.played > 0 ? ta.wins / ta.played : 0.33;
    const homeDrawRate = th.played > 0 ? th.draws / th.played : 0.33;
    const awayDrawRate = ta.played > 0 ? ta.draws / ta.played : 0.33;

    // (2) Squad strength — mean goals+assists per game of the top attackers' 2025/26 CLUB season.
    // Typical range ~0.15 (weak) to ~0.8 (elite attacking talent). 0 when no player data loaded.
    const squadStrength = (players) => {
      const ps = (players || []).filter(p => p && p.apps > 0).slice(0, 5);
      if (!ps.length) return 0;
      return ps.reduce((s, p) => s + (p.goals + p.assists) / Math.max(1, p.apps), 0) / ps.length;
    };
    const homeSquad = squadStrength(intl?.home?.players);
    const awaySquad = squadStrength(intl?.away?.players);

    // (3) Head-to-head — historical win share between the two nations (0.5 = even, draws excluded).
    let h2hHomeShare = 0.5;
    if (intl?.h2h?.length) {
      let hw = 0, aw = 0;
      intl.h2h.forEach(f => {
        if (f.homeGoals == null || f.awayGoals == null) return;
        const homeIsHome = f.homeName === match.home;
        const g = homeIsHome ? f.homeGoals : f.awayGoals;
        const o = homeIsHome ? f.awayGoals : f.homeGoals;
        if (g > o) hw++; else if (g < o) aw++;
      });
      if (hw + aw > 0) h2hHomeShare = hw / (hw + aw);
    }

    // Per-side strength indices = recent form + squad club quality + H2H. No artificial home boost:
    // most World Cup 2026 fixtures are at neutral venues, so the nominal "home" side gets no edge.
    const homeStrength = Math.max(0.05, homeWinRate + homeSquad * 0.6 + (h2hHomeShare - 0.5) * 0.4);
    const awayStrength = Math.max(0.05, awayWinRate + awaySquad * 0.6 + (0.5 - h2hHomeShare) * 0.4);
    // Split the win mass by RELATIVE strength first, then allocate the draw SEPARATELY from how
    // close the sides are (base draw rate, shrinking as the matchup gets more lopsided). This keeps
    // two strong attacking teams from being wrongly treated as low-draw, lets a clear favourite pull
    // away, and still leaves a genuine draw on the board for evenly-matched nations.
    const homeShare = homeStrength / (homeStrength + awayStrength);  // 0..1 share of the non-draw mass
    const lopsided = Math.abs(homeShare - 0.5) * 2;                  // 0 (even) .. 1 (total mismatch)
    const baseDraw = (homeDrawRate + awayDrawRate) / 2;
    const drawShare = Math.max(0.10, Math.min(0.40, baseDraw * (1 - 0.7 * lopsided)));
    hp = Math.round((1 - drawShare) * homeShare * 100);
    ap = Math.round((1 - drawShare) * (1 - homeShare) * 100);
    dp = 100 - hp - ap;
  }

  // Recent form summary
  const homeFormStr = useIntl
    ? th.form?.slice(0, 5) || ""
    : (match.homeForm?.length ? match.homeForm.map(f => typeof f === "string" ? f : f.result).join("") : th.form?.slice(-5) || "");
  const awayFormStr = useIntl
    ? ta.form?.slice(0, 5) || ""
    : (match.awayForm?.length ? match.awayForm.map(f => typeof f === "string" ? f : f.result).join("") : ta.form?.slice(-5) || "");
  const homeWins = homeFormStr.split("").filter(c => c === "W").length;
  const awayWins = awayFormStr.split("").filter(c => c === "W").length;

  // ─── Polymarket per-match signal ───
  // Group-winner % (per-group probability of each side topping their group) is way more
  // informative than WC outright. Fall back to outright if group-winner data is missing.
  const pm = match.polymarket;
  const pmH = pm?.home;
  const pmA = pm?.away;
  const pmHomeP = (pmH?.groupWin?.probYes != null ? pmH.groupWin.probYes : (pmH?.outright?.probYes || 0));
  const pmAwayP = (pmA?.groupWin?.probYes != null ? pmA.groupWin.probYes : (pmA?.outright?.probYes || 0));
  const pmKind = (pmH?.groupWin && pmA?.groupWin) ? "groupWin"
               : (pmH?.outright && pmA?.outright) ? "outright" : null;
  const pmTotal = pmHomeP + pmAwayP;
  // Group-winner markets die once the groups are decided: both sides near 0% (eliminated) OR
  // either side ≥99% (settled winner) is a resolved market, not a prediction — "France 99.9% to
  // top Group D" after the groups ended says nothing about a knockout matchup, and 0.1% vs 0.1%
  // is NOT a "50/50 lean". Treat a dead group market as no signal at all.
  const pmDead = pmKind === "groupWin" &&
    ((pmHomeP < 0.05 && pmAwayP < 0.05) || pmHomeP >= 0.99 || pmAwayP >= 0.99);
  const pmHomeRatio = !pmDead && pmTotal > 0 ? pmHomeP / pmTotal : null;  // null = no signal
  const pmSentence = pmHomeRatio != null && pmKind ? (() => {
    const hPct = (pmHomeP * 100).toFixed(1);
    const aPct = (pmAwayP * 100).toFixed(1);
    const ratioPct = Math.round(pmHomeRatio * 100);
    if (pmKind === "groupWin") {
      const grp = pmH.groupWin.group || pmA.groupWin.group;
      return ` Polymarket's prediction market gives ${match.home} a ${hPct}% chance to top Group ${grp} vs ${match.away} at ${aPct}% — a ${ratioPct}/${100-ratioPct} head-to-head lean toward ${pmHomeRatio > 0.5 ? match.home : match.away}.`;
    }
    return ` Polymarket gives ${match.home} ${hPct}% to win the WC vs ${match.away} ${aPct}%.`;
  })() : "";

  // Risk adjustment: lower when Polymarket consensus agrees with the season-stats pick,
  // higher when they disagree strongly. Skip for draws.
  const polyAdjust = (baseRisk, pickedSide) => {
    if (pmHomeRatio == null) return baseRisk;
    const agrees = pickedSide === "home" ? pmHomeRatio > 0.55
                : pickedSide === "away" ? pmHomeRatio < 0.45
                : null;
    if (agrees === true) return Math.max(1, baseRisk - 1);
    if (agrees === false) return Math.min(10, baseRisk + 1);
    return baseRisk;
  };

  // ─── Reasoning helpers — rich, multi-source explanations ───
  const fmtPlayer = (p) => p && p.name ? `${p.name} (${p.goals}G/${p.assists}A in ${p.apps} club apps for ${p.club})` : "";
  const topPlayersList = (players, n = 2) => (players || []).filter(p => p?.name).slice(0, n).map(fmtPlayer).filter(Boolean);

  const formInsight = (formStr, teamName) => {
    if (!formStr) return "";
    const last = formStr.slice(0, Math.min(5, formStr.length));
    const w = last.split("").filter(c => c === "W").length;
    const d = last.split("").filter(c => c === "D").length;
    const l = last.split("").filter(c => c === "L").length;
    return `${teamName}'s last ${last.length} (${last}) read ${w}W-${d}D-${l}L`;
  };

  // Compute clean sheet % and "defense breached" counts for richer defensive narrative
  const homeCSpct = th.played > 0 ? Math.round((th.clean_sheet / th.played) * 100) : 0;
  const awayCSpct = ta.played > 0 ? Math.round((ta.clean_sheet / ta.played) * 100) : 0;
  const homeBreached = Math.max(0, th.played - th.clean_sheet);
  const awayBreached = Math.max(0, ta.played - ta.clean_sheet);

  // Game importance — heuristic from match date
  const gameImportance = (() => {
    if (!match.date) return null;
    const d = new Date(match.date);
    const wcStart = new Date("2026-06-11");
    const wcEnd = new Date("2026-07-19");
    if (d >= wcStart && d <= wcEnd) {
      const daysIn = (d - wcStart) / 86400000;
      if (daysIn < 3) return "World Cup opening days — first impressions matter, both squads desperate to set the tone";
      if (daysIn < 14) return "World Cup group stage — every result reshapes qualification chances";
      if (daysIn < 21) return "World Cup round of 32/16 — single-elimination knockout, all-or-nothing";
      return "World Cup knockout stage — championship-defining stakes";
    }
    return null;
  })();
  const importanceSentence = gameImportance ? ` Match context: ${gameImportance}.` : "";

  // H2H aggregate (only when international data is loaded)
  const h2hAgg = (() => {
    if (!intl?.h2h?.length) return null;
    let homeW = 0, draws = 0, awayW = 0, goalsTotal = 0;
    intl.h2h.forEach(f => {
      if (f.homeGoals == null || f.awayGoals == null) return;
      const homeIsHome = f.homeName === match.home;
      const h = homeIsHome ? f.homeGoals : f.awayGoals;
      const a = homeIsHome ? f.awayGoals : f.homeGoals;
      goalsTotal += h + a;
      if (h > a) homeW++; else if (h === a) draws++; else awayW++;
    });
    return {
      total: intl.h2h.length, homeW, draws, awayW,
      avgGoals: intl.h2h.length > 0 ? (goalsTotal / intl.h2h.length).toFixed(1) : "0",
      recent: intl.h2h[0],
    };
  })();
  const h2hSentence = h2hAgg ? ` Recent H2H: ${match.home} ${h2hAgg.homeW}W-${h2hAgg.draws}D-${h2hAgg.awayW}L from the last ${h2hAgg.total} meetings (averaging ${h2hAgg.avgGoals} goals/match)${h2hAgg.recent ? `; most recent ${(h2hAgg.recent.date || "").split("-")[0]} ${h2hAgg.recent.homeName} ${h2hAgg.recent.homeGoals}-${h2hAgg.recent.awayGoals} ${h2hAgg.recent.awayName}` : ""}.` : "";

  // Top attacking players from squad/club form
  const homeTop = topPlayersList(intl?.home?.players, 2);
  const awayTop = topPlayersList(intl?.away?.players, 2);
  const homeTopSentence = homeTop.length > 0 ? ` ${match.home}'s attack is led by ${homeTop.join(" and ")}.` : "";
  const awayTopSentence = awayTop.length > 0 ? ` ${match.away} look to ${awayTop.join(" and ")} for goals.` : "";

  // ═══ CONSISTENCY ANCHOR — the predicted scoreline drives every goal-market so picks NEVER contradict ═══
  // Winner direction, Over/Under and BTTS are all derived from one expected scoreline, so you can never
  // see e.g. "Predicted 2-1" next to "BTTS No" or "Under 2.5", or a "Home Win" pick alongside a 1-2 score.
  // Two-way separation between the sides (draw % aside) — decides whether an evenly-modelled
  // fixture (draw is the modal outcome) is still backed as an outright Win for a clear favourite,
  // a Double Chance, or a straight Draw. Keeps the scoreline aligned with the Match Winner pick.
  const favHomeLean = hp >= ap;
  const twoWayLean = Math.abs(hp - ap);
  const winnerSide = hp > ap && hp > dp ? "home"
    : ap > hp ? "away"
    : twoWayLean >= 10 ? (favHomeLean ? "home" : "away") // clear 2-way favourite even if draw is modal → back outright
    : "draw";
  // ─── Poisson goal model ───
  // Each side's expected goals (lambda) = their attack blended with the opponent's concede rate —
  // the same numbers quoted in the reason text, so headline and explanation can never disagree.
  // Over/BTTS probabilities come from the Poisson distribution instead of the old linear
  // "avg × 28" style formulas, which claimed absurd edges (2.8 avg goals is ~57% Over, not 78%).
  const POIS_FACT = [1, 1, 2, 6, 24, 120, 720, 5040];
  const pois = (k, lam) => Math.exp(-lam) * Math.pow(lam, k) / POIS_FACT[k];
  const lamHome = Math.max(0.15, (homeGF + awayGA) / 2);
  const lamAway = Math.max(0.15, (awayGF + homeGA) / 2);
  const lamTotal = lamHome + lamAway;
  const pOver25 = Math.round((1 - (pois(0, lamTotal) + pois(1, lamTotal) + pois(2, lamTotal))) * 100);
  const pBttsYes = Math.round((1 - Math.exp(-lamHome)) * (1 - Math.exp(-lamAway)) * 100);
  // Goal-market directions follow the model's actual probabilities.
  let over25 = pOver25 >= 50;
  let bttsYes = pBttsYes >= 50;
  // Predicted scoreline = the most likely exact score (independent Poissons) CONSISTENT with the
  // Match Winner direction and the goal-market picks, so the card can never contradict itself.
  // Some combinations are impossible (e.g. a Win + Under 2.5 + BTTS Yes): then the weaker
  // goal-market signal is dropped entirely (no pick shown) instead of showing a contradiction.
  let dropOuPick = false, dropBttsPick = false;
  const mostLikelyScore = (constrainOU, constrainBtts) => {
    let sc = null;
    for (let h = 0; h <= 5; h++) for (let a = 0; a <= 5; a++) {
      const okWinner = winnerSide === "home" ? h > a : winnerSide === "away" ? a > h : h === a;
      if (!okWinner) continue;
      if (constrainOU && ((h + a) >= 3) !== over25) continue;
      if (constrainBtts && ((h >= 1 && a >= 1) !== bttsYes)) continue;
      const p = pois(h, lamHome) * pois(a, lamAway);
      if (!sc || p > sc.p) sc = { h, a, p };
    }
    return sc;
  };
  let sc = mostLikelyScore(true, true);
  if (!sc) {
    const ouStrength = Math.abs(pOver25 - 50), bttsStrength = Math.abs(pBttsYes - 50);
    if (bttsStrength <= ouStrength) { dropBttsPick = true; sc = mostLikelyScore(true, false); }
    else { dropOuPick = true; sc = mostLikelyScore(false, true); }
    if (!sc) { dropOuPick = true; dropBttsPick = true; sc = mostLikelyScore(false, false); }
  }
  const predHome = sc.h, predAway = sc.a;
  const polyScoreNote = pmHomeRatio != null && pmHomeRatio > 0.45 && pmHomeRatio < 0.55
    ? ` Polymarket has the matchup near 50/50, supporting a tight scoreline.`
    : pmHomeRatio != null && (pmHomeRatio >= 0.65 || pmHomeRatio <= 0.35)
    ? ` Polymarket's prediction market leans ${Math.round(Math.max(pmHomeRatio, 1 - pmHomeRatio) * 100)}% toward ${pmHomeRatio >= 0.65 ? match.home : match.away} on this matchup.`
    : "";

  // ─── Value + risk: one honest formula for every pick ───
  // Every priced pick states its edge vs the bookmaker-implied probability, and risk is derived
  // from the model probability + that edge — not hand-tuned per pick.
  const impliedFrom = (odd) => odd > 0 ? Math.round(100 / odd) : null;
  const valueSentence = (probNum, odd) => {
    const imp = impliedFrom(odd);
    if (imp == null || probNum == null) return "";
    const edge = probNum - imp;
    const verdict = edge >= 5 ? "genuine value at this price"
      : edge >= -2 ? "fairly priced by the market"
      : "no value at this price, the market rates it higher than our model does";
    return ` Value check: model ${probNum}% vs bookmaker-implied ${imp}% (${edge >= 0 ? "+" : ""}${edge} pts) = ${verdict}.`;
  };
  // Risk = probability of the bet WINNING, nothing else. The bookmaker-implied probability is
  // the best-calibrated estimate when a live price exists (short odds = easy win = low risk),
  // falling back to the model probability. The value edge is deliberately NOT mixed in — it's
  // reported separately in the Value check sentence. A poor price on a near-certainty is still
  // a safe bet (France @1.16 must read LOW risk), and a great price on a coin-flip is still risky.
  const riskFrom = (probNum, odd) => {
    const imp = impliedFrom(odd);
    const pWin = imp != null ? imp : (probNum != null ? probNum : 50);
    return Math.max(1, Math.min(10, Math.round((100 - pWin) / 10)));
  };

  // ─── Pick 1: Match Winner ───
  if (hp > ap && hp > dp) {
    const b = best(mw.home);
    const winRate = th.played > 0 ? Math.round((th.wins / th.played) * 100) : 0;
    const reason = [
      `${match.home} are the data-favored side: ${winRate}% win rate from ${th.played} ${useIntl ? "internationals this cycle" : "fixtures"}, averaging ${homeGF} goals per game while conceding ${homeGA}.`,
      formInsight(homeFormStr, match.home) ? `${formInsight(homeFormStr, match.home)}, while ${formInsight(awayFormStr, match.away)}.` : "",
      homeTopSentence,
      `By comparison, ${match.away}: ${ta.played > 0 ? Math.round(ta.wins/ta.played*100) : 0}% win rate, conceding ${awayGA}/game with their backline breached in ${awayBreached} of ${ta.played} fixtures.`,
      awayTop[0] ? `Best threat: ${awayTop[0]} — limited firepower otherwise.` : "",
      th.clean_sheet > 0 ? `${match.home} kept ${th.clean_sheet} clean sheet${th.clean_sheet === 1 ? "" : "s"} in ${th.played} (${homeCSpct}%), suggesting their defense can shut down a weaker attack.` : "",
      pmSentence.trim(),
      h2hSentence,
      importanceSentence,
      valueSentence(hp, b.odds).trim(),
      pmHomeRatio != null && pmHomeRatio > 0.65 ? `Risk note: heavy favorites can fall to underdogs with nothing to lose — tournament openers historically produce ~20% upsets.` : "",
    ].filter(Boolean).join(" ").trim();
    picks.push({ bet: `${match.home} Win`, odds: b.odds, bk: b.bk, prob: `${hp}%`, reason,
      risk: polyAdjust(riskFrom(hp, b.odds), "home") });
  } else if (ap > hp) {
    const b = best(mw.away);
    const winRate = ta.played > 0 ? Math.round((ta.wins / ta.played) * 100) : 0;
    const reason = [
      `${match.away} are the data-favored side: ${winRate}% win rate from ${ta.played} ${useIntl ? "internationals this cycle" : "fixtures"}, averaging ${awayGF} goals per game while conceding ${awayGA}.`,
      formInsight(awayFormStr, match.away) ? `${formInsight(awayFormStr, match.away)}, while ${formInsight(homeFormStr, match.home)}.` : "",
      awayTopSentence,
      `By comparison, ${match.home}: ${th.played > 0 ? Math.round(th.wins/th.played*100) : 0}% win rate, conceding ${homeGA}/game with their defense breached in ${homeBreached} of ${th.played} fixtures.`,
      homeTop[0] ? `Best threat: ${homeTop[0]} — limited firepower otherwise.` : "",
      ta.clean_sheet > 0 ? `${match.away} kept ${ta.clean_sheet} clean sheet${ta.clean_sheet === 1 ? "" : "s"} in ${ta.played} (${awayCSpct}%), capable of holding off a weaker attack.` : "",
      pmSentence.trim(),
      h2hSentence,
      importanceSentence,
      valueSentence(ap, b.odds).trim(),
      pmHomeRatio != null && pmHomeRatio < 0.35 ? `Risk note: heavy favorites can fall to underdogs with nothing to lose — tournament openers historically produce ~20% upsets.` : "",
    ].filter(Boolean).join(" ").trim();
    picks.push({ bet: `${match.away} Win`, odds: b.odds, bk: b.bk, prob: `${ap}%`, reason,
      risk: polyAdjust(riskFrom(ap, b.odds), "away") });
  } else {
    // The model has the draw as its modal outcome here (or hp==ap). A straight Draw / Double
    // Chance is rarely the best value, and forcing "(team or draw)" onto every evenly-modelled
    // fixture over-recommended it. So: a CLEAR two-way favourite (≥10pt edge) is backed outright
    // to WIN, a moderate edge (≥6pt) gets Double Chance, and only genuinely level sides (<6pt)
    // get a straight Draw.
    const favHome = favHomeLean;
    const lean = twoWayLean;                          // 2-way separation, draw % aside
    const favName = favHome ? match.home : match.away;
    if (lean >= 10) {
      const b = best(favHome ? mw.home : mw.away);
      const favWinRate = favHome ? (th.played > 0 ? Math.round(th.wins / th.played * 100) : 0)
                                 : (ta.played > 0 ? Math.round(ta.wins / ta.played * 100) : 0);
      const reason = [
        `${favName} are the clearer side and worth backing outright: win model ${match.home} ${hp}% / Draw ${dp}% / ${match.away} ${ap}% — a ${lean}-point two-way edge (${favWinRate}% win rate) over ${favHome ? match.away : match.home}.`,
        formInsight(homeFormStr, match.home) && formInsight(awayFormStr, match.away) ? `${formInsight(homeFormStr, match.home)}, ${formInsight(awayFormStr, match.away)}.` : "",
        favHome ? homeTopSentence : awayTopSentence,
        pmSentence.trim(),
        h2hSentence,
        importanceSentence,
      ].filter(Boolean).join(" ").trim();
      picks.push({ bet: `${favName} Win`, odds: b.odds, bk: b.bk,
        prob: `${favHome ? hp : ap}%`, reason: `${reason}${valueSentence(favHome ? hp : ap, b.odds)}`,
        risk: polyAdjust(riskFrom(favHome ? hp : ap, b.odds), favHome ? "home" : "away") });
    } else {
    const dcObj = favHome ? odds.double_chance?.home_draw : odds.double_chance?.away_draw;
    const bDc = best(dcObj);
    const drawSupport = pmHomeRatio != null && pmHomeRatio > 0.45 && pmHomeRatio < 0.55;
    const trulyLevel = lean < 6 || !(bDc.odds > 0); // level sides (or no DC price) → straight draw
    if (trulyLevel) {
      const b = best(mw.draw);
      const reason = [
        `Tightly matched on paper: ${match.home} (${th.wins}W-${th.draws}D-${th.losses}L) vs ${match.away} (${ta.wins}W-${ta.draws}D-${ta.losses}L), with virtually identical scoring outputs (${homeGF} vs ${awayGF} per game) and defensive lines (${homeGA} vs ${awayGA} conceded).`,
        `${match.home} have drawn ${th.draws} of ${th.played} (${th.played > 0 ? Math.round(th.draws/th.played*100) : 0}%) and ${match.away} ${ta.draws} of ${ta.played} (${ta.played > 0 ? Math.round(ta.draws/ta.played*100) : 0}%) — the draw is a recurring outcome for both.`,
        formInsight(homeFormStr, match.home) && formInsight(awayFormStr, match.away) ? `${formInsight(homeFormStr, match.home)}, ${formInsight(awayFormStr, match.away)}.` : "",
        homeTopSentence,
        awayTopSentence,
        drawSupport ? `Polymarket's prediction market also splits the matchup near 50/50, reinforcing the draw value.` : pmSentence.trim(),
        h2hSentence,
        importanceSentence,
      ].filter(Boolean).join(" ").trim();
      picks.push({ bet: "Draw", odds: b.odds, bk: b.bk, prob: `${dp}%`,
        reason: `${reason}${valueSentence(dp, b.odds)}`, risk: riskFrom(dp, b.odds) });
    } else {
      const favWinRate = favHome ? (th.played > 0 ? Math.round(th.wins / th.played * 100) : 0)
                                 : (ta.played > 0 ? Math.round(ta.wins / ta.played * 100) : 0);
      const dcProb = Math.min(90, (favHome ? hp : ap) + dp);
      const reason = [
        `${favName} hold the edge but not by enough to back outright — Double Chance (${favName} or Draw) banks the value while covering the two most likely results.`,
        `Win model: ${match.home} ${hp}% / Draw ${dp}% / ${match.away} ${ap}% — ${favName} lead the head-to-head read (${favWinRate}% win rate), but a one-goal game or stalemate is live.`,
        formInsight(homeFormStr, match.home) && formInsight(awayFormStr, match.away) ? `${formInsight(homeFormStr, match.home)}, ${formInsight(awayFormStr, match.away)}.` : "",
        favHome ? homeTopSentence : awayTopSentence,
        pmSentence.trim(),
        h2hSentence,
        importanceSentence,
      ].filter(Boolean).join(" ").trim();
      picks.push({ bet: `Double Chance: ${favName} or Draw`, odds: bDc.odds, bk: bDc.bk,
        prob: `${dcProb}%`, reason: `${reason}${valueSentence(dcProb, bDc.odds)}`,
        risk: riskFrom(dcProb, bDc.odds) });
    }
    }
  }

  // ─── Pick 2: Over/Under 2.5 Goals (Poisson probability from the expected-goal lambdas) ───
  const b25 = best(odds.over_25);
  let ouPick, ouProbNum;
  if (over25) {
    ouProbNum = pOver25;
    const reason = [
      `The goal model expects ~${lamTotal.toFixed(1)} total goals: ${match.home} ~${lamHome.toFixed(1)} (their ${homeGF}/game attack vs ${match.away} conceding ${awayGA}) and ${match.away} ~${lamAway.toFixed(1)} (${awayGF}/game attack vs ${homeGA} conceded).`,
      `A Poisson model on that expectancy puts Over 2.5 at ${pOver25}%.`,
      homeTopSentence,
      awayTopSentence,
      `Defensively, ${match.home} have kept only ${th.clean_sheet} clean sheet${th.clean_sheet === 1 ? "" : "s"} in ${th.played} (${homeCSpct}%) and ${match.away} ${ta.clean_sheet} in ${ta.played} (${awayCSpct}%) — both backlines are leakable.`,
      h2hAgg && parseFloat(h2hAgg.avgGoals) >= 2.5 ? `Their last ${h2hAgg.total} meetings averaged ${h2hAgg.avgGoals} goals per game — a recurring high-tempo matchup.` : "",
      pmHomeRatio != null && Math.abs(pmHomeRatio - 0.5) > 0.2 ? `Polymarket's lopsided ${Math.round(pmHomeRatio*100)}/${100-Math.round(pmHomeRatio*100)} lean suggests one side will need to chase — opening late-game scoring windows.` : "",
      `Predicted scoreline: ${predHome}-${predAway}.`,
      valueSentence(ouProbNum, b25.odds).trim(),
      importanceSentence,
    ].filter(Boolean).join(" ").trim();
    ouPick = { bet: "Over 2.5 Goals", odds: b25.odds, bk: b25.bk, prob: `${ouProbNum}%`, reason,
      risk: riskFrom(ouProbNum, b25.odds) };
  } else {
    ouProbNum = 100 - pOver25;
    // No live Under price is fetched — estimate it from the Over quote and say so honestly.
    const underOdds = b25.odds > 0 ? parseFloat((1 / (1 - 1 / b25.odds)).toFixed(2)) : 0;
    const reason = [
      `The goal model expects only ~${lamTotal.toFixed(1)} total goals: ${match.home} ~${lamHome.toFixed(1)} (${homeGF}/game attack vs ${match.away} conceding ${awayGA}) and ${match.away} ~${lamAway.toFixed(1)} (${awayGF}/game attack vs ${homeGA} conceded).`,
      `A Poisson model on that expectancy puts Under 2.5 at ${ouProbNum}%.`,
      `Defenses back it up: ${th.clean_sheet + ta.clean_sheet} combined clean sheets across ${th.played + ta.played} fixtures (${match.home} ${homeCSpct}%, ${match.away} ${awayCSpct}%).`,
      homeGF < 1.1 ? `${match.home} struggle to convert (${homeGF}/game).` : "",
      awayGF < 1.1 ? `${match.away} struggle to convert (${awayGF}/game).` : "",
      h2hAgg && parseFloat(h2hAgg.avgGoals) < 2.5 ? `Their last ${h2hAgg.total} meetings averaged just ${h2hAgg.avgGoals} goals per game — a historically tight matchup.` : "",
      pmHomeRatio != null && (pmHomeRatio > 0.65 || pmHomeRatio < 0.35) ? `Polymarket's heavy ${Math.round(Math.max(pmHomeRatio, 1-pmHomeRatio)*100)}% favorite often closes games out rather than chasing more goals.` : "",
      underOdds > 0 ? `Note: the Under price shown is estimated from the live Over quote — check the exact Under 2.5 price at your bookmaker before betting.` : "",
      importanceSentence,
    ].filter(Boolean).join(" ").trim();
    ouPick = { bet: "Under 2.5 Goals", odds: underOdds, bk: underOdds > 0 ? "estimated" : b25.bk, prob: `${ouProbNum}%`, reason,
      risk: riskFrom(ouProbNum, 0) };
  }

  // ─── Pick 3: BTTS (derived from the anchored scoreline so it can't contradict it) ───
  const bBtts = best(odds.btts);
  let bttsPick, bttsProbNum;
  if (bttsYes) {
    const reason = [
      `The goal model gives both teams scoring a ${pBttsYes}% probability (${match.home} expected ~${lamHome.toFixed(1)} goals, ${match.away} ~${lamAway.toFixed(1)}).`,
      `${match.home} average ${homeGF} goals/game and concede ${homeGA}; ${match.away} average ${awayGF} and concede ${awayGA}.`,
      `Clean sheets are rare: ${match.home} just ${th.clean_sheet}/${th.played} (${homeCSpct}%), ${match.away} ${ta.clean_sheet}/${ta.played} (${awayCSpct}%) — both backlines have been breached regularly.`,
      homeTopSentence,
      awayTopSentence,
      h2hAgg && parseFloat(h2hAgg.avgGoals) >= 2 ? `Recent H2H averaged ${h2hAgg.avgGoals} goals per meeting — both sides historically find the net.` : "",
      pmHomeRatio != null && Math.abs(pmHomeRatio - 0.5) > 0.15 ? `Polymarket's lean toward one side often forces the underdog to push forward (opening BTTS chances).` : "",
      valueSentence(pBttsYes, bBtts.odds).trim(),
      importanceSentence,
    ].filter(Boolean).join(" ").trim();
    bttsProbNum = pBttsYes;
    bttsPick = { bet: "Both Teams to Score — Yes", odds: bBtts.odds, bk: bBtts.bk,
      prob: `${bttsProbNum}%`, reason, risk: riskFrom(bttsProbNum, bBtts.odds) };
  } else {
    const reason = [
      `At least one clean sheet is the more likely outcome: the goal model puts "both teams score" at just ${pBttsYes}% (${match.home} expected ~${lamHome.toFixed(1)} goals, ${match.away} ~${lamAway.toFixed(1)}).`,
      `${match.home} kept ${th.clean_sheet} clean sheet${th.clean_sheet === 1 ? "" : "s"} in ${th.played} (${homeCSpct}%), ${match.away} ${ta.clean_sheet} in ${ta.played} (${awayCSpct}%).`,
      homeGF < 1.1 ? `${match.home} struggle to score (${homeGF}/game).` : "",
      awayGF < 1.1 ? `${match.away} struggle to score (${awayGF}/game).` : "",
      h2hAgg && parseFloat(h2hAgg.avgGoals) < 2.5 ? `Recent H2H averaged ${h2hAgg.avgGoals} goals/match — historically a low-scoring fixture.` : "",
      pmHomeRatio != null && (pmHomeRatio > 0.7 || pmHomeRatio < 0.3) ? `Polymarket's extreme lean (${Math.round(Math.max(pmHomeRatio, 1-pmHomeRatio)*100)}%) often produces blowouts where the underdog rarely scores.` : "",
      importanceSentence,
    ].filter(Boolean).join(" ").trim();
    bttsProbNum = 100 - pBttsYes;
    // Only a live BTTS-Yes price is fetched — estimate the No price from it and say so.
    const bttsNoOdds = bBtts.odds > 0 ? parseFloat((1 / (1 - 1 / bBtts.odds)).toFixed(2)) : 0;
    bttsPick = { bet: "Both Teams to Score — No", odds: bttsNoOdds, bk: bttsNoOdds > 0 ? "estimated" : bBtts.bk,
      prob: `${bttsProbNum}%`,
      reason: `${reason}${bttsNoOdds > 0 ? ` Note: the No price shown is estimated from the live Yes quote — check the exact BTTS-No price at your bookmaker before betting.` : ""}`,
      risk: riskFrom(bttsProbNum, 0) };
  }

  // Under 2.5 and BTTS-No overlap heavily (both are satisfied by the same low-scoring, clean-sheet
  // games), so presenting them as two separate "value" picks is misleading. When both point the
  // same defensive way, keep only the stronger one; otherwise they capture distinct angles → show both.
  // A pick flagged dropOuPick/dropBttsPick clashed with the anchored scoreline (impossible combo) —
  // it is withheld entirely rather than shown with a contradiction.
  if (!dropOuPick && !dropBttsPick && !over25 && !bttsYes) {
    picks.push(ouProbNum >= bttsProbNum ? ouPick : bttsPick);
  } else {
    if (!dropOuPick) picks.push(ouPick);
    if (!dropBttsPick) picks.push(bttsPick);
  }

  // ─── Pick 4: Predicted Score — rich, multi-source (scoreline computed in the consistency anchor above) ───
  const dataBase = useIntl
    ? `Expected goals derived from ${match.home}'s ${th.played} recent internationals (${th.wins}-${th.draws}-${th.losses}, ${homeGF} GPG) vs ${match.away}'s ${ta.played} (${ta.wins}-${ta.draws}-${ta.losses}, ${awayGF} GPG)`
    : `season-long records: ${match.home} ${th.wins}W-${th.draws}D-${th.losses}L (${homeGF} GPG, ${homeGA} conceded), ${match.away} ${ta.wins}W-${ta.draws}D-${ta.losses}L (${awayGF} GPG, ${awayGA} conceded)`;
  // When the win model and the raw goal averages point different ways (results/form/H2H favor one
  // side while goal expectancy leans the other), say so explicitly instead of leaving the xG line
  // to silently contradict the scoreline.
  const modelVsGoals = (winnerSide === "home" && lamAway > lamHome) || (winnerSide === "away" && lamHome > lamAway)
    ? ` Note: the win model favors ${winnerSide === "home" ? match.home : match.away} on results, form and H2H even though raw goal averages lean the other way — expect a tight margin.`
    : "";
  const predReason = [
    `${dataBase}.`,
    `${match.home} expected goals: ~${lamHome.toFixed(1)} (their attack vs ${match.away}'s conceding rate).`,
    `${match.away} expected goals: ~${lamAway.toFixed(1)} (their attack vs ${match.home}'s conceding rate).`,
    `The scoreline shown is the most likely exact score consistent with the Match Winner pick above.`,
    modelVsGoals.trim(),
    homeTop[0] || awayTop[0] ? `Likely scorers:${homeTop[0] ? " " + homeTop[0] : ""}${homeTop[0] && awayTop[0] ? ";" : ""}${awayTop[0] ? " " + awayTop[0] : ""}.` : "",
    polyScoreNote.trim(),
    h2hAgg ? `Recent H2H averaged ${h2hAgg.avgGoals} goals/match — directly informs the realistic scoreline range.` : "",
    `Alternatives to consider: ${predHome === predAway ? `${predHome+1}-${predAway} or ${predHome}-${predAway+1}` : `${Math.max(0,predHome-1)}-${predAway} or ${predHome}-${Math.max(0,predAway-1)}`}.`,
    `⚠️ Exact scores are HIGH variance — even the most likely outcome usually has <15% probability.`,
    importanceSentence,
  ].filter(Boolean).join(" ").trim();
  // Live correct-score price (bookmakers name it "2-1", home goals first) + the model's own
  // Poisson probability for this exact score, so the pick gets the same value/risk treatment
  // as every other priced pick.
  const scOdd = match.scoreOdds?.[`${predHome}-${predAway}`] || null;
  const scoreProbNum = Math.max(1, Math.round(pois(Math.min(predHome, 7), lamHome) * pois(Math.min(predAway, 7), lamAway) * 100));
  picks.push({ bet: `Predicted Score: ${predHome} - ${predAway}`, odds: scOdd?.odd || null, bk: scOdd?.bk || null,
    prob: `${scoreProbNum}%`,
    reason: `${predReason}${valueSentence(scoreProbNum, scOdd?.odd || 0)}`,
    risk: riskFrom(scoreProbNum, scOdd?.odd || 0) });

  // ─── Pick 5 + 6: Pre-tournament only — Key Players + H2H ───
  if (useIntl && intl) {
    // ─── Anytime Goalscorer — ONLY recommend a player who is likely to play ───
    // Drop anyone on either side's injury list; if lineups are out, drop anyone not in the XI.
    // We never recommend a doubtful/absent player — we move down to the next available attacker.
    const injuredSurnames = (list) => new Set(
      (list || [])
        .filter(s => !/no injuries|data unavailable/i.test(s))
        .map(s => surnameOf(s.replace(/\s*\(.*?\)\s*/g, "")))
        .filter(Boolean)
    );
    const homeOut = injuredSurnames(match.injuries?.home);
    const awayOut = injuredSurnames(match.injuries?.away);
    const lineup = (match.lineups || []);
    const startSurnames = (teamName) => {
      const tl = lineup.find(l => l.teamName === teamName);
      return tl ? new Set((tl.startXI || []).map(p => surnameOf(p.name)).filter(Boolean)) : null;
    };
    const homeXI = startSurnames(match.home);
    const awayXI = startSurnames(match.away);
    const isAvailable = (p) => {
      const sn = surnameOf(p.name);
      const out = p.team === match.home ? homeOut : awayOut;
      if (out.has(sn)) return false;                 // listed injured → not available
      const xi = p.team === match.home ? homeXI : awayXI;
      if (xi && !xi.has(sn)) return false;           // lineup published and player not starting → skip
      return true;
    };
    const scorerOdds = match.scorerOdds || null;
    const allPlayers = [
      ...intl.home.players.map(p => ({ ...p, team: match.home })),
      ...intl.away.players.map(p => ({ ...p, team: match.away })),
    ]
      .filter(p => p?.name && isAvailable(p))
      .sort((a, b) => (b.goals + b.assists) - (a.goals + a.assists))
      .slice(0, 4);

    if (allPlayers.length > 0) {
      // Prefer the strongest available attacker who also has a live goalscorer price; else the top one.
      const oddFor = (p) => scorerOdds?.[surnameOf(p.name)] || null;
      const topScorer = allPlayers.find(p => oddFor(p)) || allPlayers[0];
      const scorerOdd = oddFor(topScorer);
      const lineupConfirmed = (topScorer.team === match.home ? homeXI : awayXI) ? "named in the starting XI" : "not in either side's injury list";
      const opponentTeamGA = topScorer.team === match.home ? awayGA : homeGA;
      const opponentName = topScorer.team === match.home ? match.away : match.home;
      const opponentTeamCS = topScorer.team === match.home ? ta.clean_sheet : th.clean_sheet;
      const opponentTeamPlayed = topScorer.team === match.home ? ta.played : th.played;
      const others = allPlayers.slice(1, 4).filter(p => p !== topScorer);
      const reason = [
        `${topScorer.name} (${topScorer.team}) is the top available G+A contributor from both squads' 2025/26 club season: ${topScorer.goals} goals + ${topScorer.assists} assists in ${topScorer.apps} apps for ${topScorer.club} (${topScorer.gPerGame} goals per appearance).`,
        `Availability: ${topScorer.name} is expected to feature (${lineupConfirmed}) — we only recommend players likely to play.`,
        `Matchup edge: ${opponentName} concede ${opponentTeamGA}/game and have kept just ${opponentTeamCS} clean sheets in ${opponentTeamPlayed} — a leakable backline for an in-form striker to exploit.`,
        others.length > 0 ? `Backup attackers worth tracking: ${others.map(p => `${p.name} (${p.team}, ${p.goals}G in ${p.apps} apps)`).join("; ")}.` : "",
        importanceSentence,
        `⚠️ Anytime goalscorer is a high-variance market — even elite finishers blank 30-40% of matches.`,
      ].filter(Boolean).join(" ").trim();
      // Poisson on the player's club scoring rate, mildly adjusted for the opponent's leakiness —
      // honest single-player numbers land ~20-60%, not the inflated gPerGame×100+15 of before.
      const oppFactor = Math.max(0.7, Math.min(1.3, (opponentTeamGA || 1) / 1.2));
      const scorerLam = Math.max(0.1, (parseFloat(topScorer.gPerGame) || 0.15) * oppFactor);
      const scorerProb = Math.max(15, Math.min(65, Math.round((1 - Math.exp(-scorerLam)) * 100)));
      picks.push({
        bet: `Anytime Goalscorer: ${topScorer.name}`,
        odds: scorerOdd?.odd || null, bk: scorerOdd?.bk || null,
        prob: `${scorerProb}%`,
        reason: `${reason}${valueSentence(scorerProb, scorerOdd?.odd || 0)}`,
        risk: riskFrom(scorerProb, scorerOdd?.odd || 0),
      });
    }

    // H2H Trend pick — already substantive, but enhance with interpretation
    if (intl.h2h?.length && h2hAgg) {
      const lastMeeting = h2hAgg.recent;
      const dominantSide = h2hAgg.homeW > h2hAgg.awayW ? match.home : h2hAgg.awayW > h2hAgg.homeW ? match.away : null;
      const reason = [
        `Last ${h2hAgg.total} meetings (from ${match.home}'s perspective): ${h2hAgg.homeW} wins, ${h2hAgg.draws} draws, ${h2hAgg.awayW} losses.`,
        `Combined goal average: ${h2hAgg.avgGoals} per meeting — ${parseFloat(h2hAgg.avgGoals) >= 2.5 ? "trending high-scoring" : "trending low-scoring"}.`,
        dominantSide ? `${dominantSide} have historically had the upper hand in this fixture, suggesting psychological/tactical advantage.` : "Recent meetings have been finely balanced — no clear historical edge.",
        lastMeeting ? `Most recent meeting: ${lastMeeting.date} ${lastMeeting.homeName} ${lastMeeting.homeGoals}-${lastMeeting.awayGoals} ${lastMeeting.awayName} (${lastMeeting.league || "international fixture"}).` : "",
        `H2H trends are useful context but rarely predictive of single outcomes — combine with current form for sharper picks.`,
      ].filter(Boolean).join(" ").trim();
      picks.push({
        bet: `H2H Trend: ${h2hAgg.homeW}-${h2hAgg.draws}-${h2hAgg.awayW} (last ${h2hAgg.total})`, odds: null, bk: null,
        prob: null, reason, risk: 5,
      });
    }
  }

  // No em/en dashes in any matches-tab pick (user preference) - swap them for plain commas so
  // bet labels and reason text never render a "—".
  const stripDash = (s) => typeof s === "string" ? s.replace(/\s*[—–]\s*/g, ", ") : s;
  return picks.map(p => ({ ...p, bet: stripDash(p.bet), reason: stripDash(p.reason) }));
}

/* ═══ AI Chat (Anthropic API with web search) ═══ */
const BETMAN_SYSTEM = `You are BetMan, an AI sports betting assistant for football (soccer). You have access to live match data from API-Football.

Key behaviors:
- Search the web for the LATEST real fixtures, odds, injuries, and stats
- When building betslips, include real upcoming matches with real odds from bookmakers
- Provide risk scores from 1-10 for each bet
- Format responses clearly with ▸ bullets; separate sections with a blank line, NEVER with lines of characters (no ━━━, ───, ===, ___ separators)
- Always end with: ⚠️ Data-driven analysis only. Bet responsibly.
- Be concise but thorough — back claims with real data`;

// Streams the chat reply. onDelta(fullTextSoFar) is called as tokens arrive.
// Returns the final reply string (or a non-streamed message for auth/quota cases).
async function callBetManAI(userMessage, chatHistory = [], onDelta) {
  try {
    const messages = [
      ...chatHistory.map(m => ({ role: m.isUser ? "user" : "assistant", content: m.text })),
      { role: "user", content: userMessage }
    ];
    // Get current session token for auth
    const headers = { "Content-Type": "application/json" };
    if (supabase) {
      const { data: { session } } = await supabase.auth.getSession();
      if (session?.access_token) headers.Authorization = `Bearer ${session.access_token}`;
    }
    const response = await fetch(apiUrl("/api/chat"), {
      method: "POST",
      headers,
      body: JSON.stringify({ messages, system: BETMAN_SYSTEM, language: getLangName() })
    });
    if (response.status === 401) return "Please log in to use BetMan chat. Tap the ⋯ menu to sign up or log in.";
    if (response.status === 429) {
      const data = await response.json().catch(() => ({}));
      // Refresh counter to show limit reached
      if (window.__betmanRefreshUsage) window.__betmanRefreshUsage();
      // Auto-redirect to subscriptions paywall after 1.5s
      if (typeof window !== "undefined" && window.__betmanShowPaywall) {
        setTimeout(() => window.__betmanShowPaywall(), 1500);
      }
      return `You've hit your daily chat limit (${data.used}/${data.limit}) on the ${data.tier} plan. Opening upgrade options...`;
    }
    // Stream Server-Sent Events: render tokens progressively as they arrive.
    const ctype = response.headers.get("content-type") || "";
    if (response.body && ctype.includes("text/event-stream")) {
      const reader = response.body.getReader();
      const decoder = new TextDecoder();
      let buf = "", full = "", sawDone = false;
      try {
        while (true) {
          const { done, value } = await reader.read();
          if (done) break;
          buf += decoder.decode(value, { stream: true });
          const chunks = buf.split("\n\n");
          buf = chunks.pop() || "";
          for (const chunk of chunks) {
            const dataLine = chunk.split("\n").find(l => l.startsWith("data:"));
            if (!dataLine) continue;
            const json = dataLine.slice(5).trim();
            if (!json) continue;
            let evt; try { evt = JSON.parse(json); } catch { continue; }
            if (evt.type === "delta" && evt.text) { full += evt.text; onDelta && onDelta(full); }
            else if (evt.type === "reset") {
              // Server is retrying after an early stream death — drop the dead partial.
              full = ""; onDelta && onDelta(full);
            }
            else if (evt.type === "error") {
              // Service failure (upstream API down/busy) — distinct from a model answer.
              return full || t("BetMan is busy right now. Please try again in a few seconds.");
            }
            else if (evt.type === "done") { sawDone = true; if (window.__betmanRefreshUsage) window.__betmanRefreshUsage(); }
          }
        }
      } catch (streamErr) {
        // Connection died mid-stream (server restart, network blip). NEVER throw away what
        // already streamed — a partial answer beats an error message that replaces it.
        console.error("Chat stream interrupted:", streamErr);
        if (full) return `${full}\n\n⚠️ ${t("This answer was interrupted, ask again to regenerate the rest.")}`;
        return t("BetMan is busy right now. Please try again in a few seconds.");
      }
      // A complete answer ALWAYS ends with a "done" event. A stream that ends without one was
      // cut off (server restart / proxy closed it cleanly) — keep the partial, but say so.
      if (full && !sawDone) {
        return `${full}\n\n⚠️ ${t("This answer was interrupted, ask again to regenerate the rest.")}`;
      }
      return full || "Sorry, I couldn't process that. Try again!";
    }
    // Fallback: non-streamed JSON (older backend or error)
    const data = await response.json().catch(() => ({}));
    if (window.__betmanRefreshUsage) window.__betmanRefreshUsage();
    return data.reply || "Sorry, I couldn't process that. Try again!";
  } catch (err) {
    console.error("AI error:", err);
    return "I'm having trouble connecting. Please try again.";
  }
}

function getBestOdds(oddsObj) {
  let best = { value: 0, bookmaker: "" };
  for (const [bk, val] of Object.entries(oddsObj)) {
    if (val > best.value) best = { value: val, bookmaker: bk };
  }
  return best;
}

/* ─── Micro Components ─── */
function FormDots({ form }) {
  return (
    <div style={{ display: "flex", gap: 3 }}>
      {form.map((r, i) => {
        const letter = typeof r === "string" ? r : r.result;
        return (
          <span key={i} style={{
            width: 20, height: 20, borderRadius: 6, display: "inline-flex",
            alignItems: "center", justifyContent: "center",
            fontSize: 9, fontWeight: 700, fontFamily: "var(--mono)",
            background: letter === "W" ? "#22c55e18" : letter === "D" ? "#94a3b815" : "#ef444418",
            color: letter === "W" ? "#4ade80" : letter === "D" ? "#6b7280" : "#f87171",
          }}>{letter}</span>
        );
      })}
    </div>
  );
}

function StatBar({ label, value, max, suffix = "" }) {
  return (
    <div style={{ marginBottom: 14 }}>
      <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 5 }}>
        <span style={{ fontSize: 12, color: "#6b7280" }}>{label}</span>
        <span style={{ fontSize: 12, color: "#e5e7eb", fontWeight: 700, fontFamily: "var(--mono)" }}>{value}{suffix}</span>
      </div>
      <div style={{ height: 4, background: "#262626", borderRadius: 4 }}>
        <div style={{
          height: "100%", width: `${Math.min((value / max) * 100, 100)}%`, borderRadius: 4,
          background: "linear-gradient(90deg, #4ade80, #22c55e)",
          transition: "width 0.8s cubic-bezier(0.4,0,0.2,1)",
        }} />
      </div>
    </div>
  );
}

function ScoringChart({ pattern }) {
  const max = Math.max(...pattern);
  const labels = ["0'","10'","20'","30'","45'","55'","65'","75'","85'"];
  return (
    <div style={{ display: "flex", alignItems: "flex-end", gap: 3, height: 70, padding: "0 2px" }}>
      {pattern.map((v, i) => (
        <div key={i} style={{ display: "flex", flexDirection: "column", alignItems: "center", flex: 1, gap: 4 }}>
          <span style={{ fontSize: 8, color: "#4ade80", fontFamily: "var(--mono)", fontWeight: 600 }}>{v}%</span>
          <div style={{
            width: "100%", borderRadius: 3, height: Math.max(6, (v / max) * 48),
            background: "linear-gradient(to top, #4ade8040, #4ade8010)",
            border: "1px solid #4ade8025",
          }} />
          <span style={{ fontSize: 7, color: "#2c2c30", fontFamily: "var(--mono)" }}>{labels[i]}</span>
        </div>
      ))}
    </div>
  );
}

/* ─── Odds Table ─── */
// Custom bet search — type any bet (player to score, corners, handicap, BTTS…)
// and get every bookmaker's price with the best one highlighted.
function CustomBetSearch({ match }) {
  const [query, setQuery] = useState("");
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState(null);
  const [picker, setPicker] = useState(null); // { type:"players"|"teams", marketKey, marketName, players? }
  const [striker, setStriker] = useState(null);
  const sportKey = LEAGUE_TO_SPORT_KEY[match.leagueId];

  // Favored team = lowest 1X2 odds (fallback: Polymarket implied prob, then home).
  const minOdds = (obj) => { const v = Object.values(obj || {}).filter(n => typeof n === "number" && n > 0); return v.length ? Math.min(...v) : Infinity; };
  const mw = match.odds?.match_winner || {};
  const homeMin = minOdds(mw.home), awayMin = minOdds(mw.away);
  let favoredIsHome = true;
  if (homeMin !== Infinity || awayMin !== Infinity) favoredIsHome = homeMin <= awayMin;
  else if (match.polymarket?.home || match.polymarket?.away) {
    const ph = match.polymarket.home?.groupWin?.probYes ?? match.polymarket.home?.outright?.probYes ?? 0;
    const pa = match.polymarket.away?.groupWin?.probYes ?? match.polymarket.away?.outright?.probYes ?? 0;
    favoredIsHome = ph >= pa;
  }
  const favoredId = favoredIsHome ? match.homeId : match.awayId;
  const favoredName = favoredIsHome ? match.home : match.away;

  // Fetch the favored team's striker for the search-bar placeholder hint.
  useEffect(() => {
    if (!favoredId) return;
    let cancelled = false;
    fetch(apiUrl(`/api/team-striker/${favoredId}`))
      .then(r => (r.ok ? r.json() : null))
      .then(d => { if (!cancelled && d?.striker) setStriker(d.striker); })
      .catch(() => {});
    return () => { cancelled = true; };
  }, [favoredId]);

  // Recommended bets. Player chips open a player picker (squad list from the live
  // market). These markets all carry deep, reliable SportMonks coverage for WC fixtures.
  const chips = [
    { label: t("Anytime goalscorer"), market: "player_goal_scorer_anytime", type: "player" },
    { label: t("First goalscorer"), market: "player_first_goal_scorer", type: "player" },
    { label: t("Player shot on target"), market: "player_shots_on_target", type: "player" },
    { label: t("Player to assist"), market: "player_assists", type: "player" },
    { label: t("Player to be booked"), market: "player_to_receive_card", type: "player" },
  ];

  // opts: string (free-text query) OR object { query, market, player, side, point, listPlayers }.
  const runSearch = async (opts) => {
    let body;
    if (typeof opts === "string") body = { query: opts.trim() };
    else if (opts && (opts.query || opts.market || opts.listPlayers)) body = { ...opts };
    else body = { query: query.trim() };
    if (typeof body.query === "string") body.query = body.query.trim();
    if (!body.query && !body.market) return;

    setLoading(true); setError(null);
    if (!body.listPlayers) { setResult(null); setPicker(null); }
    try {
      const headers = { "Content-Type": "application/json" };
      if (supabase) {
        const { data: { session } } = await supabase.auth.getSession();
        if (session?.access_token) headers.Authorization = `Bearer ${session.access_token}`;
      }
      const res = await fetch(apiUrl("/api/match-odds-search"), {
        method: "POST",
        headers,
        body: JSON.stringify({ sportKey, home: match.home, away: match.away, ...body }),
      });
      if (res.status === 401) { setError(t("Please log in to search odds.")); return; }
      if (!res.ok) { setError(t("Odds search failed. Try again.")); return; }
      const data = await res.json();
      if (data.mode === "players") {
        setPicker({ type: "players", marketKey: data.marketKey, marketName: data.marketName, players: data.players });
        setResult(null);
      } else {
        setResult(data);
        setPicker(null);
      }
    } catch (e) {
      setError(t("Odds search failed. Try again."));
    } finally {
      setLoading(false);
    }
  };

  const onChip = (chip) => {
    setError(null); setResult(null); setQuery("");
    if (chip.type === "team") {
      setPicker({ type: "teams", marketKey: chip.market, marketName: chip.label });
    } else {
      setPicker(null);
      runSearch({ market: chip.market, listPlayers: true });
    }
  };

  const acceptSuggestion = (sug) => {
    if (sug.category === "player" && sug.players?.length) {
      setPicker({ type: "players", marketKey: sug.marketKey, marketName: sug.marketName, players: sug.players });
      setResult(null);
    } else {
      setResult({ ...sug, found: true });
    }
  };

  if (!sportKey) return null;

  return (
    <div style={{ marginTop: 16, background: "#060608", borderRadius: 4, border: "1px solid #262626", padding: 14 }}>
      <div style={{ fontSize: 11, fontWeight: 700, color: "#60a5fa", fontFamily: "var(--mono)", letterSpacing: 0.4, marginBottom: 4 }}>{t("SEARCH ANY BET")}</div>
      <div style={{ fontSize: 10, color: "#4b5563", fontFamily: "var(--mono)", marginBottom: 10 }}>{t("Find any market — players, corners, cards, handicaps — with average bookmaker's odds.")}</div>
      <div style={{ display: "flex", gap: 6 }}>
        <input
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          onKeyDown={(e) => { if (e.key === "Enter") runSearch(); }}
          placeholder={t("e.g. {player} to score, over 9.5 corners, {team} total goals", { player: striker || `${favoredName} ${t("striker")}`, team: favoredName })}
          style={{
            flex: 1, background: "#060608", border: "1px solid #262626", borderRadius: 4,
            padding: "10px 12px", color: "#e5e7eb", fontSize: 12, fontFamily: "var(--mono)", outline: "none",
          }}
        />
        <button onClick={() => runSearch()} disabled={loading || !query.trim()} style={{
          padding: "10px 16px", borderRadius: 4, border: "1px solid #60a5fa40",
          background: loading || !query.trim() ? "#060608" : "#60a5fa18", color: "#60a5fa",
          fontSize: 12, fontWeight: 700, fontFamily: "var(--mono)", cursor: loading || !query.trim() ? "default" : "pointer",
        }}>{loading ? "…" : t("Search")}</button>
      </div>

      {/* Recommended bet chips — player chips open a player picker, team chips open a Home/Away picker */}
      <div style={{ display: "flex", gap: 6, flexWrap: "wrap", marginTop: 10 }}>
        {chips.map(c => (
          <button key={c.label} onClick={() => onChip(c)} disabled={loading} style={{
            padding: "5px 10px", borderRadius: 4, fontSize: 10, fontFamily: "var(--mono)",
            background: "#060608", border: "1px solid #262626", color: "#9ca3af", cursor: loading ? "default" : "pointer",
          }}>{c.label}</button>
        ))}
      </div>

      {/* Picker: choose a player or a team for the selected chip */}
      {picker && (
        <div style={{ marginTop: 12, background: "#060608", borderRadius: 4, border: "1px solid #262626", padding: 12 }}>
          <div style={{ fontSize: 10, color: "#60a5fa", fontFamily: "var(--mono)", fontWeight: 700, marginBottom: 8 }}>
            {picker.marketName} — {picker.type === "teams" ? t("pick a team") : t("pick a player")}
          </div>
          <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
            {picker.type === "teams" ? (
              [{ side: "home", name: match.home }, { side: "away", name: match.away }].map(t => (
                <button key={t.side} onClick={() => runSearch({ market: picker.marketKey, side: t.side })} disabled={loading} style={{
                  padding: "7px 14px", borderRadius: 4, fontSize: 11, fontFamily: "var(--mono)", fontWeight: 700,
                  background: "#060608", border: "1px solid #60a5fa40", color: "#60a5fa", cursor: loading ? "default" : "pointer",
                }}>{t.name}</button>
              ))
            ) : (
              picker.players.map(p => (
                <button key={p} onClick={() => runSearch({ market: picker.marketKey, player: p })} disabled={loading} style={{
                  padding: "6px 12px", borderRadius: 4, fontSize: 11, fontFamily: "var(--mono)",
                  background: "#060608", border: "1px solid #262626", color: "#d1d5db", cursor: loading ? "default" : "pointer",
                }}>{p}</button>
              ))
            )}
          </div>
        </div>
      )}

      {error && <div style={{ marginTop: 12, fontSize: 12, color: "#f87171", fontFamily: "var(--mono)" }}>{error}</div>}

      {result && !result.found && (
        <div style={{ marginTop: 12 }}>
          <div style={{ fontSize: 12, color: "#9ca3af", fontFamily: "var(--mono)", lineHeight: 1.5 }}>{result.message}</div>
          {result.suggestion && (
            <div style={{ display: "flex", gap: 6, marginTop: 10 }}>
              <button onClick={() => acceptSuggestion(result.suggestion)} style={{
                padding: "7px 14px", borderRadius: 4, border: "1px solid #4ade8040",
                background: "#4ade8018", color: "#4ade80", fontSize: 11, fontWeight: 700,
                fontFamily: "var(--mono)", cursor: "pointer",
              }}>{t("Yes, show")} {result.suggestion.marketName}</button>
              <button onClick={() => setResult(null)} style={{
                padding: "7px 14px", borderRadius: 4, border: "1px solid #262626",
                background: "#060608", color: "#9ca3af", fontSize: 11, fontWeight: 700,
                fontFamily: "var(--mono)", cursor: "pointer",
              }}>{t("No")}</button>
            </div>
          )}
        </div>
      )}

      {result && result.found && (
        <div style={{ marginTop: 14 }}>
          <div style={{ fontSize: 11, color: "#60a5fa", fontFamily: "var(--mono)", fontWeight: 700, marginBottom: 8 }}>
            {result.label || result.marketName} · {result.home} vs {result.away}
          </div>
          <div style={{ overflowX: "auto", paddingBottom: 6 }}>
            <table style={{ minWidth: "100%", borderCollapse: "separate", borderSpacing: "0 3px" }}>
              <thead>
                <tr>
                  <th style={{ textAlign: "left", fontSize: 10, color: "#4b5563", padding: "6px 10px", fontFamily: "var(--mono)", position: "sticky", left: 0, background: "#060608", zIndex: 1 }}>{t("Selection")}</th>
                  <th style={{ fontSize: 9, color: "#4b5563", padding: "6px 8px", fontFamily: "var(--mono)", textAlign: "right", whiteSpace: "nowrap" }}>{t("Average odds")}</th>
                </tr>
              </thead>
              <tbody>
                {result.rows.map((row, ri) => {
                  const prices = Object.values(row.prices || {}).map(Number).filter(v => isFinite(v) && v > 0);
                  const avg = prices.length ? prices.reduce((a, b) => a + b, 0) / prices.length : null;
                  return (
                    <tr key={ri}>
                      <td style={{ fontSize: 12, color: "#d1d5db", padding: "10px 10px", background: "#060608", borderRadius: "10px 0 0 10px", fontWeight: 600, position: "sticky", left: 0, zIndex: 1, whiteSpace: "nowrap" }}>{row.label}</td>
                      <td style={{
                        textAlign: "right", padding: "10px 12px", background: "#060608",
                        borderRadius: "0 10px 10px 0", fontFamily: "var(--mono)", fontSize: 13,
                        fontWeight: 700, color: "#4ade80", minWidth: 72,
                      }}>
                        {avg != null ? avg.toFixed(2) : "—"}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
          <div style={{ fontSize: 9, color: "#4b5563", padding: "6px 4px", fontFamily: "var(--mono)" }}>▸ {t("Average odds across bookmakers · live")}</div>
        </div>
      )}
    </div>
  );
}

function OddsTable({ match }) {
  const o = match.odds || {};
  const mw = o.match_winner || {};
  const dc = o.double_chance || {};
  const markets = [
    { name: t("Home Win"), odds: mw.home || {} },
    { name: t("Draw"), odds: mw.draw || {} },
    { name: t("Away Win"), odds: mw.away || {} },
    { name: t("{team} or Draw", { team: match.home }), odds: dc.home_draw || {} },
    { name: t("{team} or Draw", { team: match.away }), odds: dc.away_draw || {} },
    { name: t("{home} or {away}", { home: match.home, away: match.away }), odds: dc.home_away || {} },
    { name: t("Over 2.5 Goals"), odds: o.over_25 || {} },
    { name: t("BTTS"), odds: o.btts || {} },
  ].filter(m => Object.keys(m.odds).length > 0);
  // Even when standard pre-match markets aren't loaded yet, still offer the
  // custom bet search + Polymarket panel.
  if (!markets.length) return (
    <>
      <div style={{ textAlign: "center", padding: 30, color: "#4b5563", fontSize: 12, fontFamily: "var(--mono)" }}>{t("Standard odds not loaded yet — search a specific bet below.")}</div>
      <CustomBetSearch match={match} />
      <PolymarketPanel polymarket={match.polymarket} home={match.home} away={match.away} />
    </>
  );

  // Bookmaker columns are derived dynamically from the live Odds API data (keyed by bookmaker key,
  // titled via _titles). Showing only keys that have at least one price avoids empty columns.
  const titles = o._titles || {};
  const allKeys = new Set();
  markets.forEach(m => Object.keys(m.odds).forEach(k => allKeys.add(k)));
  // Order bookmakers by how many of the shown markets they price (most coverage on
  // the left, least on the right); break ties alphabetically.
  const coverage = (k) => markets.reduce((n, m) => n + (typeof m.odds[k] === "number" ? 1 : 0), 0);
  // Market-GROUP coverage = how many distinct bet types (match result / double chance /
  // over-under / BTTS) a bookmaker prices. Some books only post the match result this far
  // out, which adds nothing but a column of "–" across every other row. Hide those once
  // we still have a healthy number of columns so the table stays dense, not a wall of blanks.
  const groups = [
    [mw.home, mw.draw, mw.away],
    [dc.home_draw, dc.away_draw, dc.home_away],
    [o.over_25],
    [o.btts],
  ];
  const groupCov = (k) => groups.reduce((n, g) => n + (g.some(od => od && typeof od[k] === "number") ? 1 : 0), 0);
  const ranked = [...allKeys]
    .map(k => ({ id: k, name: titles[k] || k, cov: coverage(k), gcov: groupCov(k) }))
    .sort((a, b) => b.cov - a.cov || a.name.localeCompare(b.name));
  const multi = ranked.filter(b => b.gcov >= 2);
  const visibleBks = multi.length >= 6 ? multi : ranked;
  const totalCols = visibleBks.length;

  // Best odds = highest value AMONG VISIBLE BOOKMAKERS for each market. Computing over all keys
  // (including hidden ones like betfair_ex_uk, unibet_fr) would mean BEST never highlights a
  // visible column when a hidden key has a slightly higher price.
  const bestForMarket = (marketOdds) => {
    let bestVal = 0;
    for (const b of visibleBks) {
      const v = marketOdds[b.id];
      if (typeof v === "number" && v > bestVal) bestVal = v;
    }
    return bestVal;
  };

  return (
    <>
      <div style={{ overflowX: "auto", border: "1px solid #262626", borderRadius: 4 }}>
        <table style={{ minWidth: "100%", borderCollapse: "collapse" }}>
          <thead>
            <tr>
              <th style={{ textAlign: "left", fontSize: 10, color: "#4b5563", padding: "10px 12px", fontFamily: "var(--mono)", position: "sticky", left: 0, background: "#060608", zIndex: 1, borderBottom: "1px solid #262626", borderRight: "1px solid #262626" }}>{t("Market")}</th>
              {visibleBks.map((b, bi) => (
                <th key={b.id} style={{
                  fontSize: 10, color: "#fff", padding: "10px 10px", fontFamily: "var(--mono)",
                  textAlign: "center", whiteSpace: "nowrap", fontWeight: 600,
                  borderBottom: "1px solid #262626",
                  borderRight: bi === totalCols - 1 ? "none" : "1px solid #262626",
                }}>{b.name}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {markets.map((m, mi) => {
              const bestVal = bestForMarket(m.odds);
              const isLastRow = mi === markets.length - 1;
              return (
                <tr key={m.name}>
                  <td style={{ fontSize: 12, color: "#d1d5db", padding: "13px 12px", background: "#060608", fontWeight: 600, position: "sticky", left: 0, zIndex: 1, whiteSpace: "nowrap", borderRight: "1px solid #262626", borderBottom: isLastRow ? "none" : "1px solid #262626" }}>{m.name}</td>
                  {visibleBks.map((b, bi) => {
                    const val = m.odds[b.id]; const isBest = bestVal > 0 && val === bestVal;
                    const isLast = bi === totalCols - 1;
                    return (
                      <td key={b.id} style={{
                        textAlign: "center", padding: "13px 8px",
                        background: isBest ? "#4ade8014" : "#060608",
                        borderRight: isLast ? "none" : "1px solid #262626",
                        borderBottom: isLastRow ? "none" : "1px solid #262626",
                        fontFamily: "var(--mono)", fontSize: 15,
                        fontWeight: isBest ? 700 : 600,
                        color: isBest ? "#4ade80" : "#e5e7eb",
                        minWidth: 56,
                      }}>
                        {val ? val.toFixed(2) : "—"}
                        {isBest && val && <div style={{ fontSize: 8, fontWeight: 700, color: "#4ade80", marginTop: 2, letterSpacing: 0.4 }}>BEST</div>}
                      </td>
                    );
                  })}
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
      <div style={{ fontSize: 10, color: "#4b5563", padding: "8px 4px", fontFamily: "var(--mono)" }}>
        ▸ {t("Scroll right to see more bookmakers")}
      </div>

      {/* Custom bet search — any market, all bookmakers, best highlighted */}
      <CustomBetSearch match={match} />

      {/* Polymarket Predictions — crowd-sourced WC outright winner probabilities */}
      <PolymarketPanel polymarket={match.polymarket} home={match.home} away={match.away} />
    </>
  );
}

function PolymarketPanel({ polymarket, home, away }) {
  if (!polymarket || (!polymarket.home && !polymarket.away)) return null;

  // Per-match implied probabilities. Use group-winner % when both teams have it
  // (way more accurate per-match signal), otherwise fall back to WC outright.
  const homeBest = polymarket.home?.groupWin || polymarket.home?.outright || polymarket.home || null;
  const awayBest = polymarket.away?.groupWin || polymarket.away?.outright || polymarket.away || null;
  const usingGroup = !!(polymarket.home?.groupWin && polymarket.away?.groupWin);
  const usingOutright = !usingGroup && !!(polymarket.home?.outright && polymarket.away?.outright);

  let implied = null;
  if (homeBest?.probYes && awayBest?.probYes) {
    const pH = homeBest.probYes;
    const pA = awayBest.probYes;
    const total = pH + pA;
    const rawHome = pH / total;
    const rawAway = pA / total;
    const closeness = total > 0 ? 1 - Math.abs(rawHome - rawAway) : 0;
    const drawShare = 0.18 + 0.12 * closeness;
    const winShareLeft = 1 - drawShare;
    implied = {
      home: rawHome * winShareLeft,
      draw: drawShare,
      away: rawAway * winShareLeft,
    };
  }

  // Row used for the outright (WC winner) section.
  const OutrightRow = ({ data, team }) => {
    const oR = data?.outright;
    if (!oR) return (
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "10px 14px", borderBottom: "1px solid #26262650" }}>
        <span style={{ fontSize: 12, color: "#9ca3af" }}>{team}</span>
        <span style={{ fontSize: 11, color: "#4b5563", fontFamily: "var(--mono)" }}>{t("Not in Polymarket WC outright")}</span>
      </div>
    );
    const pct = (oR.probYes * 100).toFixed(1);
    return (
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "12px 14px", borderBottom: "1px solid #26262650" }}>
        <div>
          <div style={{ fontSize: 13, color: "#e5e7eb", fontWeight: 700 }}>{team}</div>
          <div style={{ fontSize: 10, color: "#4b5563", fontFamily: "var(--mono)", marginTop: 2 }}>{oR.question}</div>
        </div>
        <div style={{ textAlign: "right" }}>
          <div style={{ fontSize: 22, fontWeight: 700, color: "#22c55e", fontFamily: "var(--mono)", lineHeight: 1 }}>{pct}%</div>
          <div style={{ fontSize: 9, color: "#22c55e80", fontFamily: "var(--mono)", marginTop: 2 }}>{t("to win WC")}</div>
        </div>
      </div>
    );
  };

  // Row used for the group-winner section.
  const GroupRow = ({ data, team }) => {
    const gW = data?.groupWin;
    if (!gW) return (
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "10px 14px", borderBottom: "1px solid #26262650" }}>
        <span style={{ fontSize: 12, color: "#9ca3af" }}>{team}</span>
        <span style={{ fontSize: 11, color: "#4b5563", fontFamily: "var(--mono)" }}>{t("Not in Polymarket group markets")}</span>
      </div>
    );
    const pct = (gW.probYes * 100).toFixed(1);
    return (
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "12px 14px", borderBottom: "1px solid #26262650" }}>
        <div>
          <div style={{ fontSize: 13, color: "#e5e7eb", fontWeight: 700 }}>{team}</div>
          <div style={{ fontSize: 10, color: "#4b5563", fontFamily: "var(--mono)", marginTop: 2 }}>{gW.question}</div>
        </div>
        <div style={{ textAlign: "right" }}>
          <div style={{ fontSize: 22, fontWeight: 700, color: "#22c55e", fontFamily: "var(--mono)", lineHeight: 1 }}>{pct}%</div>
          <div style={{ fontSize: 9, color: "#22c55e80", fontFamily: "var(--mono)", marginTop: 2 }}>{t("to top Group {group}", { group: gW.group })}</div>
        </div>
      </div>
    );
  };

  const fmt = (p) => (p * 100).toFixed(1) + "%";
  const odd = (p) => p > 0.005 ? (1 / p).toFixed(2) : "—";

  return (
    <div style={{ marginTop: 16, background: "#060608", borderRadius: 4, border: "1px solid #22c55e30", overflow: "hidden" }}>
      {/* Header */}
      <div style={{ background: "#22c55e0d", padding: "10px 14px", borderBottom: "1px solid #22c55e20", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
        <div>
          <div style={{ fontSize: 11, fontWeight: 700, color: "#22c55e", fontFamily: "var(--mono)", letterSpacing: 0.4 }}>POLYMARKET</div>
          <div style={{ fontSize: 10, color: "#4b5563", fontFamily: "var(--mono)", marginTop: 2 }}>{t("Crowd-sourced prediction market")}</div>
        </div>
        <span style={{ fontSize: 8, fontWeight: 700, color: "#22c55e", fontFamily: "var(--mono)", background: "#22c55e15", padding: "3px 8px", borderRadius: 6, letterSpacing: 0.5 }}>LIVE</span>
      </div>

      {/* THIS MATCH — implied per-match probability (derived from outright ratio) */}
      {implied && (
        <div style={{ background: "#060608", padding: "14px", borderBottom: "1px solid #22c55e20" }}>
          <div style={{ fontSize: 10, color: "#22c55e", fontFamily: "var(--mono)", letterSpacing: 0.4, marginBottom: 8, fontWeight: 700 }}>
            {t("THIS MATCH — IMPLIED")}
          </div>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 8 }}>
            {[
              { label: home, prob: implied.home, side: "Home" },
              { label: t("Draw"), prob: implied.draw, side: "Draw" },
              { label: away, prob: implied.away, side: "Away" },
            ].map((o) => (
              <div key={o.side} style={{ background: "#060608", borderRadius: 4, border: "1px solid #22c55e20", padding: "10px 8px", textAlign: "center" }}>
                <div style={{ fontSize: 10, color: "#9ca3af", fontFamily: "var(--mono)", marginBottom: 4, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{o.label}</div>
                <div style={{ fontSize: 20, fontWeight: 700, color: "#22c55e", fontFamily: "var(--mono)", lineHeight: 1 }}>{fmt(o.prob)}</div>
                <div style={{ fontSize: 9, color: "#22c55e80", fontFamily: "var(--mono)", marginTop: 4 }}>@ {odd(o.prob)}</div>
              </div>
            ))}
          </div>
          <div style={{ fontSize: 9, color: "#4b5563", marginTop: 10, fontFamily: "var(--mono)", textAlign: "center", lineHeight: 1.5 }}>
            ▸ {usingGroup ? t("Derived from Polymarket's per-group winner probabilities (strong per-match signal)") : usingOutright ? t("Derived from Polymarket's WC-winner outright probabilities (broad signal — interpret with care)") : ""}
          </div>
        </div>
      )}

      {/* GROUP STAGE — per-team probability to top their group */}
      {(polymarket.home?.groupWin || polymarket.away?.groupWin) && (
        <div style={{ padding: "0" }}>
          <div style={{ fontSize: 10, color: "#22c55e", fontFamily: "var(--mono)", letterSpacing: 0.4, padding: "12px 14px 6px", fontWeight: 700 }}>
            {t("GROUP STAGE — WIN GROUP PROBABILITY")}
          </div>
          <GroupRow data={polymarket.home} team={home} />
          <GroupRow data={polymarket.away} team={away} />
        </div>
      )}

      {/* TOURNAMENT — raw outright winner probabilities */}
      {(polymarket.home?.outright || polymarket.away?.outright) && (
        <div style={{ padding: "0" }}>
          <div style={{ fontSize: 10, color: "#22c55e", fontFamily: "var(--mono)", letterSpacing: 0.4, padding: "12px 14px 6px", fontWeight: 700 }}>
            {t("TOURNAMENT — WC WINNER PROBABILITY")}
          </div>
          <OutrightRow data={polymarket.home} team={home} />
          <OutrightRow data={polymarket.away} team={away} />
        </div>
      )}
      <div style={{ fontSize: 9, color: "#4b5563", padding: "8px 14px", fontFamily: "var(--mono)", textAlign: "center" }}>
        ▸ {t("Live odds on the country winning the FIFA World Cup 2026 outright")}
      </div>
    </div>
  );
}

// "Time ago" label for a news article publish date.
function timeAgo(iso) {
  if (!iso) return "";
  const diff = Date.now() - new Date(iso).getTime();
  if (isNaN(diff)) return "";
  const h = Math.floor(diff / 3600000);
  if (h < 1) return "just now";
  if (h < 24) return `${h}h ago`;
  const d = Math.floor(h / 24);
  return d === 1 ? "1 day ago" : `${d} days ago`;
}

/* ─── Match Detail Panel ─── */
function MatchDetail({ match, onReloadDetails }) {
  const [tab, setTab] = useState("picks");
  const [teamNews, setTeamNews] = useState(null);
  const [newsLoading, setNewsLoading] = useState(false);

  // Lazily fetch latest headlines for both teams the first time the News tab opens.
  useEffect(() => {
    if (tab !== "injuries" || teamNews !== null || !match?.home || !match?.away) return;
    let cancelled = false;
    setNewsLoading(true);
    const qs = new URLSearchParams({
      home: match.home, away: match.away,
      homeId: match.homeId || "", awayId: match.awayId || "",
      date: match.fixtureDate || "",
    });
    fetch(apiUrl(`/api/team-news?${qs.toString()}`))
      .then(r => (r.ok ? r.json() : { home: [], away: [] }))
      .then(d => { if (!cancelled) setTeamNews(d || { home: [], away: [] }); })
      .catch(() => { if (!cancelled) setTeamNews({ home: [], away: [] }); })
      .finally(() => { if (!cancelled) setNewsLoading(false); });
    return () => { cancelled = true; };
  }, [tab, teamNews, match?.home, match?.away]);

  const [scorers, setScorers] = useState(null);
  const [scorersLoading, setScorersLoading] = useState(false);

  // Lazily aggregate last-5 scorers for both teams the first time the Form tab opens.
  // Kept out of the main match-detail load to avoid a large parallel API burst.
  useEffect(() => {
    if (tab !== "form" || scorers !== null) return;
    if (!match?.homeForm?.length && !match?.awayForm?.length) return;
    let cancelled = false;
    setScorersLoading(true);
    Promise.all([
      loadTeamScorers(match.homeId, match.homeForm),
      loadTeamScorers(match.awayId, match.awayForm),
    ])
      .then(([home, away]) => { if (!cancelled) setScorers({ home, away }); })
      .catch(() => { if (!cancelled) setScorers({ home: [], away: [] }); })
      .finally(() => { if (!cancelled) setScorersLoading(false); });
    return () => { cancelled = true; };
  }, [tab, scorers, match?.homeForm, match?.awayForm, match?.homeId, match?.awayId]);

  const picks = match.detailsLoaded ? buildPicks(match) : null;
  const hasLineups = match.lineups && match.lineups.length > 0;

  // Auto-refresh lineups when within 2 hours of kickoff (until lineups arrive)
  useEffect(() => {
    if (hasLineups || !match?.fixtureDate) return;
    const kickoff = new Date(match.fixtureDate).getTime();
    const minsUntil = (kickoff - Date.now()) / 60000;
    // Poll only if kickoff is in (-30 min, +120 min) window
    if (minsUntil > 120 || minsUntil < -30) return;
    const interval = setInterval(() => {
      if (onReloadDetails) onReloadDetails(match);
    }, 120000); // every 2 min
    return () => clearInterval(interval);
  }, [hasLineups, match?.fixtureDate]);
  const tabs = [
    { id: "picks", label: t("Picks"), icon: "target" },
    { id: "odds", label: t("Odds"), icon: "odds" },
    { id: "squad", label: t("Squad"), icon: "users" },
    { id: "form", label: t("Form"), icon: "chart" },
    { id: "injuries", label: t("News"), icon: "news" },
  ];

  const riskDots = (r) => "●".repeat(r) + "○".repeat(10 - r);

  return (
    <div>
      <div style={{ display: "flex", gap: 4, marginBottom: 18, overflowX: "auto", paddingBottom: 2 }}>
        {tabs.map(tb => (
          <button key={tb.id} onClick={() => setTab(tb.id)} style={{
            padding: "7px 13px", borderRadius: 4,
            border: tab === tb.id ? "1px solid #4ade8030" : "1px solid #262626",
            cursor: "pointer", fontSize: 11, fontWeight: 600,
            background: tab === tb.id ? "#4ade800d" : "#060608",
            color: tab === tb.id ? "#4ade80" : "#6b7280",
            whiteSpace: "nowrap", transition: "all 0.2s", display: "flex", alignItems: "center", gap: 5,
          }}><Icon name={tb.icon} size={15} />{tb.label}</button>
        ))}
      </div>

      {tab === "picks" && (
        <div style={{ display: "grid", gap: 14 }}>
          {picks ? picks.map((p, i) => {
            const riskCol = p.risk <= 3 ? "#4ade80" : p.risk <= 6 ? "#facc15" : "#f87171";
            const riskLabel = p.risk <= 3 ? t("LOW RISK") : p.risk <= 6 ? t("MEDIUM") : t("HIGH RISK");
            return (
              <div key={i} style={{
                background: "#060608", borderRadius: 4, border: "1px solid #262626",
                overflow: "hidden",
              }}>
                <div style={{ display: "flex" }}>
                  <div style={{ width: 4, background: riskCol, flexShrink: 0 }} />
                  <div style={{ padding: "18px 20px", flex: 1 }}>
                    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 10 }}>
                      <div style={{ fontSize: 16, fontWeight: 700, color: "#f9fafb", lineHeight: 1.3, flex: 1, marginRight: 10 }}>{p.bet}</div>
                      {p.prob && <span style={{ fontSize: 10, fontWeight: 700, color: "#4ade80", background: "#4ade800d", padding: "3px 10px", borderRadius: 4, fontFamily: "var(--mono)", border: "1px solid #4ade8020", flexShrink: 0 }}>{t("MODEL PROB")} {p.prob}</span>}
                    </div>
                    {p.odds > 0 && (
                      <div style={{ display: "flex", alignItems: "baseline", gap: 10, marginBottom: 16 }}>
                        <span style={{ fontSize: 30, fontWeight: 700, color: "#4ade80", fontFamily: "var(--mono)", lineHeight: 1 }}>@{typeof p.odds === "number" ? p.odds.toFixed(2) : p.odds}</span>
                        <span style={{ fontSize: 11, color: "#9ca3af", fontFamily: "var(--mono)", background: "transparent", border: "1px solid #262626", padding: "3px 9px", borderRadius: 6 }}>{p.bk}</span>
                      </div>
                    )}
                    <div style={{ fontSize: 12, color: "#9ca3af", lineHeight: 1.7, marginBottom: 16, paddingTop: 14, borderTop: "1px solid #262626" }}>{p.reason}</div>
                    <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                      <div style={{ fontSize: 11, fontFamily: "var(--mono)", letterSpacing: 0.4 }}>
                        <span style={{ color: riskCol }}>{"●".repeat(p.risk) + "○".repeat(10 - p.risk)}</span>
                      </div>
                      <span style={{ fontSize: 9, fontWeight: 700, color: riskCol, fontFamily: "var(--mono)", background: `${riskCol}12`, padding: "2px 8px", borderRadius: 6, letterSpacing: 0.5 }}>{riskLabel} {p.risk}/10</span>
                    </div>
                  </div>
                </div>
              </div>
            );
          }) : (
            <div style={{ background: "#060608", borderRadius: 4, padding: 40, border: "1px solid #262626", textAlign: "center" }}>
              <div style={{ display: "flex", justifyContent: "center", marginBottom: 10, color: "#2c2c30" }}><Icon name="chart" size={28} /></div>
              <div style={{ color: "#6b7280", fontSize: 13, marginBottom: 4 }}>{t("Prediction data not available")}</div>
              <div style={{ color: "#2c2c30", fontSize: 11, fontFamily: "var(--mono)" }}>{t("Check back closer to kickoff")}</div>
            </div>
          )}
          <div style={{ fontSize: 10, color: "#2c2c30", textAlign: "center", padding: "6px 0", fontFamily: "var(--mono)" }}>
            {t("Data-driven analysis only. Bet responsibly.")}
          </div>
        </div>
      )}

      {tab === "odds" && <OddsTable match={match} />}

      {tab === "squad" && (
        <div style={{ display: "grid", gap: 12 }}>
          {hasLineups ? match.lineups.map(team => (
            <div key={team.teamId} style={{ background: "#060608", borderRadius: 4, padding: 16, border: "1px solid #262626" }}>
              <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 4 }}>
                {team.teamLogo && <img src={team.teamLogo} alt="" style={{ width: 22, height: 22, objectFit: "contain" }} />}
                <span style={{ fontSize: 14, fontWeight: 700, color: "#e5e7eb" }}>{team.teamName}</span>
              </div>
              <div style={{ display: "flex", gap: 10, marginBottom: 14 }}>
                {team.formation && <span style={{ fontSize: 11, color: "#4ade80", fontFamily: "var(--mono)", fontWeight: 700 }}>{team.formation}</span>}
                <span style={{ fontSize: 11, color: "#4b5563", fontFamily: "var(--mono)" }}>{t("Coach:")} {team.coach}</span>
              </div>
              <div style={{ fontSize: 10, fontWeight: 700, color: "#6b7280", textTransform: "uppercase", letterSpacing: 0.4, marginBottom: 8, fontFamily: "var(--mono)" }}>{t("Starting XI")}</div>
              <div style={{ display: "grid", gap: 2, marginBottom: 14 }}>
                {team.startXI.map((p, i) => (
                  <div key={i} style={{ display: "flex", alignItems: "center", gap: 8, padding: "5px 0", borderBottom: "1px solid #26262650" }}>
                    <span style={{ fontSize: 11, color: "#4b5563", fontFamily: "var(--mono)", width: 22, textAlign: "right" }}>{p.number || ""}</span>
                    <span style={{ fontSize: 10, color: "#4ade80", fontFamily: "var(--mono)", width: 22, textAlign: "center",
                      background: "#4ade800d", borderRadius: 4, padding: "1px 4px", fontWeight: 700 }}>{p.pos || "?"}</span>
                    <span style={{ fontSize: 12, color: "#d1d5db" }}>{p.name}</span>
                  </div>
                ))}
              </div>
              {team.subs.length > 0 && (
                <>
                  <div style={{ fontSize: 10, fontWeight: 700, color: "#6b7280", textTransform: "uppercase", letterSpacing: 0.4, marginBottom: 8, fontFamily: "var(--mono)" }}>{t("Substitutes")}</div>
                  <div style={{ display: "grid", gap: 2 }}>
                    {team.subs.map((p, i) => (
                      <div key={i} style={{ display: "flex", alignItems: "center", gap: 8, padding: "4px 0" }}>
                        <span style={{ fontSize: 11, color: "#2c2c30", fontFamily: "var(--mono)", width: 22, textAlign: "right" }}>{p.number || ""}</span>
                        <span style={{ fontSize: 10, color: "#6b7280", fontFamily: "var(--mono)", width: 22, textAlign: "center" }}>{p.pos || ""}</span>
                        <span style={{ fontSize: 12, color: "#6b7280" }}>{p.name}</span>
                      </div>
                    ))}
                  </div>
                </>
              )}
            </div>
          )) : (
            <div style={{ background: "#060608", borderRadius: 4, padding: 30, border: "1px solid #262626", textAlign: "center" }}>
              <div style={{ display: "flex", justifyContent: "center", marginBottom: 10, color: "#2c2c30" }}><Icon name="clipboard" size={24} /></div>
              <div style={{ fontSize: 13, color: "#6b7280", marginBottom: 4 }}>{t("Lineups not yet announced")}</div>
              <div style={{ fontSize: 11, color: "#2c2c30", fontFamily: "var(--mono)", marginBottom: 14 }}>{t("Usually available ~1 hour before kickoff")}</div>
              <button onClick={async () => {
                if (!match || !onReloadDetails) return;
                onReloadDetails(match);
              }} style={{
                padding: "8px 18px", borderRadius: 4, border: "1px solid #4ade8030",
                background: "#4ade8012", color: "#4ade80", fontSize: 12,
                cursor: "pointer", fontFamily: "var(--mono)", fontWeight: 700,
                display: "inline-flex", alignItems: "center", gap: 6,
              }}><Icon name="refresh" size={13} /> {t("Refresh now")}</button>
              <div style={{ fontSize: 9, color: "#2c2c30", marginTop: 8, fontFamily: "var(--mono)" }}>{t("Auto-refreshes every 2 min near kickoff")}</div>
            </div>
          )}
        </div>
      )}

      {tab === "form" && (
        <div style={{ display: "grid", gap: 14 }}>
          {[{ name: match.home, form: match.homeForm, logo: match.homeLogo, stats: match.predictions?.teams?.home, scorers: scorers?.home || [] },
            { name: match.away, form: match.awayForm, logo: match.awayLogo, stats: match.predictions?.teams?.away, scorers: scorers?.away || [] }].map(tm => {
            const hasRichForm = tm.form?.length > 0 && typeof tm.form[0] === "object" && tm.form[0].opponent !== "—";
            const hasSimpleForm = tm.form?.length > 0;
            return (
              <div key={tm.name} style={{ background: "#060608", borderRadius: 4, padding: 18, border: "1px solid #262626" }}>
                <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 6 }}>
                  {tm.logo && <img src={tm.logo} alt="" style={{ width: 22, height: 22, objectFit: "contain" }} />}
                  <span style={{ fontSize: 14, fontWeight: 700, color: "#e5e7eb" }}>{tm.name}</span>
                </div>
                <div style={{ fontSize: 10, color: "#4b5563", fontFamily: "var(--mono)", marginBottom: 14, textTransform: "uppercase", letterSpacing: 1 }}>{t("Last 5 matches")}</div>

                {hasRichForm ? (
                  <div style={{ display: "grid", gap: 6 }}>
                    {tm.form.map((m, i) => {
                      const col = m.result === "W" ? "#4ade80" : m.result === "D" ? "#6b7280" : "#f87171";
                      return (
                        <div key={i} style={{
                          display: "flex", alignItems: "center", gap: 10, padding: "10px 12px",
                          background: `${col}06`, borderRadius: 4, border: `1px solid ${col}15`,
                        }}>
                          <span style={{
                            width: 28, height: 28, borderRadius: 4, display: "inline-flex",
                            alignItems: "center", justifyContent: "center",
                            fontSize: 11, fontWeight: 700, fontFamily: "var(--mono)",
                            background: `${col}18`, color: col, border: `1px solid ${col}30`, flexShrink: 0,
                          }}>{m.result}</span>
                          <div style={{ flex: 1, minWidth: 0 }}>
                            <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                              {m.opponentLogo && <img src={m.opponentLogo} alt="" style={{ width: 14, height: 14, objectFit: "contain", flexShrink: 0 }} />}
                              <span style={{ fontSize: 12, color: "#d1d5db", fontWeight: 600, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                                {m.wasHome ? "vs" : "@"} {m.opponent}
                              </span>
                            </div>
                          </div>
                          <span style={{ fontSize: 14, fontWeight: 700, color: "#e5e7eb", fontFamily: "var(--mono)", flexShrink: 0 }}>
                            {m.homeGoals}-{m.awayGoals}
                          </span>
                          <span style={{ fontSize: 9, color: "#4b5563", fontFamily: "var(--mono)", flexShrink: 0, width: 42, textAlign: "right" }}>{m.date}</span>
                        </div>
                      );
                    })}
                  </div>
                ) : hasSimpleForm ? (
                  <div>
                    <div style={{ display: "flex", gap: 8, marginBottom: 16 }}>
                      {tm.form.map((f, i) => {
                        const letter = typeof f === "string" ? f : f.result;
                        const col = letter === "W" ? "#4ade80" : letter === "D" ? "#6b7280" : "#f87171";
                        return (
                          <span key={i} style={{
                            width: 40, height: 40, borderRadius: 4, display: "inline-flex",
                            alignItems: "center", justifyContent: "center",
                            fontSize: 14, fontWeight: 700, fontFamily: "var(--mono)",
                            background: `${col}15`, color: col, border: `1px solid ${col}30`,
                          }}>{letter}</span>
                        );
                      })}
                    </div>
                    {tm.stats && (
                      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 8 }}>
                        {[
                          { label: t("Played"), val: tm.stats.played },
                          { label: t("Wins"), val: tm.stats.wins },
                          { label: t("Draws"), val: tm.stats.draws },
                          { label: t("Losses"), val: tm.stats.losses },
                          { label: t("Goals/G"), val: tm.stats.goals_for_avg },
                          { label: t("Clean Sh."), val: tm.stats.clean_sheet },
                        ].map(s => (
                          <div key={s.label} style={{ textAlign: "center", padding: "8px 0", background: "#060608", borderRadius: 4, border: "1px solid #26262650" }}>
                            <div style={{ fontSize: 16, fontWeight: 700, color: "#e5e7eb", fontFamily: "var(--mono)" }}>{s.val}</div>
                            <div style={{ fontSize: 8, color: "#4b5563", fontFamily: "var(--mono)", textTransform: "uppercase", letterSpacing: 0.5, marginTop: 2 }}>{s.label}</div>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                ) : (
                  <div style={{ fontSize: 12, color: "#4b5563", textAlign: "center", padding: 10 }}>{t("Form data unavailable")}</div>
                )}

                {(tm.scorers.length > 0 || scorersLoading) && (
                  <div style={{ marginTop: 16 }}>
                    <div style={{ fontSize: 10, color: "#4b5563", fontFamily: "var(--mono)", marginBottom: 10, textTransform: "uppercase", letterSpacing: 1 }}>{t("Scorers (last 5)")}</div>
                    {scorersLoading && tm.scorers.length === 0 ? (
                      <div style={{ fontSize: 12, color: "#4b5563", padding: "4px 0" }}>{t("Loading scorers…")}</div>
                    ) : (
                    <div style={{ display: "grid", gap: 6 }}>
                      {tm.scorers.map((s, i) => (
                        <div key={i} style={{
                          display: "flex", alignItems: "center", gap: 10, padding: "8px 12px",
                          background: "#060608", borderRadius: 4, border: "1px solid #26262650",
                        }}>
                          <span style={{ fontSize: 12, color: "#d1d5db", fontWeight: 600, flex: 1, minWidth: 0, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{s.name}</span>
                          <span style={{
                            display: "inline-flex", alignItems: "center", gap: 4, fontSize: 12, fontWeight: 700,
                            color: "#4ade80", fontFamily: "var(--mono)", flexShrink: 0,
                          }}>{s.goals} <Icon name="ball" size={12} /></span>
                        </div>
                      ))}
                    </div>
                    )}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      {tab === "injuries" && (
        <div style={{ display: "grid", gap: 12 }}>
          {[{ l: match.home, inj: match.injuries?.home || [], logo: match.homeLogo, news: teamNews?.home || [] },
            { l: match.away, inj: match.injuries?.away || [], logo: match.awayLogo, news: teamNews?.away || [] }].map(({ l, inj, logo, news }) => {
            const out = (inj || []).filter(s => s && !/^no injuries|^no injury|^data unavailable|^no injury data/i.test(s));
            return (
              <div key={l} style={{ background: "#060608", borderRadius: 4, padding: 16, border: "1px solid #262626" }}>
                <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 12 }}>
                  {logo && <img src={logo} alt="" style={{ width: 22, height: 22, objectFit: "contain" }} />}
                  <span style={{ fontSize: 13, fontWeight: 700, color: "#e5e7eb" }}>{l}</span>
                  <span style={{ marginLeft: "auto", fontSize: 9, color: "#4b5563", fontFamily: "var(--mono)", textTransform: "uppercase", letterSpacing: 1 }}>{t("Team news")}</span>
                </div>

                {/* Latest headlines */}
                <div style={{ fontSize: 9, color: "#4b5563", fontFamily: "var(--mono)", textTransform: "uppercase", letterSpacing: 0.4, marginBottom: 8 }}>{t("Latest headlines")}</div>
                {newsLoading && teamNews === null ? (
                  <div style={{ fontSize: 12, color: "#4b5563", padding: "4px 0 10px" }}>{t("Loading headlines…")}</div>
                ) : news.length ? (
                  <div style={{ display: "grid", gap: 8, marginBottom: 6 }}>
                    {news.map((a, i) => (
                      <a key={i} href={a.url} target="_blank" rel="noopener noreferrer" style={{ textDecoration: "none" }}>
                        <div style={{ display: "flex", gap: 10, padding: "8px 10px", background: "#060608", borderRadius: 4, border: "1px solid #26262650" }}>
                          {a.image && <img src={a.image} alt="" style={{ width: 48, height: 48, objectFit: "cover", borderRadius: 4, flexShrink: 0 }} />}
                          <div style={{ minWidth: 0 }}>
                            <div style={{ fontSize: 12, color: "#e5e7eb", fontWeight: 600, lineHeight: "16px", display: "-webkit-box", WebkitLineClamp: 2, WebkitBoxOrient: "vertical", overflow: "hidden" }}>{a.title}</div>
                            <div style={{ fontSize: 10, color: "#6b7280", fontFamily: "var(--mono)", marginTop: 4 }}>
                              {a.source}{a.publishedAt ? ` · ${timeAgo(a.publishedAt)}` : ""}
                            </div>
                          </div>
                        </div>
                      </a>
                    ))}
                  </div>
                ) : (
                  <div style={{ fontSize: 12, color: "#4b5563", padding: "4px 0 10px" }}>{t("No recent headlines found")}</div>
                )}

                {/* Availability */}
                <div style={{ fontSize: 9, color: "#4b5563", fontFamily: "var(--mono)", textTransform: "uppercase", letterSpacing: 0.4, margin: "12px 0 8px" }}>{t("Availability")}</div>
                {out.length ? out.map((injury, i) => (
                  <div key={i} style={{ display: "flex", alignItems: "center", gap: 8, padding: "7px 0", borderBottom: i < out.length - 1 ? "1px solid #262626" : "none" }}>
                    <div style={{ width: 6, height: 6, borderRadius: "50%", background: "#f87171", flexShrink: 0 }} />
                    <span style={{ fontSize: 12, color: "#fca5a5" }}>{injury}</span>
                  </div>
                )) : (
                  <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                    <div style={{ width: 6, height: 6, borderRadius: "50%", background: "#4ade80", flexShrink: 0 }} />
                    <span style={{ fontSize: 12, color: "#86efac" }}>{t("No injuries reported — full squad available")}</span>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

/* ─── Match Card ─── */
function WorldCupBanner() {
  // FIFA World Cup 2026: June 11 – July 19, 2026
  const wcStart = new Date("2026-06-11T16:00:00Z");
  const wcEnd = new Date("2026-07-19T22:00:00Z");
  const now = new Date();
  // Only show during May 1 → July 19, 2026
  const showFrom = new Date("2026-05-01T00:00:00Z");
  if (now < showFrom || now > wcEnd) return null;

  const ongoing = now >= wcStart && now <= wcEnd;
  const daysUntil = Math.ceil((wcStart - now) / (1000 * 60 * 60 * 24));

  return (
    <div style={{
      background: "linear-gradient(135deg, #fbbf2418, #ef444412, #4ade8018)",
      border: "1px solid #fbbf2430",
      borderRadius: 4, padding: "14px 16px", marginBottom: 2,
      position: "relative", overflow: "hidden",
    }}>
      <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
        <div style={{ color: "#fbbf24", display: "flex" }}><Icon name="trophy" size={30} /></div>
        <div style={{ flex: 1 }}>
          <div style={{ fontSize: 13, fontWeight: 700, color: "#fbbf24", fontFamily: "var(--mono)", marginBottom: 2 }}>
            FIFA WORLD CUP 2026
          </div>
          <div style={{ fontSize: 11, color: "#e5e7eb" }}>
            {ongoing
              ? t("Tournament is LIVE! Tap the World Cup filter below to browse all group-stage matches.")
              : `${daysUntil === 1 ? t("Kick-off in {n} day", { n: daysUntil }) : t("Kick-off in {n} days", { n: daysUntil })} · June 11 → July 19`}
          </div>
        </div>
        {!ongoing && (
          <div style={{
            background: "#fbbf2420", color: "#fbbf24", padding: "6px 12px",
            borderRadius: 4, fontSize: 14, fontWeight: 700, fontFamily: "var(--mono)",
            border: "1px solid #fbbf2440",
          }}>
            {daysUntil}d
          </div>
        )}
        {ongoing && (
          <div style={{
            background: "#4ade8020", color: "#4ade80", padding: "6px 12px",
            borderRadius: 4, fontSize: 11, fontWeight: 700, fontFamily: "var(--mono)",
            border: "1px solid #4ade8040",
            animation: "pulse 2s infinite",
            display: "inline-flex", alignItems: "center", gap: 5,
          }}>
            <Icon name="live" size={9} color="#ef4444" /> LIVE
          </div>
        )}
      </div>
    </div>
  );
}

function MatchCard({ match, onSelect }) {
  const bestHome = getBestOdds(match.odds?.match_winner?.home || {});
  const bestDraw = getBestOdds(match.odds?.match_winner?.draw || {});
  const bestAway = getBestOdds(match.odds?.match_winner?.away || {});
  const hasOdds = bestHome.value > 0;

  // Implied (de-vigged) probabilities from decimal odds — drive the % pills + bars.
  let probs = null, leader = 0;
  if (hasOdds) {
    const rawH = 1 / bestHome.value;
    const rawD = bestDraw.value > 0 ? 1 / bestDraw.value : 0;
    const rawA = 1 / bestAway.value;
    const sum = rawH + rawD + rawA || 1;
    probs = { home: rawH / sum, draw: rawD / sum, away: rawA / sum };
    leader = Math.max(probs.home, probs.draw, probs.away);
  }

  // 1/X/2 coupon cell — favorite (lowest odd / highest implied prob) is highlighted green.
  const OddsCell = ({ label, odds, isLead }) => (
    <div className="odds-cell" style={{
      flex: 1, textAlign: "center", padding: "9px 4px", borderRadius: 4,
      background: isLead ? "rgba(74,222,128,0.09)" : "#0a0e0c",
      border: isLead ? "1px solid rgba(74,222,128,0.30)" : "1px solid #262626",
    }}>
      <div style={{ fontSize: 10, fontWeight: 700, letterSpacing: 0.6, textTransform: "uppercase", color: isLead ? "#4ade80" : "#6e7a76", marginBottom: 4 }}>{label}</div>
      <div style={{ fontSize: 16, fontWeight: 700, fontFamily: "var(--mono)", color: isLead ? "#4ade80" : "#f5f7f6" }}>{odds.toFixed(2)}</div>
    </div>
  );

  const TeamRow = ({ logo, name, fav }) => (
    <div style={{ display: "flex", alignItems: "center", gap: 10, minWidth: 0 }}>
      {logo
        ? <img src={logo} alt="" style={{ width: 26, height: 26, objectFit: "contain", flexShrink: 0 }} />
        : <div style={{ width: 26, height: 26, borderRadius: 7, background: "#080a09", border: "1px solid #262626", flexShrink: 0 }} />}
      <span style={{ fontSize: 15.5, fontWeight: fav ? 700 : 600, color: fav ? "#ffffff" : "#cdd4d0", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{name}</span>
    </div>
  );

  const homeFav = hasOdds && probs.home === leader;
  const awayFav = hasOdds && probs.away === leader;

  return (
    <div className="match-card" onClick={() => onSelect(match)} style={{
      background: "#060608", border: "1px solid #262626",
      borderRadius: 4, padding: 14, cursor: "pointer",
    }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 12, minWidth: 0 }}>
        <span style={{ fontSize: 10.5, fontWeight: 600, color: "#9aa6a1", textTransform: "uppercase", letterSpacing: 0.6, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{match.league}</span>
        <div style={{ display: "flex", alignItems: "center", gap: 7, flexShrink: 0, marginLeft: 10 }}>
          <span style={{ fontSize: 11, color: "#9aa6a1", fontFamily: "var(--mono)" }}>{match.date} · {match.time}</span>
          <Icon name="arrow-right" size={13} color="#3a423d" />
        </div>
      </div>

      <div style={{ display: "flex", flexDirection: "column", gap: 9 }}>
        <TeamRow logo={match.homeLogo} name={match.home} fav={homeFav} />
        <TeamRow logo={match.awayLogo} name={match.away} fav={awayFav} />
      </div>

      {hasOdds ? (
        <div style={{ display: "flex", gap: 7, marginTop: 13 }}>
          <OddsCell label="1" odds={bestHome.value} isLead={homeFav} />
          {bestDraw.value > 0 && <OddsCell label="X" odds={bestDraw.value} isLead={false} />}
          <OddsCell label="2" odds={bestAway.value} isLead={awayFav} />
        </div>
      ) : (
        <div style={{ marginTop: 12, paddingTop: 11, borderTop: "1px solid #262626", display: "flex", alignItems: "center", gap: 6, fontSize: 11.5, color: "#6e7a76", fontFamily: "var(--mono)" }}>
          <Icon name="chart" size={12} color="#6e7a76" />
          {t("Tap for picks & analysis →")}
        </div>
      )}
    </div>
  );
}

/* ─── Chat Bubble ─── */
function formatMessage(text) {
  // Strip separator lines (━━━, ───, ===, ___): box-drawing runs can't word-wrap, so they
  // overflow the chat bubble and draw a line across the screen. Blank lines already separate
  // sections. Defensive even though the prompt now bans them (old cached history can contain them).
  text = (text || "").replace(/[ \t]*[━─═―▬—_=]{3,}[ \t]*/g, " ");
  // No em/en dashes in chat answers (user preference) - swap them for plain commas before render.
  text = text.replace(/\s*[—–]\s*/g, ", ");
  // Split by **bold** markers and render with proper formatting
  const parts = text.split(/(\*\*[^*]+\*\*)/g);
  return parts.map((part, i) => {
    if (part.startsWith("**") && part.endsWith("**")) {
      return <strong key={i} style={{ fontWeight: 700, color: "#e5e7eb" }}>{part.slice(2, -2)}</strong>;
    }
    return <span key={i}>{part}</span>;
  });
}

function ChatBubble({ msg, isUser, userQuestion, onFeedback }) {
  useLang();
  const [rating, setRating] = useState(null);   // 'up' | 'down' | null
  const [showBox, setShowBox] = useState(false);
  const [comment, setComment] = useState("");
  const [sent, setSent] = useState(false);

  const onUp = () => {
    if (rating === "up") return;
    setRating("up"); setShowBox(false); setSent(true);
    onFeedback?.("up", msg, userQuestion, "");
  };
  const onDown = () => {
    setRating("down"); setSent(false); setShowBox(true);
  };
  const submitDown = () => {
    onFeedback?.("down", msg, userQuestion, comment.trim());
    setShowBox(false); setSent(true);
  };

  const ico = (active, on) => (active ? on : "#5a6661");

  return (
    <div style={{
      display: "flex", justifyContent: isUser ? "flex-end" : "flex-start",
      marginBottom: 12, animation: "msgIn 0.3s ease",
    }}>
      {!isUser && (
        <img src={MASCOT_SMALL} alt="BetMan" style={{
          width: 32, height: 32, borderRadius: 4, marginRight: 10, flexShrink: 0, marginTop: 2,
          objectFit: "cover",
          animation: "bobSmall 2.5s ease-in-out infinite",
        }} />
      )}
      <div style={{ display: "flex", flexDirection: "column", alignItems: isUser ? "flex-end" : "flex-start", maxWidth: "82%", minWidth: 0 }}>
        <div style={{
          padding: "14px 16px", maxWidth: "100%",
          borderRadius: isUser ? "20px 20px 6px 20px" : "20px 20px 20px 6px",
          background: isUser ? "#4ade80" : "#101413",
          color: isUser ? "#020617" : "#d1d5db",
          fontSize: 13, lineHeight: 1.7, whiteSpace: "pre-wrap",
          border: isUser ? "none" : "1px solid #262626",
          fontWeight: isUser ? 600 : 400,
        }}>{isUser ? msg : formatMessage(msg)}</div>

        {!isUser && onFeedback && (
          <div style={{ marginTop: 6, marginLeft: 4, width: "100%" }}>
            {sent ? (
              <span style={{ fontSize: 11, color: "#6e7a76", fontFamily: "var(--mono)" }}>
                {t("Thanks for your feedback")}
              </span>
            ) : (
              <div style={{ display: "flex", gap: 10, alignItems: "center" }}>
                <button onClick={onUp} aria-label="like" style={{
                  background: "none", border: "none", padding: 2, cursor: "pointer", lineHeight: 0,
                }}><Icon name="thumbs-up" size={15} color={ico(rating === "up", "#4ade80")} strokeWidth={1.6} /></button>
                <button onClick={onDown} aria-label="dislike" style={{
                  background: "none", border: "none", padding: 2, cursor: "pointer", lineHeight: 0,
                }}><Icon name="thumbs-down" size={15} color={ico(rating === "down", "#f87171")} strokeWidth={1.6} /></button>
              </div>
            )}
            {showBox && !sent && (
              <div style={{ marginTop: 8, maxWidth: 320 }}>
                <textarea
                  value={comment}
                  onChange={(e) => setComment(e.target.value)}
                  placeholder={t("What went wrong? (optional)")}
                  rows={2}
                  style={{
                    width: "100%", boxSizing: "border-box", resize: "vertical",
                    background: "#0c0f0e", border: "1px solid #333333", borderRadius: 4,
                    color: "#f5f7f6", fontSize: 12, padding: "8px 10px", outline: "none",
                    fontFamily: "var(--ui)", lineHeight: 1.5,
                  }}
                />
                <button onClick={submitDown} style={{
                  marginTop: 6, padding: "6px 14px", borderRadius: 4, cursor: "pointer",
                  background: "#4ade80", border: "none", color: "#020617",
                  fontSize: 11, fontWeight: 700, fontFamily: "var(--mono)",
                  textTransform: "uppercase", letterSpacing: 0.4,
                }}>{t("Send")}</button>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

/* ═══ News Tab ═══ */
/* Standalone football news page for bettors. Phase (b) section 1: aggregated headlines
   feed (GNews + RSS) from GET /api/news/feed, gated behind login. Odds-movers & injury
   tracker sections come next. */
// Exactly three article types — Result (green, post-match), Team News (purple/blue,
// injuries/lineups/previews), Transfer (amber).
const NEWS_TAG_COLORS = {
  Result: "#4ade80", "Team News": "#818cf8", Transfer: "#fbbf24",
};
// Normalize any tag (incl. legacy/cached ones like News/Preview/Injury/Lineup/Suspension) to
// the three canonical types so the feed never shows a stale label after the backend change.
function normNewsTag(tag) {
  return tag === "Result" || tag === "Transfer" ? tag : "Team News";
}

// Flag (nations → flagcdn ISO code) or club crest (API-Football team id, every id verified
// against the live API) for the team label on news cards. Unknown names render text-only.
const NEWS_TEAM_FLAG = {
  England: "gb-eng", Scotland: "gb-sct", Wales: "gb-wls", France: "fr", Germany: "de",
  Spain: "es", Italy: "it", Netherlands: "nl", Portugal: "pt", Belgium: "be", Croatia: "hr",
  Switzerland: "ch", Denmark: "dk", Austria: "at", Poland: "pl", Norway: "no", Sweden: "se",
  Serbia: "rs", Ukraine: "ua", Slovakia: "sk", Slovenia: "si", Hungary: "hu", Romania: "ro",
  Greece: "gr", Albania: "al", Georgia: "ge", Brazil: "br", Argentina: "ar", Uruguay: "uy",
  Colombia: "co", Ecuador: "ec", Chile: "cl", Peru: "pe", Paraguay: "py", Bolivia: "bo",
  Venezuela: "ve", Canada: "ca", Panama: "pa", "Costa Rica": "cr", Honduras: "hn",
  Jamaica: "jm", Haiti: "ht", Morocco: "ma", Senegal: "sn", Ghana: "gh", Nigeria: "ng",
  Cameroon: "cm", Egypt: "eg", Algeria: "dz", Tunisia: "tn", "Ivory Coast": "ci",
  "South Africa": "za", "Cape Verde": "cv", Japan: "jp", Australia: "au",
  "Saudi Arabia": "sa", Iran: "ir", Qatar: "qa", Jordan: "jo", Uzbekistan: "uz",
  "New Zealand": "nz", Curacao: "cw", Mexico: "mx", USA: "us", "South Korea": "kr", Turkey: "tr",
};
const NEWS_TEAM_CREST = {
  "Real Madrid": 541, Barcelona: 529, "Atletico Madrid": 530, "Manchester United": 33,
  "Manchester City": 50, Liverpool: 40, Arsenal: 42, Chelsea: 49, Tottenham: 47,
  Newcastle: 34, "Aston Villa": 66, "West Ham": 48, Everton: 45, Leeds: 63, Brighton: 51,
  "Crystal Palace": 52, "Nottingham Forest": 65, "Bayern Munich": 157,
  "Borussia Dortmund": 165, "RB Leipzig": 173, Leverkusen: 168, Juventus: 496,
  "Inter Milan": 505, "AC Milan": 489, Napoli: 492, Lazio: 487, "Paris Saint-Germain": 85,
  Marseille: 81, Monaco: 91, Lyon: 80, Ajax: 194, PSV: 197, Feyenoord: 209, Benfica: 211,
  Porto: 212, Sporting: 228, Celtic: 247, Rangers: 257, Galatasaray: 645, Fenerbahce: 611,
  "Al-Hilal": 2932, "Al-Nassr": 2939, "Inter Miami": 9568,
};
const newsTeamBadge = (name) => NEWS_TEAM_FLAG[name]
  ? `https://flagcdn.com/w40/${NEWS_TEAM_FLAG[name]}.png`
  : NEWS_TEAM_CREST[name]
  ? `https://media.api-sports.io/football/teams/${NEWS_TEAM_CREST[name]}.png`
  : null;
const NEWS_SECTIONS = [
  { id: "headlines", label: "Headlines", endpoint: "/api/news/feed" },
];
function NewsTab({ user, tier, requireLogin, openPaywall }) {
  useLang();
  const isPaid = tier !== "free";
  const [section] = useState("headlines");
  const [cacheBy, setCacheBy] = useState({});         // { sectionId: items[] }
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(false);

  const load = async (sec, force) => {
    if (!force && cacheBy[sec]) { setLoading(false); setError(false); return; }
    setLoading(true);
    setError(false);
    const ep = NEWS_SECTIONS.find(s => s.id === sec)?.endpoint;
    try {
      const { data: { session } } = await supabase.auth.getSession();
      const headers = {};
      if (session?.access_token) headers.Authorization = `Bearer ${session.access_token}`;
      const res = await fetch(`${API_ROOT}${ep}`, { headers });
      if (!res.ok) throw new Error("feed failed");
      const data = await res.json();
      setCacheBy(prev => ({ ...prev, [sec]: data.items || [] }));
    } catch {
      setError(true);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!user || !isPaid) return;
    let cancelled = false;
    if (cacheBy[section]) { setLoading(false); setError(false); return; }
    setLoading(true);
    setError(false);
    const ep = NEWS_SECTIONS.find(s => s.id === section)?.endpoint;
    (async () => {
      try {
        const { data: { session } } = await supabase.auth.getSession();
        const headers = {};
        if (session?.access_token) headers.Authorization = `Bearer ${session.access_token}`;
        const res = await fetch(`${API_ROOT}${ep}`, { headers });
        if (!res.ok) throw new Error("feed failed");
        const data = await res.json();
        if (!cancelled) setCacheBy(prev => ({ ...prev, [section]: data.items || [] }));
      } catch {
        if (!cancelled) setError(true);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [user, isPaid, section]); // eslint-disable-line react-hooks/exhaustive-deps

  const items = cacheBy[section];

  return (
    <div style={{ flex: 1, overflow: "auto", padding: 14 }}>
      <h1 style={{
        fontSize: 22, fontWeight: 700, color: "#fff", margin: "4px 0 2px",
        fontFamily: "var(--mono)",
        display: "flex", alignItems: "center", gap: 9,
      }}>
        <Icon name="news" size={22} /> {t("News")}
      </h1>
      <p style={{ fontSize: 13, color: "#6b7280", margin: "0 0 14px" }}>
        {t("Football news that matters for your bets.")}
      </p>

      {!user ? (
        <div style={{
          border: "1px solid #262626", borderRadius: 4, padding: "32px 18px",
          textAlign: "center", color: "#4b5563", background: "#060608",
        }}>
          <div style={{ display: "flex", justifyContent: "center", marginBottom: 10, color: "#4b5563" }}><Icon name="lock" size={32} strokeWidth={1.4} /></div>
          <div style={{ fontSize: 14, fontWeight: 600, color: "#9ca3af", marginBottom: 14 }}>
            {t("Log in to read the latest football news")}
          </div>
          <button onClick={requireLogin} style={{
            background: "#4ade80", color: "#000", border: "none", borderRadius: 4,
            padding: "10px 22px", fontWeight: 700, fontSize: 13, cursor: "pointer",
            fontFamily: "var(--mono)",
          }}>
            {t("Log In")}
          </button>
        </div>
      ) : !isPaid ? (
        <div style={{
          border: "1px solid #262626", borderRadius: 4, padding: "32px 18px",
          textAlign: "center", color: "#4b5563", background: "#060608",
        }}>
          <div style={{ display: "flex", justifyContent: "center", marginBottom: 10, color: "#fbbf24" }}><Icon name="zap" size={32} strokeWidth={1.4} /></div>
          <div style={{ fontSize: 14, fontWeight: 700, color: "#e5e7eb", marginBottom: 6 }}>
            {t("News is a Pro feature")}
          </div>
          <div style={{ fontSize: 12, color: "#9ca3af", marginBottom: 16 }}>
            {t("Upgrade to Pro or Elite for unlimited access to the football news feed.")}
          </div>
          <button onClick={openPaywall} style={{
            background: "#4ade80", color: "#000", border: "none", borderRadius: 4,
            padding: "10px 22px", fontWeight: 700, fontSize: 13, cursor: "pointer",
            fontFamily: "var(--mono)",
            display: "inline-flex", alignItems: "center", gap: 6,
          }}>
            <Icon name="zap" size={13} color="#000" /> {t("Upgrade to Pro")}
          </button>
        </div>
      ) : loading ? (
        <div style={{ textAlign: "center", color: "#6b7280", padding: 40, fontSize: 13 }}>
          {t("Loading news…")}
        </div>
      ) : error ? (
        <div style={{ textAlign: "center", padding: 40 }}>
          <div style={{ color: "#9ca3af", fontSize: 13, marginBottom: 12 }}>{t("Couldn't load news")}</div>
          <button onClick={() => load(section, true)} style={{
            background: "#262626", color: "#e5e7eb", border: "1px solid #2c2c30",
            borderRadius: 4, padding: "8px 18px", fontWeight: 600, fontSize: 12, cursor: "pointer",
          }}>
            {t("Retry")}
          </button>
        </div>
      ) : !items || items.length === 0 ? (
        <div style={{ textAlign: "center", color: "#4b5563", padding: 40, fontSize: 13 }}>
          {t("No news right now")}
        </div>
      ) : (
        <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
          {items.map((a, i) => {
            const tag = normNewsTag(a.tag);
            const tagColor = NEWS_TAG_COLORS[tag];
            return (
              <a key={i} href={a.url} target="_blank" rel="noopener noreferrer" style={{
                display: "flex", gap: 12, textDecoration: "none",
                border: "1px solid #1a1f1c", borderRadius: 6, padding: 12,
                background: "#060608", alignItems: "center",
              }}>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 6 }}>
                    <span style={{
                      fontSize: 10, fontWeight: 700, fontFamily: "var(--mono)",
                      color: tagColor,
                      background: `${tagColor}1a`,
                      border: `1px solid ${tagColor}59`,
                      borderRadius: 5, padding: "2px 7px", textTransform: "uppercase", letterSpacing: 0.4,
                    }}>
                      {t(tag)}
                    </span>
                    {/* Club/nation the article covers — nation flag / club crest + bold white
                        name, so the team is recognizable at a glance. */}
                    {a.teams?.length > 0 && (
                      <span style={{
                        display: "inline-flex", alignItems: "center", gap: 5,
                        fontSize: 11, fontWeight: 700, fontFamily: "var(--mono)",
                        color: "#f5f7f6", textTransform: "uppercase", letterSpacing: 0.5,
                        whiteSpace: "nowrap", overflow: "hidden", maxWidth: 220, flexShrink: 0,
                      }}>
                        {a.teams.slice(0, 2).map((tm, j) => (
                          <span key={j} style={{ display: "inline-flex", alignItems: "center", gap: 5 }}>
                            {j > 0 && <span style={{ color: "#4b5563", fontWeight: 400 }}>·</span>}
                            {newsTeamBadge(tm) && (
                              <img src={newsTeamBadge(tm)} alt="" loading="lazy"
                                onError={(e) => { e.currentTarget.style.display = "none"; }}
                                style={NEWS_TEAM_FLAG[tm]
                                  ? { width: 16, height: 12, objectFit: "cover", borderRadius: 2, display: "block" }
                                  : { width: 14, height: 14, objectFit: "contain", display: "block" }} />
                            )}
                            {tm}
                          </span>
                        ))}
                      </span>
                    )}
                    <span style={{ fontSize: 11, color: "#8b9794", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                      {a.source}{a.publishedAt ? ` · ${timeAgo(a.publishedAt)}` : ""}
                    </span>
                  </div>
                  <div style={{ fontSize: 14, fontWeight: 600, color: "#f5f7f6", lineHeight: 1.4 }}>
                    {a.title}
                  </div>
                </div>
              </a>
            );
          })}
        </div>
      )}
    </div>
  );
}

/* ══════════════════════════════════════════
   MAIN APP
   ══════════════════════════════════════════ */
export default function BetMan() {
  // Subscribe to language changes — re-renders the entire app tree when the user
  // switches language (child components read the active language via the imported t()).
  const { lang } = useLang();
  const [screen, setScreen] = useState("home");
  const [selectedMatch, setSelectedMatch] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);
  // Cache enriched match details by id so re-opens are instant — WITHOUT writing
  // form/odds back into the `matches` list (keeps list cards clean & uniform).
  const detailCacheRef = useRef({});
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [typing, setTyping] = useState(false);
  const [matches, setMatches] = useState(() => readMatchesCache()?.data || []); // hydrate instantly from cache
  const [matchesLoading, setMatchesLoading] = useState(false);
  const [matchesFetched, setMatchesFetched] = useState(false);
  const [matchesError, setMatchesError] = useState(false);
  const [leagueFilter, setLeagueFilter] = useState(() => localStorage.getItem("betman_league_filter") || "all");
  const [wcStageFilter, setWcStageFilter] = useState(() => localStorage.getItem("betman_wc_stage") || "all");
  const [menuOpen, setMenuOpen] = useState(false);
  // Track viewport width so suggestion chips can show their full prompt on wider screens.
  const [vw, setVw] = useState(typeof window !== "undefined" ? window.innerWidth : 420);
  const [vh, setVh] = useState(typeof window !== "undefined" ? window.innerHeight : 800);
  useEffect(() => {
    const onResize = () => { setVw(window.innerWidth); setVh(window.innerHeight); };
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []);
  const [authMode, setAuthMode] = useState(null); // null | "login" | "signup"
  const [user, setUser] = useState(null);
  const [authEmail, setAuthEmail] = useState("");
  const [authPass, setAuthPass] = useState("");
  const [authName, setAuthName] = useState("");
  const [authError, setAuthError] = useState("");
  const [authLoading, setAuthLoading] = useState(false);
  const [otpCode, setOtpCode] = useState("");
  const [otpSentTo, setOtpSentTo] = useState(() => {
    // Restore pending verification email from previous session (popup recovery),
    // but only if it was requested within the last hour.
    try {
      const raw = localStorage.getItem("betman_pending_verify");
      if (!raw) return "";
      // New format: JSON { email, ts }. Old format: plain string (treat as expired).
      if (raw.startsWith("{")) {
        const { email, ts } = JSON.parse(raw);
        if (email && ts && Date.now() - ts < 60 * 60 * 1000) return email;
      }
      // Expired or legacy entry — clear it so the login screen shows the standard login UI.
      localStorage.removeItem("betman_pending_verify");
      return "";
    } catch { return ""; }
  });
  const [resendCooldown, setResendCooldown] = useState(0);
  const [subscription, setSubscription] = useState(null); // { tier, status, usage, limits }
  const [showPaywall, setShowPaywall] = useState(false);
  const [paywallLoading, setPaywallLoading] = useState(null); // priceId being processed
  const [billingPeriod, setBillingPeriod] = useState("monthly"); // monthly | yearly
  const chatEnd = useRef(null);

  // Reusable: refresh subscription/usage from server
  const refreshSubscription = async () => {
    if (!user || !supabase) return;
    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session?.access_token) return;
      const res = await fetch(apiUrl("/api/me"), {
        headers: { Authorization: `Bearer ${session.access_token}` },
      });
      if (res.ok) setSubscription(await res.json());
    } catch (e) { console.error("Failed to fetch subscription:", e); }
  };

  // ─── Stripe Checkout (opens Stripe-hosted page) ───
  const openCheckout = async (priceId, withTrial = false) => {
    if (!user) { setMenuOpen(true); setAuthMode("signup"); return; }
    if (!supabase) return;
    setPaywallLoading(priceId);
    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session?.access_token) return;
      const res = await fetch(apiUrl("/api/create-checkout-session"), {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${session.access_token}` },
        body: JSON.stringify({ priceId, trial: withTrial }),
      });
      const data = await res.json();
      if (data.url) {
        window.location.href = data.url; // redirect to Stripe-hosted checkout
      } else if (data.upgraded) {
        // Plan change applied directly via Stripe subscriptions.update (prorated).
        // Modifies the existing subscription in place — the old plan is replaced, so there's never a second subscription billed.
        const planName = { pro: "Pro", elite: "Elite", ultimate: "Ultimate" }[data.newTier] || data.newTier;
        alert(`✅ Plan changed! You're now on the ${planName} plan. Your previous plan was replaced and the difference was prorated to your card.`);
        if (window.__betmanRefreshUsage) window.__betmanRefreshUsage();
        setShowPaywall(false);
      } else {
        alert(data.error || "Could not start checkout. Please try again.");
      }
    } catch (e) {
      console.error("Checkout error:", e);
      alert("Could not start checkout. Please try again.");
    } finally {
      setPaywallLoading(null);
    }
  };

  // Expose paywall trigger so chat limit handler (and other non-React contexts) can open it
  useEffect(() => {
    window.__betmanShowPaywall = () => setShowPaywall(true);
    return () => { delete window.__betmanShowPaywall; };
  }, []);

  // When the menu opens and there's a pending email verification (popup was closed earlier),
  // auto-restore the verify view so the user can finish entering the code.
  useEffect(() => {
    if (menuOpen && !user && otpSentTo && authMode === null) {
      setAuthMode("verify");
    }
  }, [menuOpen, user, otpSentTo, authMode]);

  // ─── Stripe Customer Portal (manage / cancel) ───
  const openCustomerPortal = async () => {
    if (!user || !supabase) return;
    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session?.access_token) return;
      const res = await fetch(apiUrl("/api/create-portal-session"), {
        method: "POST",
        headers: { Authorization: `Bearer ${session.access_token}` },
      });
      const data = await res.json();
      if (data.url) window.location.href = data.url;
      else alert(data.error || "Could not open subscription manager.");
    } catch (e) { console.error("Portal error:", e); }
  };

  // Price IDs (LIVE mode) — must match backend .env
  const PRICE_IDS = {
    pro_monthly: "price_1ThA4tAOJr1c0xoPUzMwMfTK",
    pro_yearly: "price_1ThA5FAOJr1c0xoPEnzPj8Gk",
    elite_monthly: "price_1ThA5YAOJr1c0xoP7JVTZ6VH",
    elite_yearly: "price_1ThA5qAOJr1c0xoPAE6DuSPp",
    ultimate_monthly: "price_1ThA69AOJr1c0xoP3mEZMKRj",
    ultimate_yearly: "price_1ThA6JAOJr1c0xoPRyOJr1IR",
  };

  // Handle Stripe checkout redirect params (?subscribed=true | ?cancelled=true)
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    if (params.get("subscribed") === "true") {
      // Clear param and refresh subscription several times (webhook may take a moment)
      window.history.replaceState({}, "", window.location.pathname);
      alert("🎉 Welkom bij BetMan Pro! Je abonnement is actief.");
      // Retry refresh a few times to catch the webhook
      let retries = 5;
      const retry = () => {
        refreshSubscription();
        if (--retries > 0) setTimeout(retry, 1500);
      };
      retry();
    } else if (params.get("cancelled") === "true") {
      window.history.replaceState({}, "", window.location.pathname);
      // No alert — let them try again silently
    }
  }, [user?.id]);

  // Expose globally so other components (callBetManAI) can trigger refresh
  useEffect(() => {
    window.__betmanRefreshUsage = refreshSubscription;
    return () => { delete window.__betmanRefreshUsage; };
  }, [user?.id]);

  // Fetch subscription + usage whenever user changes
  useEffect(() => {
    if (!user || !supabase) { setSubscription(null); return; }
    refreshSubscription();
  }, [user?.id]);

  // Cross-device sync: re-pull the server-side subscription whenever the user
  // returns to the app (tab focus / visibility). Fixes the case where a desktop
  // session that was already open before a mobile purchase keeps showing the old
  // tier — the subscription is account-bound on the server, so just re-fetch it.
  // Throttled to once per 20s to avoid spamming on rapid focus changes.
  useEffect(() => {
    if (!user || !supabase) return;
    let lastSync = Date.now();
    const maybeSync = () => {
      if (document.visibilityState === "hidden") return;
      if (Date.now() - lastSync < 20000) return;
      lastSync = Date.now();
      refreshSubscription();
    };
    window.addEventListener("focus", maybeSync);
    document.addEventListener("visibilitychange", maybeSync);
    return () => {
      window.removeEventListener("focus", maybeSync);
      document.removeEventListener("visibilitychange", maybeSync);
    };
  }, [user?.id]);

  useEffect(() => { chatEnd.current?.scrollIntoView({ behavior: "smooth" }); }, [messages, typing]);

  // Restore session on mount + listen for auth changes
  useEffect(() => {
    if (!supabase) return;
    supabase.auth.getSession().then(({ data: { session } }) => {
      if (session?.user) {
        setUser({
          id: session.user.id,
          email: session.user.email,
          name: session.user.user_metadata?.name || session.user.email.split("@")[0],
          joined: session.user.created_at,
        });
      }
    });
    const { data: sub } = supabase.auth.onAuthStateChange((_event, session) => {
      if (session?.user) {
        setUser({
          id: session.user.id,
          email: session.user.email,
          name: session.user.user_metadata?.name || session.user.email.split("@")[0],
          joined: session.user.created_at,
        });
      } else {
        setUser(null);
      }
    });
    return () => sub.subscription.unsubscribe();
  }, []);

  const handleAuth = async (mode) => {
    setAuthError("");
    // Verify-mode only needs the OTP code, skip email/password checks
    if (mode !== "verify") {
      if (!authEmail || !authPass) { setAuthError("Please fill in all fields"); return; }
      if (mode === "signup" && !authName) { setAuthError("Please enter your name"); return; }
      if (!/\S+@\S+\.\S+/.test(authEmail)) { setAuthError("Please enter a valid email"); return; }
      if (authPass.length < 6) { setAuthError("Password must be at least 6 characters"); return; }
    } else {
      if (!otpCode || otpCode.length < 6) { setAuthError("Please enter the verification code"); return; }
      if (!otpSentTo) { setAuthError("Session expired — please sign up again"); return; }
    }

    if (!supabase) {
      setAuthError("Authentication is not configured. Please check Supabase settings.");
      return;
    }

    setAuthLoading(true);
    try {
      if (mode === "signup") {
        const { data, error } = await supabase.auth.signUp({
          email: authEmail,
          password: authPass,
          options: { data: { name: authName } },
        });
        if (error) { setAuthError(error.message); setAuthLoading(false); return; }
        // Supabase silently returns success for duplicate emails (anti-enumeration).
        // Detect via empty identities array — means the email already has an account.
        const isExistingUser = data?.user && Array.isArray(data.user.identities) && data.user.identities.length === 0;
        if (isExistingUser) {
          // Auto-recover: try to resend the verification code for unverified accounts.
          // If the account is already verified, this will return an error and we ask them to log in.
          const { error: resendErr } = await supabase.auth.resend({ type: "signup", email: authEmail });
          if (resendErr) {
            const msg = resendErr.message?.toLowerCase() || "";
            if (msg.includes("already confirmed") || msg.includes("already registered") || msg.includes("already verified")) {
              setAuthError("This email is already verified. Please log in instead, or use 'Forgot password' if you don't remember it.");
            } else {
              setAuthError(`This email is already in use. ${resendErr.message}`);
            }
            setAuthLoading(false);
            return;
          }
          // Resend returned no error — but Supabase's resend silently succeeds for already-verified
          // accounts too (no email actually sent). So we honestly tell the user both possibilities
          // and offer "Forgot password" as a guaranteed fallback.
          setOtpSentTo(authEmail);
          try { localStorage.setItem("betman_pending_verify", JSON.stringify({ email: authEmail, ts: Date.now() })); } catch {}
          setAuthMode("verify");
          setAuthError("This email is already in use. If your account is unverified, a new code has been sent — check your inbox + spam. If you already verified it, use 'Forgot password' below.");
          setResendCooldown(60);
          const timer = setInterval(() => {
            setResendCooldown(c => { if (c <= 1) { clearInterval(timer); return 0; } return c - 1; });
          }, 1000);
          setAuthLoading(false);
          return;
        }
        if (!data.session) {
          // Email confirmation required — switch to OTP entry screen
          setOtpSentTo(authEmail);
          try { localStorage.setItem("betman_pending_verify", JSON.stringify({ email: authEmail, ts: Date.now() })); } catch {}
          setAuthMode("verify");
          setAuthError("");
          setResendCooldown(60);
          const timer = setInterval(() => {
            setResendCooldown(c => { if (c <= 1) { clearInterval(timer); return 0; } return c - 1; });
          }, 1000);
        } else {
          setAuthMode(null);
        }
      } else if (mode === "verify") {
        const { error } = await supabase.auth.verifyOtp({
          email: otpSentTo,
          token: otpCode,
          type: "signup",
        });
        if (error) { setAuthError(error.message || "Invalid or expired code"); setAuthLoading(false); return; }
        // Success — user is now authenticated
        try { localStorage.removeItem("betman_pending_verify"); } catch {}
        setAuthMode(null);
        setOtpCode("");
        setOtpSentTo("");
        setMenuOpen(false);
      } else {
        const { error } = await supabase.auth.signInWithPassword({
          email: authEmail,
          password: authPass,
        });
        if (error) { setAuthError(error.message); setAuthLoading(false); return; }
        setAuthMode(null);
      }
      setAuthEmail(""); setAuthPass(""); setAuthName("");
    } catch (err) {
      setAuthError(err.message || "Something went wrong");
    }
    setAuthLoading(false);
  };

  const handleLogout = async () => {
    if (supabase) await supabase.auth.signOut();
    setUser(null);
    setAuthMode(null);
  };

  const handlePasswordReset = async () => {
    setAuthError("");
    if (!authEmail) { setAuthError("Please enter your email"); return; }
    if (!/\S+@\S+\.\S+/.test(authEmail)) { setAuthError("Please enter a valid email"); return; }
    if (!supabase) { setAuthError("Auth not configured"); return; }

    setAuthLoading(true);
    try {
      const { error } = await supabase.auth.resetPasswordForEmail(authEmail, {
        redirectTo: window.location.origin + "/reset-password",
      });
      if (error) {
        // Translate common Supabase errors into actionable guidance
        const raw = (error.message || "").toLowerCase();
        if (raw.includes("rate") || raw.includes("for security purposes") || raw.includes("over the rate limit")) {
          setAuthError("Too many recent attempts. Please wait 60 seconds and try again. (Supabase rate-limits password resets per email.)");
        } else if (raw.includes("sending recovery email") || raw.includes("smtp") || raw.includes("email send") || raw.includes("email rate")) {
          setAuthError("Couldn't send recovery email. Possible causes: (1) you've hit the resend cooldown — wait 60s; (2) email already in spam — check there; (3) account doesn't exist with this email.");
        } else if (raw.includes("user not found") || raw.includes("not found")) {
          setAuthError("No account exists with that email. Try creating a new account or check the spelling.");
        } else {
          setAuthError(error.message);
        }
        console.error("[Password reset]", error);
      }
      else {
        setAuthError("");
        alert("✅ Reset link sent! Check your email (and spam folder).");
        setAuthMode("login");
      }
    } catch (err) {
      setAuthError("Something went wrong: " + err.message);
    }
    setAuthLoading(false);
  };

  const handleDeleteAccount = async () => {
    const confirmed = window.confirm(
      "⚠️ DELETE YOUR ACCOUNT?\n\nThis will permanently delete your account and all your data including:\n• Saved betslips\n• Usage history\n• Subscription records\n\nThis cannot be undone. Are you sure?"
    );
    if (!confirmed) return;
    const doubleCheck = window.prompt('Type "DELETE" to confirm:');
    if (doubleCheck !== "DELETE") {
      alert("Account deletion cancelled.");
      return;
    }

    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session?.access_token) { alert("Please log in first"); return; }

      const res = await fetch(apiUrl("/api/delete-account"), {
        method: "POST",
        headers: { Authorization: `Bearer ${session.access_token}` },
      });
      if (res.ok) {
        alert("Your account has been deleted. You'll be logged out now.");
        await supabase.auth.signOut();
        setUser(null);
        setSubscription(null);
        setMenuOpen(false);
      } else {
        const err = await res.json().catch(() => ({}));
        alert("Failed to delete account: " + (err.error || "Unknown error"));
      }
    } catch (err) {
      alert("Error: " + err.message);
    }
  };

  // Cache-first fixtures loader: paints cached matches instantly, refreshes in
  // the background, re-caches the fresh set. Only shows a spinner / error when
  // there's nothing cached to display.
  const loadMatches = async () => {
    const cached = readMatchesCache();
    if (!cached?.data?.length) setMatchesLoading(true);
    setMatchesError(false);
    const data = await fetchLiveMatches();
    if (Array.isArray(data)) {
      if (data.length) { setMatches(data); writeMatchesCache(data); }
      else if (!cached?.data?.length) setMatches([]); // genuinely no matches
    } else if (data === null && !cached?.data?.length) {
      setMatchesError(true); // only error when we have no cache fallback
    }
    setMatchesLoading(false);
    setMatchesFetched(true);
  };

  // Prefetch fixtures on app open (not just when the Matches tab is opened) so
  // the data is already cached and instant by the time the user navigates there.
  useEffect(() => {
    loadMatches();
  }, []);

  // Background refresh when the Matches tab is opened, if the cache is stale.
  useEffect(() => {
    if (screen === "matches" && matchesFetched && !readMatchesCache()?.fresh) {
      loadMatches();
    }
  }, [screen]);

  // Load detailed data when a match is selected (requires login + daily quota)
  const pendingMatchRef = useRef(null); // match tapped while logged out — reopened after login
  const selectMatch = async (match) => {
    // Require login first — remember which match they wanted so login doesn't dead-end them
    // back on the list.
    if (!user) {
      pendingMatchRef.current = match;
      setMenuOpen(true);
      setAuthMode("signup");
      return;
    }
    // Track usage + enforce quota
    if (supabase) {
      try {
        const { data: { session } } = await supabase.auth.getSession();
        if (session?.access_token) {
          const res = await fetch(apiUrl("/api/track-usage"), {
            method: "POST",
            headers: { "Content-Type": "application/json", Authorization: `Bearer ${session.access_token}` },
            body: JSON.stringify({ feature: "match_detail" }),
          });
          if (res.status === 429) {
            const info = await res.json().catch(() => ({}));
            alert(`You've reached your daily limit of ${info.limit} match detail views on the ${info.tier} plan.`);
            // Take them straight to the upgrade options instead of dead-ending on an alert.
            if (window.__betmanShowPaywall) window.__betmanShowPaywall();
            // Refresh usage display
            const meRes = await fetch(apiUrl("/api/me"), { headers: { Authorization: `Bearer ${session.access_token}` } });
            if (meRes.ok) setSubscription(await meRes.json());
            return;
          }
          // Refresh usage display after successful track
          const meRes = await fetch(apiUrl("/api/me"), { headers: { Authorization: `Bearer ${session.access_token}` } });
          if (meRes.ok) setSubscription(await meRes.json());
        }
      } catch (e) { console.error("Usage tracking failed:", e); }
    }

    const cached = detailCacheRef.current[match.id];
    if (cached) {
      setSelectedMatch(cached);
      return;
    }
    setSelectedMatch(match);
    setDetailLoading(true);
    const enriched = await loadMatchDetails(match);
    detailCacheRef.current[match.id] = enriched;
    setSelectedMatch(enriched);
    setDetailLoading(false);
  };

  // After a successful login, open the match the user originally tapped instead of dropping
  // them back on the list to find it again.
  useEffect(() => {
    if (user && pendingMatchRef.current) {
      const m = pendingMatchRef.current;
      pendingMatchRef.current = null;
      setMenuOpen(false);
      selectMatch(m);
    }
  }, [user]);

  const sendChatFeedback = async (rating, botMessage, userQuestion, comment) => {
    try {
      const headers = { "Content-Type": "application/json" };
      if (supabase) {
        const { data: { session } } = await supabase.auth.getSession();
        if (session?.access_token) headers.Authorization = `Bearer ${session.access_token}`;
      }
      await fetch(apiUrl("/api/chat-feedback"), {
        method: "POST",
        headers,
        body: JSON.stringify({ rating, botMessage, userQuestion, comment }),
      });
    } catch (err) {
      console.error("feedback error:", err);
    }
  };

  const send = async (text) => {
    const msg = text || input.trim();
    if (!msg || typing) return;
    const newMessages = [...messages, { text: msg, isUser: true }];
    setMessages(newMessages);
    setInput("");
    // INSTANT auth gate — skip API call when not logged in
    if (!user) {
      setMessages(p => [...p, {
        text: "Please log in or sign up first to chat with BetMan. Tap the ⋯ menu (top right) to get started — it's free!",
        isUser: false,
      }]);
      setMenuOpen(true);
      setAuthMode("signup");
      return;
    }
    setTyping(true);
    // Update the streaming bot bubble (the last non-user message) in place.
    const updateLastBot = (patch) => setMessages(p => {
      const copy = [...p];
      for (let i = copy.length - 1; i >= 0; i--) {
        if (!copy[i].isUser) { copy[i] = { ...copy[i], ...patch }; break; }
      }
      return copy;
    });
    let started = false;
    try {
      const reply = await callBetManAI(msg, messages, (partial) => {
        if (!started) {
          // First token arrived — swap the typing indicator for a live bubble.
          started = true;
          setTyping(false);
          setMessages(p => [...p, { text: partial, isUser: false, streaming: true }]);
        } else {
          updateLastBot({ text: partial });
        }
      });
      if (!started) {
        // No stream (auth/quota/fallback returned a plain string) — show one bubble.
        setMessages(p => [...p, { text: reply, isUser: false }]);
      } else {
        updateLastBot({ text: reply, streaming: false });
      }
    } catch {
      if (started) updateLastBot({ text: "Something went wrong. Please try again.", streaming: false });
      else setMessages(p => [...p, { text: "Something went wrong. Please try again.", isUser: false }]);
    }
    setTyping(false);
  };

  // No em/en dashes anywhere in the chat tab (user preference) - strip them from each suggestion
  // chip's rendered text (keeps the i18n keys untouched while cleaning translated values too).
  const noDash = (s) => (s || "").replace(/\s*[—–]\s*/g, ", ");
  const prompts = [
    { icon: "gem", text: noDash(t("Find mispriced odds for today's matches")) },
    { icon: "zap", text: noDash(t("Who is most likely to score in the next match?")) },
    { icon: "chart", text: noDash(t("Which market has the biggest gap between bookmaker odds and real probability?")) },
    { icon: "ticket", text: noDash(t("Build me a value combo — no guesswork, only data-backed picks")) },
  ];

  return (
    <div style={{
      fontFamily: "var(--ui)",
      background: "#000", color: "#f3f4f6", height: "100vh", display: "flex", flexDirection: "column",
      width: "100%", overflow: "hidden",
    }}>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Syne:wght@400;500;600;700;800&family=Instrument+Serif:ital@1&family=Lekton:wght@400;700&family=Inter:wght@400;500;600;700;800&display=swap');
        /* Shikō-inspired type system: Inter body, Lekton mono labels, Syne display headings,
           Instrument Serif italic accents. --mono now resolves to Lekton (the mono-label look). */
        :root {
          --ui: 'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
          --mono: 'Lekton', ui-monospace, 'SFMono-Regular', Menlo, monospace;
          --display: 'Syne', 'Inter', sans-serif;
          --serif: 'Instrument Serif', Georgia, serif;
        }
        html, body, #root, input, button, textarea { font-feature-settings: 'tnum' 1, 'ss01' 1; font-variant-numeric: tabular-nums; }
        @keyframes msgIn { from { opacity:0; transform:translateY(6px); } to { opacity:1; transform:translateY(0); } }
        @keyframes slideUp { from { opacity:0; transform:translateY(16px); } to { opacity:1; transform:translateY(0); } }
        @keyframes dotPulse { 0%,100% { opacity:.25; } 50% { opacity:1; } }
        @keyframes bob { 0%,100% { transform:translateY(0); } 50% { transform:translateY(-6px); } }
        @keyframes bobSmall { 0%,100% { transform:translateY(0); } 50% { transform:translateY(-2px); } }
        @keyframes scanLine { 0% { width:10%; margin-left:0; } 50% { width:60%; margin-left:20%; } 100% { width:10%; margin-left:90%; } }
        @keyframes slideInRight { from { transform: translateX(100%); } to { transform: translateX(0); } }
        @keyframes softGlow { 0%,100% { opacity:.5; } 50% { opacity:.9; } }
        @keyframes mascotGlow {
          0%,100% { filter: drop-shadow(0 0 8px #4ade8025) drop-shadow(0 0 20px #4ade8015); }
          50% { filter: drop-shadow(0 0 14px #4ade8045) drop-shadow(0 0 35px #4ade8025); }
        }
        * { box-sizing:border-box; margin:0; padding:0; }
        *::-webkit-scrollbar { width:0; }
        input::placeholder { color:#4b5563; }
      `}</style>

      {/* ─── Header ─── */}
      <header style={{
        padding: "14px 20px", display: "flex", alignItems: "center", justifyContent: "space-between",
        borderBottom: "1px solid #101413", background: "#000", zIndex: 10, flexShrink: 0,
      }}>
        <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
          {(screen !== "home" || messages.length > 0) && (
            <button onClick={() => { if (selectedMatch) { setSelectedMatch(null); } else { setScreen("home"); setMessages([]); } }} style={{
              background: "none", border: "none", color: "#9ca3af", cursor: "pointer",
              fontSize: 22, display: "flex", padding: "0 6px 0 0", lineHeight: 1,
            }}>‹</button>
          )}
          <span style={{ fontFamily: "var(--display)", fontSize: 22, fontWeight: 800, letterSpacing: -0.5 }}>
            <span style={{ color: "#3a9a3a" }}>Bet</span><span style={{ color: "#d4a843" }}>Man</span>
          </span>
        </div>
        <button onClick={() => setMenuOpen(true)} style={{
          background: "none", border: "none", cursor: "pointer", padding: 4,
          display: "flex", gap: 5, alignItems: "center",
        }}>
          {[0,1,2].map(i => (
            <div key={i} style={{ width: 5, height: 5, borderRadius: "50%", background: "#6b7280" }} />
          ))}
        </button>
      </header>

      {/* ─── Paywall Modal ─── */}
      {showPaywall && (() => {
        const tiers = [
          {
            id: "free",
            name: "Free",
            tagline: t("Try the app"),
            monthly: 0, yearly: 0,
            features: [
              t("1 AI chat message per day"),
              t("View 1 match per day"),
              { text: t("Access to News page"), excluded: true },
            ],
            cta: t("Start free"), disabled: true,
          },
          {
            id: "pro",
            name: "Pro",
            tagline: t("Great for starters"),
            monthly: 4.99, yearly: 39.99,
            monthlyPriceId: PRICE_IDS.pro_monthly,
            yearlyPriceId: PRICE_IDS.pro_yearly,
            features: [
              t("5 AI chat messages per day"),
              t("Up to 5 matches per day"),
              t("Access to News page"),
            ],
            cta: t("Try Pro"),
          },
          {
            id: "elite",
            name: "Elite",
            tagline: t("Most popular"),
            popular: true,
            monthly: 12.99, yearly: 99.99,
            monthlyPriceId: PRICE_IDS.elite_monthly,
            yearlyPriceId: PRICE_IDS.elite_yearly,
            features: [
              t("15 AI chat messages per day"),
              t("Up to 15 matches per day"),
              t("Access to News page"),
            ],
            cta: t("Go Elite"),
          },
          {
            id: "ultimate",
            name: "Ultimate",
            tagline: t("For serious bettors"),
            monthly: 29.99, yearly: 229.99,
            monthlyPriceId: PRICE_IDS.ultimate_monthly,
            yearlyPriceId: PRICE_IDS.ultimate_yearly,
            features: [
              t("40 AI chat messages per day"),
              t("Up to 40 matches per day"),
              t("Access to News page"),
            ],
            cta: t("Go Ultimate"),
          },
        ];
        const fmtPrice = (n) => n === 0 ? "€0" : `€${n.toFixed(2).replace(".", ",")}`;
        return (
          <div style={{
            position: "fixed", inset: 0, zIndex: 200, background: "#000d",
            display: "flex", alignItems: "center", justifyContent: "center", padding: 20,
            overflowY: "auto",
          }} onClick={(e) => { if (e.target === e.currentTarget) setShowPaywall(false); }}>
            <div style={{
              background: "#060608", border: "1px solid #262626", borderRadius: 4,
              padding: "30px 24px", width: "100%", maxWidth: 1180, position: "relative",
              maxHeight: "92vh", overflowY: "auto",
            }}>
              {/* Close button */}
              <button onClick={() => setShowPaywall(false)} style={{
                position: "absolute", top: 16, right: 16, background: "none", border: "none",
                color: "#6b7280", fontSize: 24, cursor: "pointer", lineHeight: 1, padding: 4,
              }}>×</button>

              {/* Heading */}
              <div style={{ textAlign: "center", marginBottom: 24 }}>
                <h2 style={{ fontSize: 24, fontWeight: 700, color: "#e5e7eb", margin: 0, marginBottom: 6 }}>
                  {t("Choose your plan")}
                </h2>
                <p style={{ fontSize: 13, color: "#6b7280", margin: 0 }}>
                  {t("Cancel anytime · Secure payment via Stripe")}
                </p>
              </div>

              {/* Active-subscription reminder — stop the current plan before buying a new one,
                  so nobody ends up paying for two subscriptions at once. */}
              {subscription?.tier && subscription.tier !== "free" && (
                <div style={{
                  display: "flex", flexWrap: "wrap", alignItems: "center", gap: 10,
                  background: "#fbbf240d", border: "1px solid #fbbf2440", borderRadius: 4,
                  padding: "10px 14px", margin: "0 auto 20px", maxWidth: 760,
                }}>
                  <div style={{ fontSize: 12, color: "#fbbf24", lineHeight: 1.5, flex: "1 1 320px" }}>
                    <span style={{ fontWeight: 700 }}>
                      {t("Current plan")}: {subscription.tier.charAt(0).toUpperCase() + subscription.tier.slice(1)}.
                    </span>{" "}
                    {t("To avoid double charges, cancel or change your current plan before purchasing a new one.")}
                  </div>
                  <button onClick={openCustomerPortal} style={{
                    padding: "8px 12px", borderRadius: 4, background: "#fbbf24", border: "none",
                    color: "#000", fontSize: 11, fontWeight: 700, cursor: "pointer",
                    fontFamily: "var(--mono)", whiteSpace: "nowrap",
                  }}>
                    {t("Manage Subscription")}
                  </button>
                </div>
              )}

              {/* Billing toggle */}
              <div style={{
                display: "flex", justifyContent: "center", gap: 4, marginBottom: 28,
                background: "#101413", padding: 4, borderRadius: 4, maxWidth: 280, margin: "0 auto 28px",
              }}>
                {["monthly", "yearly"].map(p => (
                  <button key={p} onClick={() => setBillingPeriod(p)} style={{
                    flex: 1, padding: "8px 14px", borderRadius: 4, fontSize: 12, fontWeight: 700,
                    background: billingPeriod === p ? "#262626" : "transparent",
                    border: "none", color: billingPeriod === p ? "#4ade80" : "#6b7280",
                    cursor: "pointer", fontFamily: "var(--mono)",
                  }}>
                    {p === "monthly" ? t("Monthly") : t("Yearly")}
                    {p === "yearly" && <span style={{
                      marginLeft: 6, fontSize: 9, background: "#4ade80", color: "#000",
                      padding: "2px 5px", borderRadius: 4, fontWeight: 700,
                    }}>-36%</span>}
                  </button>
                ))}
              </div>

              {/* Tier cards */}
              <div style={{
                display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(240px, 1fr))",
                gap: 16,
              }}>
                {tiers.map(tr => {
                  const price = billingPeriod === "yearly" ? tr.yearly : tr.monthly;
                  const priceId = billingPeriod === "yearly" ? tr.yearlyPriceId : tr.monthlyPriceId;
                  const isCurrentTier = subscription?.tier === tr.id;
                  return (
                    <div key={tr.id} style={{
                      background: tr.popular ? "linear-gradient(180deg, #1a1a1e, #060608)" : "#101413",
                      border: `1px solid ${tr.popular ? "#3b82f6" : "#262626"}`,
                      borderRadius: 4, padding: 24, position: "relative",
                    }}>
                      {tr.popular && (
                        <div style={{
                          position: "absolute", top: -10, left: 16,
                          background: "#3b82f6", color: "#fff", padding: "3px 10px",
                          borderRadius: 6, fontSize: 10, fontWeight: 700, fontFamily: "var(--mono)",
                        }}>{tr.tagline.toUpperCase()}</div>
                      )}
                      <h3 style={{ fontSize: 18, fontWeight: 700, color: "#e5e7eb", margin: "0 0 4px" }}>{tr.name}</h3>
                      <div style={{ display: "flex", alignItems: "baseline", gap: 6, marginBottom: 6 }}>
                        <span style={{ fontSize: 32, fontWeight: 700, color: "#fff" }}>{fmtPrice(price)}</span>
                        <span style={{ fontSize: 13, color: "#6b7280" }}>/ {billingPeriod === "yearly" ? t("year") : t("month")}</span>
                      </div>
                      <p style={{ fontSize: 11, color: "#6b7280", margin: "0 0 16px", minHeight: 16 }}>
                        {tr.monthly > 0 && (() => {
                          const savePct = Math.round((1 - tr.yearly / (tr.monthly * 12)) * 100);
                          return billingPeriod === "yearly"
                            ? t("or {price}/month — save {pct}%", { price: fmtPrice(tr.monthly), pct: savePct })
                            : t("or {price}/year — save {pct}%", { price: fmtPrice(tr.yearly), pct: savePct });
                        })()}
                        {tr.monthly === 0 ? tr.tagline : null}
                      </p>
                      <div style={{ borderTop: "1px solid #26262670", paddingTop: 14, marginBottom: 18 }}>
                        {tr.features.map((f, i) => {
                          const text = typeof f === "string" ? f : f.text;
                          const excluded = typeof f === "object" && f.excluded;
                          return (
                            <div key={i} style={{
                              display: "flex", alignItems: "flex-start", gap: 8,
                              fontSize: 13, color: excluded ? "#4b5563" : "#d1d5db",
                              textDecoration: excluded ? "line-through" : "none",
                              marginBottom: 8,
                            }}>
                              <span style={{ color: excluded ? "#4b5563" : "#4ade80", fontWeight: 700 }}>
                                {excluded ? "✕" : "✓"}
                              </span>
                              <span>{text}</span>
                            </div>
                          );
                        })}
                      </div>
                      <button
                        onClick={() => {
                          if (tr.id === "free" || isCurrentTier || !priceId) return;
                          openCheckout(priceId, false);
                        }}
                        disabled={tr.disabled || isCurrentTier || paywallLoading === priceId}
                        style={{
                          width: "100%", padding: "12px 14px", borderRadius: 4,
                          background: isCurrentTier
                            ? "#262626"
                            : (tr.popular ? "#3b82f6" : "#262626"),
                          border: tr.popular ? "none" : "1px solid #2c2c30",
                          color: isCurrentTier ? "#9ca3af" : (tr.popular ? "#fff" : "#e5e7eb"),
                          fontSize: 13, fontWeight: 700, fontFamily: "var(--mono)",
                          cursor: (tr.disabled || isCurrentTier) ? "default" : "pointer",
                          opacity: paywallLoading === priceId ? 0.5 : 1,
                        }}>
                        {paywallLoading === priceId ? t("Loading...") : (isCurrentTier ? t("Your current plan") : tr.cta)}
                      </button>
                    </div>
                  );
                })}
              </div>

              <p style={{
                textAlign: "center", marginTop: 20, fontSize: 11, color: "#6b7280",
                fontFamily: "var(--mono)",
              }}>
                {t("Cancel anytime · Secure payment via Stripe")}
              </p>
            </div>
          </div>
        );
      })()}

      {/* ─── Menu / Account Panel ─── */}
      {menuOpen && (
        <div style={{
          position: "fixed", top: 0, left: 0, right: 0, bottom: 0, zIndex: 100,
          display: "flex", justifyContent: "flex-end",
        }}>
          {/* Backdrop */}
          <div onClick={() => { setMenuOpen(false); setAuthMode(null); setAuthError(""); }}
            style={{ position: "absolute", inset: 0, background: "#000c" }} />

          {/* Panel */}
          <div style={{
            position: "relative", width: "85%", maxWidth: 380, background: "#060608",
            borderLeft: "1px solid #262626", display: "flex", flexDirection: "column",
            animation: "slideInRight 0.25s ease",
            overflow: "auto",
          }}>
            {/* Panel header */}
            <div style={{
              padding: "16px 20px", borderBottom: "1px solid #262626",
              display: "flex", justifyContent: "space-between", alignItems: "center",
            }}>
              <span style={{ fontSize: 16, fontWeight: 700, color: "#e5e7eb" }}>
                {authMode === "login" ? t("Log In") :
                 authMode === "signup" ? t("Create Account") :
                 authMode === "reset" ? t("Reset Password") :
                 authMode === "verify" ? t("Verify Email") :
                 t("Menu")}
              </span>
              <button onClick={() => { setMenuOpen(false); setAuthMode(null); setAuthError(""); }} style={{
                background: "none", border: "none", color: "#6b7280", fontSize: 22,
                cursor: "pointer", lineHeight: 1,
              }}>✕</button>
            </div>

            <div style={{ flex: 1, overflow: "auto", padding: 20 }}>
              {/* ── Auth Forms ── */}
              {authMode === "verify" ? (
                <div>
                  <button onClick={() => { setAuthMode("signup"); setAuthError(""); setOtpCode(""); }} style={{
                    background: "none", border: "none", color: "#4ade80", fontSize: 12,
                    cursor: "pointer", fontFamily: "var(--mono)", marginBottom: 16, padding: 0,
                  }}>← {t("Back")}</button>

                  <div style={{ fontSize: 13, color: "#e5e7eb", marginBottom: 8, fontWeight: 600 }}>
                    {t("Check your email")}
                  </div>
                  <div style={{ fontSize: 12, color: "#9ca3af", marginBottom: 20, lineHeight: 1.5 }}>
                    {t("We sent an 8-digit code to")} <span style={{ color: "#4ade80" }}>{otpSentTo}</span>. {t("Enter it below to verify your account.")}
                  </div>

                  <div style={{ marginBottom: 16 }}>
                    <label style={{ fontSize: 11, color: "#6b7280", fontFamily: "var(--mono)", display: "block", marginBottom: 6 }}>
                      {t("Verification Code")}
                    </label>
                    <input
                      autoFocus
                      inputMode="numeric"
                      maxLength={10}
                      value={otpCode}
                      onChange={e => setOtpCode(e.target.value.replace(/\D/g, "").slice(0, 10))}
                      placeholder={t("Enter code")}
                      style={{
                        width: "100%", padding: "14px 16px", borderRadius: 4,
                        fontSize: 22, letterSpacing: 4, textAlign: "center",
                        background: "#101413", border: "1px solid #262626", color: "#e5e7eb",
                        outline: "none", fontFamily: "var(--mono)", fontWeight: 700,
                      }}
                    />
                  </div>

                  {authError && (
                    <div style={{
                      background: "#ef444418", border: "1px solid #ef444430", borderRadius: 4,
                      padding: 10, marginBottom: 12, fontSize: 12, color: "#f87171", textAlign: "center",
                    }}>{authError}</div>
                  )}

                  <button onClick={() => handleAuth("verify")} disabled={authLoading || otpCode.length < 6} style={{
                    width: "100%", padding: 14, borderRadius: 4,
                    background: (authLoading || otpCode.length < 6) ? "#4ade8050" : "#22c55e",
                    border: "none", color: "#000", fontSize: 14, fontWeight: 700,
                    cursor: (authLoading || otpCode.length < 6) ? "not-allowed" : "pointer", fontFamily: "var(--mono)", marginBottom: 12,
                  }}>
                    {authLoading ? t("Verifying...") : t("Verify & Continue")}
                  </button>

                  <div style={{ textAlign: "center", fontSize: 12, color: "#6b7280" }}>
                    {t("Didn't receive a code?")}{" "}
                    {resendCooldown > 0 ? (
                      <span style={{ color: "#4b5563" }}>{t("Resend in {n}s", { n: resendCooldown })}</span>
                    ) : (
                      <button onClick={async () => {
                        setAuthError("");
                        const { error } = await supabase.auth.resend({ type: "signup", email: otpSentTo });
                        if (error) { setAuthError(error.message); return; }
                        setResendCooldown(60);
                        const timer = setInterval(() => {
                          setResendCooldown(c => { if (c <= 1) { clearInterval(timer); return 0; } return c - 1; });
                        }, 1000);
                      }} style={{ background: "none", border: "none", color: "#4ade80", cursor: "pointer", fontSize: 12, textDecoration: "underline" }}>
                        {t("Resend code")}
                      </button>
                    )}
                  </div>

                  {/* Already-verified guidance — prominent so returning users with an existing
                      account don't sit waiting for a code that will never arrive. */}
                  <div style={{
                    marginTop: 16, padding: "12px 14px", borderRadius: 4,
                    background: "#4ade8010", border: "1px solid #4ade8030",
                  }}>
                    <div style={{ fontSize: 12, color: "#cbd5e1", lineHeight: 1.5, marginBottom: 10 }}>
                      {t("Already verified this email?")} {t("You won't get a code — just log in instead.")}
                    </div>
                    <div style={{ display: "flex", gap: 8 }}>
                      <button onClick={() => {
                        setAuthEmail(otpSentTo); setAuthError(""); setOtpCode(""); setAuthMode("login");
                      }} style={{
                        flex: 1, padding: "10px 12px", borderRadius: 4, background: "#22c55e",
                        border: "none", color: "#000", fontSize: 13, fontWeight: 700, cursor: "pointer",
                        fontFamily: "var(--mono)",
                      }}>
                        {t("Log In")}
                      </button>
                      <button onClick={() => {
                        setAuthEmail(otpSentTo); setAuthError(""); setOtpCode(""); setAuthMode("reset");
                      }} style={{
                        flex: 1, padding: "10px 12px", borderRadius: 4, background: "transparent",
                        border: "1px solid #4ade8040", color: "#4ade80", fontSize: 13, fontWeight: 600,
                        cursor: "pointer", fontFamily: "var(--mono)",
                      }}>
                        {t("Reset password instead")}
                      </button>
                    </div>
                  </div>
                </div>
              ) : authMode ? (
                <div>
                  <button onClick={() => { setAuthMode(null); setAuthError(""); }} style={{
                    background: "none", border: "none", color: "#4ade80", fontSize: 12,
                    cursor: "pointer", fontFamily: "var(--mono)", marginBottom: 16, padding: 0,
                  }}>← {t("Back to menu")}</button>

                  {authMode === "reset" && (
                    <div style={{ fontSize: 12, color: "#9ca3af", marginBottom: 16, lineHeight: 1.5 }}>
                      {t("Enter your email and we'll send you a link to reset your password.")}
                    </div>
                  )}

                  {authMode === "signup" && (
                    <div style={{ marginBottom: 12 }}>
                      <label style={{ fontSize: 11, color: "#6b7280", fontFamily: "var(--mono)", display: "block", marginBottom: 4 }}>{t("Name")}</label>
                      <input value={authName} onChange={e => setAuthName(e.target.value)} placeholder={t("Your name")}
                        style={{
                          width: "100%", padding: "12px 14px", borderRadius: 4, fontSize: 14,
                          background: "#101413", border: "1px solid #262626", color: "#e5e7eb",
                          outline: "none", fontFamily: "inherit",
                        }} />
                    </div>
                  )}

                  <div style={{ marginBottom: 12 }}>
                    <label style={{ fontSize: 11, color: "#6b7280", fontFamily: "var(--mono)", display: "block", marginBottom: 4 }}>{t("Email")}</label>
                    <input type="email" value={authEmail} onChange={e => setAuthEmail(e.target.value)} placeholder="you@email.com"
                      style={{
                        width: "100%", padding: "12px 14px", borderRadius: 4, fontSize: 14,
                        background: "#101413", border: "1px solid #262626", color: "#e5e7eb",
                        outline: "none", fontFamily: "inherit",
                      }} />
                  </div>

                  {authMode !== "reset" && (
                    <div style={{ marginBottom: 16 }}>
                      <label style={{ fontSize: 11, color: "#6b7280", fontFamily: "var(--mono)", display: "block", marginBottom: 4 }}>{t("Password")}</label>
                      <input type="password" value={authPass} onChange={e => setAuthPass(e.target.value)} placeholder={t("Min 6 characters")}
                        style={{
                          width: "100%", padding: "12px 14px", borderRadius: 4, fontSize: 14,
                          background: "#101413", border: "1px solid #262626", color: "#e5e7eb",
                          outline: "none", fontFamily: "inherit",
                        }} />
                    </div>
                  )}

                  {authError && (
                    <div style={{
                      background: "#ef444418", border: "1px solid #ef444430", borderRadius: 4,
                      padding: 10, marginBottom: 12, fontSize: 12, color: "#f87171", textAlign: "center",
                    }}>{authError}</div>
                  )}

                  <button onClick={() => authMode === "reset" ? handlePasswordReset() : handleAuth(authMode)} disabled={authLoading} style={{
                    width: "100%", padding: 14, borderRadius: 4,
                    background: authLoading ? "#4ade8050" : "#22c55e",
                    border: "none", color: "#000", fontSize: 14, fontWeight: 700,
                    cursor: authLoading ? "not-allowed" : "pointer", fontFamily: "var(--mono)", marginBottom: 12,
                  }}>
                    {authLoading ? t("Please wait...") : (
                      authMode === "login" ? t("Log In") :
                      authMode === "signup" ? t("Create Account") :
                      t("Send Reset Link")
                    )}
                  </button>

                  {authMode === "login" && (
                    <div style={{ textAlign: "center", marginBottom: 10, display: "flex", justifyContent: "center", gap: 12 }}>
                      <button onClick={() => { setAuthMode("reset"); setAuthError(""); }}
                        style={{ background: "none", border: "none", color: "#6b7280", cursor: "pointer", fontSize: 11, fontFamily: "var(--mono)", textDecoration: "underline" }}>
                        {t("Forgot password?")}
                      </button>
                      {otpSentTo && (
                        <button onClick={() => { setAuthMode("verify"); setAuthError(""); }}
                          style={{ background: "none", border: "none", color: "#4ade80", cursor: "pointer", fontSize: 11, fontFamily: "var(--mono)", textDecoration: "underline" }}>
                          {t("Enter verification code")}
                        </button>
                      )}
                    </div>
                  )}

                  <div style={{ textAlign: "center", fontSize: 12, color: "#6b7280" }}>
                    {authMode === "reset" ? (
                      <button onClick={() => { setAuthMode("login"); setAuthError(""); }}
                        style={{ background: "none", border: "none", color: "#4ade80", cursor: "pointer", fontSize: 12 }}>
                        ← {t("Back to login")}
                      </button>
                    ) : (
                      <>
                        {authMode === "login" ? t("Don't have an account? ") : t("Already have an account? ")}
                        <button onClick={() => { setAuthMode(authMode === "login" ? "signup" : "login"); setAuthError(""); }}
                          style={{ background: "none", border: "none", color: "#4ade80", cursor: "pointer", fontSize: 12 }}>
                          {authMode === "login" ? t("Sign up") : t("Log in")}
                        </button>
                      </>
                    )}
                  </div>
                </div>
              ) : (
                <>
                  {/* ── Account Section ── */}
                  <div style={{ marginBottom: 24 }}>
                    <div style={{ fontSize: 10, color: "#4b5563", fontFamily: "var(--mono)", textTransform: "uppercase", letterSpacing: 0.4, marginBottom: 12 }}>
                      {t("Account")}
                    </div>

                    {user ? (
                      <div style={{
                        background: "#101413", border: "1px solid #262626", borderRadius: 4, padding: 16,
                      }}>
                        <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 12 }}>
                          <div style={{
                            width: 42, height: 42, borderRadius: "50%",
                            background: "#22c55e",
                            display: "flex", alignItems: "center", justifyContent: "center",
                            fontSize: 18, fontWeight: 700, color: "#000",
                          }}>{user.name?.[0]?.toUpperCase() || "?"}</div>
                          <div>
                            <div style={{ fontSize: 15, fontWeight: 700, color: "#e5e7eb" }}>{user.name}</div>
                            <div style={{ fontSize: 11, color: "#6b7280" }}>{user.email}</div>
                          </div>
                        </div>
                        {(() => {
                          const tier = subscription?.tier || "free";
                          const tierLabel = { free: t("Free Plan"), pro: t("Pro Plan"), elite: t("Elite Plan"), ultimate: t("Ultimate Plan") }[tier] || t("Free Plan");
                          const tierColor = { free: "#6b7280", pro: "#4ade80", elite: "#3b82f6", ultimate: "#fbbf24" }[tier] || "#6b7280";
                          const u = subscription?.usage || {};
                          // Default tier limits (matches server.js TIER_LIMITS)
                          const defaultLimits = { free: { chat: 1, match_detail: 1 }, pro: { chat: 5, match_detail: 5 }, elite: { chat: 15, match_detail: 15 }, ultimate: { chat: 40, match_detail: 40 } }[tier] || { chat: 1, match_detail: 1 };
                          const fmt = (x, key) => {
                            const used = x?.used ?? 0;
                            const limit = x?.limit ?? defaultLimits[key];
                            return limit === -1 ? t("Unlimited") : `${used}/${limit}`;
                          };
                          return (
                            <>
                              <div style={{
                                display: "flex", justifyContent: "space-between", alignItems: "center",
                                padding: "8px 0", borderTop: "1px solid #26262650",
                              }}>
                                <span style={{ fontSize: 11, color: tierColor, fontFamily: "var(--mono)", fontWeight: 700 }}>{tierLabel}</span>
                                <span style={{
                                  padding: "3px 8px", borderRadius: 6, fontSize: 9, fontWeight: 700,
                                  background: `${tierColor}12`, color: tierColor, border: `1px solid ${tierColor}25`,
                                  fontFamily: "var(--mono)",
                                }}>{t("ACTIVE")}</span>
                              </div>
                              <div style={{ padding: "8px 0", borderTop: "1px solid #26262650", fontFamily: "var(--mono)" }}>
                                <div style={{ fontSize: 10, color: "#4b5563", marginBottom: 6 }}>{t("DAILY USAGE")}</div>
                                <div style={{ display: "flex", justifyContent: "space-between", fontSize: 11, color: "#9ca3af", marginBottom: 4 }}>
                                  <span>{t("Chat")}</span><span style={{ color: "#e5e7eb" }}>{fmt(u.chat, "chat")}</span>
                                </div>
                                <div style={{ display: "flex", justifyContent: "space-between", fontSize: 11, color: "#9ca3af" }}>
                                  <span>{t("Match Details")}</span><span style={{ color: "#e5e7eb" }}>{fmt(u.match_detail, "match_detail")}</span>
                                </div>
                              </div>
                              {tier === "free" && (
                                <button style={{
                                  width: "100%", padding: 10, borderRadius: 4, marginTop: 8,
                                  background: "#f59e0b",
                                  border: "none", color: "#000", fontSize: 12, fontWeight: 700,
                                  cursor: "pointer", fontFamily: "var(--mono)",
                                  display: "inline-flex", alignItems: "center", justifyContent: "center", gap: 6,
                                }} onClick={() => { setShowPaywall(true); setMenuOpen(false); }}>
                                  <Icon name="zap" size={13} color="#000" /> {t("Upgrade subscription")}
                                </button>
                              )}
                              {tier === "pro" && (
                                <button style={{
                                  width: "100%", padding: 10, borderRadius: 4, marginTop: 8,
                                  background: "#7c3aed",
                                  border: "none", color: "#fff", fontSize: 12, fontWeight: 700,
                                  cursor: "pointer", fontFamily: "var(--mono)",
                                  display: "inline-flex", alignItems: "center", justifyContent: "center", gap: 6,
                                }} onClick={() => { setShowPaywall(true); setMenuOpen(false); }}>
                                  <Icon name="arrow-up" size={13} color="#fff" /> {t("Upgrade subscription")}
                                </button>
                              )}
                              {tier === "elite" && (
                                <button style={{
                                  width: "100%", padding: 10, borderRadius: 4, marginTop: 8,
                                  background: "#fbbf24",
                                  border: "none", color: "#000", fontSize: 12, fontWeight: 700,
                                  cursor: "pointer", fontFamily: "var(--mono)",
                                  display: "inline-flex", alignItems: "center", justifyContent: "center", gap: 6,
                                }} onClick={() => { setShowPaywall(true); setMenuOpen(false); }}>
                                  <Icon name="arrow-up" size={13} color="#000" /> {t("Upgrade subscription")}
                                </button>
                              )}
                              {tier === "ultimate" && (
                                // Top tier has nothing to upgrade to, but must still be able to
                                // browse the plans (e.g. to downgrade).
                                <button style={{
                                  width: "100%", padding: 10, borderRadius: 4, marginTop: 8,
                                  background: "#4ade80",
                                  border: "none", color: "#000", fontSize: 12, fontWeight: 700,
                                  cursor: "pointer", fontFamily: "var(--mono)",
                                  display: "inline-flex", alignItems: "center", justifyContent: "center", gap: 6,
                                }} onClick={() => { setShowPaywall(true); setMenuOpen(false); }}>
                                  <Icon name="ticket" size={13} color="#000" /> {t("View all plans")}
                                </button>
                              )}
                              {tier !== "free" && (
                                <button style={{
                                  width: "100%", padding: 10, borderRadius: 4, marginTop: 8,
                                  background: "#262626", border: "1px solid #2c2c30",
                                  color: "#e5e7eb", fontSize: 12, fontWeight: 700,
                                  cursor: "pointer", fontFamily: "var(--mono)",
                                  display: "inline-flex", alignItems: "center", justifyContent: "center", gap: 6,
                                }} onClick={openCustomerPortal}>
                                  <Icon name="card" size={13} /> {t("Manage Subscription")}
                                </button>
                              )}
                            </>
                          );
                        })()}
                        <button onClick={handleLogout} style={{
                          width: "100%", padding: 10, borderRadius: 4, marginTop: 8,
                          background: "none", border: "1px solid #262626",
                          color: "#6b7280", fontSize: 12, cursor: "pointer", fontFamily: "var(--mono)",
                        }}>{t("Log Out")}</button>
                      </div>
                    ) : (
                      <div style={{
                        background: "#101413", border: "1px solid #262626", borderRadius: 4, padding: 20,
                        textAlign: "center",
                      }}>
                        <div style={{ display: "flex", justifyContent: "center", marginBottom: 8, color: "#4b5563" }}><Icon name="user" size={30} strokeWidth={1.4} /></div>
                        <div style={{ fontSize: 14, color: "#e5e7eb", fontWeight: 700, marginBottom: 4 }}>{t("Join BetMan")}</div>
                        <div style={{ fontSize: 11, color: "#6b7280", marginBottom: 16 }}>{t("Sign up to save your preferences and betslips")}</div>
                        <button onClick={() => setAuthMode("signup")} style={{
                          width: "100%", padding: 12, borderRadius: 4, marginBottom: 8,
                          background: "#22c55e",
                          border: "none", color: "#000", fontSize: 13, fontWeight: 700,
                          cursor: "pointer", fontFamily: "var(--mono)",
                        }}>{t("Create Account")}</button>
                        <button onClick={() => setAuthMode("login")} style={{
                          width: "100%", padding: 12, borderRadius: 4,
                          background: "none", border: "1px solid #262626",
                          color: "#e5e7eb", fontSize: 13, fontWeight: 600,
                          cursor: "pointer", fontFamily: "var(--mono)",
                        }}>{t("Log In")}</button>
                      </div>
                    )}
                  </div>

                  {/* ── Language Section ── */}
                  <div style={{ marginBottom: 24 }}>
                    <div style={{ fontSize: 10, color: "#4b5563", fontFamily: "var(--mono)", textTransform: "uppercase", letterSpacing: 0.4, marginBottom: 12 }}>
                      {t("Language")}
                    </div>
                    <div style={{
                      background: "#101413", border: "1px solid #262626", borderRadius: 4, padding: 10,
                      display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8,
                    }}>
                      {LANGUAGES.map(l => {
                        const active = getLang() === l.code;
                        return (
                          <button key={l.code} onClick={() => setLang(l.code)} style={{
                            display: "flex", alignItems: "center", gap: 8, padding: "10px 12px",
                            borderRadius: 4, cursor: "pointer", textAlign: "left",
                            background: active ? "#4ade8015" : "#060608",
                            border: active ? "1px solid #4ade8060" : "1px solid #262626",
                            color: active ? "#4ade80" : "#9ca3af",
                            fontSize: 12, fontWeight: active ? 700 : 500, fontFamily: "var(--mono)",
                          }}>
                            <span style={{ fontSize: 16 }}>{l.flag}</span>
                            <span style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{l.native}</span>
                          </button>
                        );
                      })}
                    </div>
                  </div>

                  {/* ── App Info ── */}
                  <div style={{
                    textAlign: "center", padding: "12px 0", borderTop: "1px solid #262626",
                  }}>
                    <span style={{ fontSize: 14, fontWeight: 700 }}>
                      <span style={{ color: "#3a9a3a" }}>Bet</span><span style={{ color: "#d4a843" }}>Man</span>
                    </span>
                    <div style={{ fontSize: 10, color: "#2c2c30", fontFamily: "var(--mono)", marginTop: 4 }}>v1.0.0 • {t("Data-driven betting")}</div>
                    <div style={{ marginTop: 10, display: "flex", justifyContent: "center", gap: 14, fontSize: 10, color: "#6b7280", fontFamily: "var(--mono)" }}>
                      <a href="/terms.html" target="_blank" rel="noopener noreferrer" style={{ color: "#6b7280", textDecoration: "none" }}>{t("Terms")}</a>
                      <span style={{ color: "#262626" }}>·</span>
                      <a href="/privacy.html" target="_blank" rel="noopener noreferrer" style={{ color: "#6b7280", textDecoration: "none" }}>{t("Privacy")}</a>
                      <span style={{ color: "#262626" }}>·</span>
                      <a href="/refund.html" target="_blank" rel="noopener noreferrer" style={{ color: "#6b7280", textDecoration: "none" }}>{t("Refunds")}</a>
                    </div>
                  </div>
                </>
              )}
            </div>
          </div>
        </div>
      )}

      {/* ─── Content ─── */}
      <main style={{ flex: 1, overflow: "auto", display: "flex", flexDirection: "column" }}>

        {/* ═══ HOME / CHAT ═══ */}
        {screen === "home" && (
          <div style={{ flex: 1, minHeight: 0, display: "flex", flexDirection: "column" }}>
            {messages.length === 0 ? (
              <div style={{ flex: 1, minHeight: 0, display: "flex", flexDirection: "column", padding: "16px 20px 10px", overflow: "auto" }}>
                {/* Mascot — vertically centered between top bar and greeting; grows when there's room */}
                {(() => {
                  const mascot = vh < 680 ? 100 : vh < 820 ? 140 : vh < 960 ? 180 : 220;
                  return (
                    <div style={{ flex: 1, minHeight: mascot, display: "flex", alignItems: "center", justifyContent: "center" }}>
                      <div style={{
                        width: mascot, height: mascot, position: "relative",
                        display: "flex", alignItems: "center", justifyContent: "center",
                      }}>
                        <img src={MASCOT_LARGE} alt="BetMan" style={{
                          width: mascot - 10, height: mascot - 10,
                          objectFit: "contain", position: "relative", zIndex: 1,
                          animation: "bob 3s ease-in-out infinite, mascotGlow 3s ease-in-out infinite",
                        }} />
                      </div>
                    </div>
                  );
                })()}

                {/* Greeting as a display heading — the old bordered pill read as a second,
                    disabled input bar sitting above the real one. */}
                <div style={{
                  marginBottom: 14, flexShrink: 0,
                  animation: "slideUp 0.4s ease 0.15s both",
                }}>
                  <div style={{
                    fontSize: 22, color: "#f5f7f6", fontWeight: 700,
                    fontFamily: "var(--display)", letterSpacing: -0.3,
                  }}>
                    {t("How can I help you today?")}
                  </div>
                </div>

                {/* Prompt suggestions */}
                <div style={{ display: "grid", gap: vw < 640 ? 10 : 7, flexShrink: 0 }}>
                  {prompts.map((p, i) => (
                    <button key={i} onClick={() => send(p.text)} style={{
                      display: "flex", alignItems: "center", gap: vw < 640 ? 14 : 12,
                      padding: vw < 640 ? "14px 16px" : "10px 14px", borderRadius: 4,
                      background: "#0c0f0e", border: "1px solid #333333",
                      color: "#f5f7f6", fontSize: vw < 640 ? 15 : 13.5, fontWeight: 500,
                      cursor: "pointer", textAlign: "left", width: "100%",
                      transition: "all 0.15s",
                      animation: `slideUp 0.35s ease ${i * 0.07}s both`,
                    }}>
                      <span style={{
                        width: vw < 640 ? 36 : 30, height: vw < 640 ? 36 : 30, borderRadius: 4, flexShrink: 0,
                        background: "#262626", display: "flex", alignItems: "center",
                        justifyContent: "center", color: "#4ade80",
                      }}><Icon name={p.icon} size={vw < 640 ? 19 : 16} /></span>
                      {p.text}
                    </button>
                  ))}
                </div>
              </div>
            ) : (
              <div style={{ flex: 1, minHeight: 0, overflow: "auto", padding: "16px 14px 8px" }}>
                {messages.map((m, i) => (
                  <ChatBubble
                    key={i}
                    msg={m.text}
                    isUser={m.isUser}
                    userQuestion={!m.isUser && messages[i - 1]?.isUser ? messages[i - 1].text : ""}
                    onFeedback={!m.isUser ? sendChatFeedback : undefined}
                  />
                ))}
                {typing && (() => {
                  const q = ([...messages].reverse().find(m => m.isUser)?.text || "").toLowerCase();
                  const label =
                    /betslip|gokbrief|coupon|schedina|boleto|wettschein|boletim|cupom/.test(q) ? t("Generating betslip")
                    : /value|waarde|valore|valor|mispric|combo|combinat|combinad|biggest gap|grootste verschil|grand écart|mayor diferencia|divario|maior diferença/.test(q) ? t("Finding value bets")
                    : /scor|doelpunt|\bgoal|marca|\btor\b|gol/.test(q) ? t("Analyzing players")
                    : /match|wedstrijd|weekend|spiel|partido|partit|jogo|fixture/.test(q) ? t("Finding matches")
                    : t("Generating answer");
                  return (
                    <div style={{ display: "flex", alignItems: "center", gap: 8, padding: "6px 0 6px 42px" }}>
                      <div style={{ display: "flex", gap: 6 }}>
                        {[0,1,2].map(i => (
                          <div key={i} style={{
                            width: 7, height: 7, borderRadius: "50%", background: "#4ade80",
                            animation: `dotPulse 1s ease ${i * 0.15}s infinite`,
                          }} />
                        ))}
                      </div>
                      <span style={{ fontSize: 12, color: "#9ca3af", fontFamily: "var(--mono)" }}>{label}…</span>
                    </div>
                  );
                })()}
                <div ref={chatEnd} />
              </div>
            )}

            {/* Input */}
            <div style={{ padding: "8px 14px 14px", flexShrink: 0 }}>
              {messages.length > 0 && (
                <div style={{
                  display: "flex", gap: 6, marginBottom: 10,
                  flexWrap: vw >= 640 ? "wrap" : "nowrap",
                  overflowX: vw >= 640 ? "visible" : "auto",
                }}>
                  {prompts.map((p, i) => (
                    <button key={i} onClick={() => send(p.text)} style={{
                      padding: "6px 12px", borderRadius: 20,
                      background: "#101413", border: "1px solid #262626",
                      color: "#cdd4d0", fontSize: 11, cursor: "pointer",
                      whiteSpace: "nowrap", fontFamily: "var(--mono)", flexShrink: 0,
                      display: "inline-flex", alignItems: "center", gap: 5,
                    }}><Icon name={p.icon} size={12} /> {vw >= 640 ? p.text : p.text.split(" ").slice(0, 3).join(" ") + "…"}</button>
                  ))}
                </div>
              )}
              <div style={{
                display: "flex", alignItems: "center", gap: 10,
                background: "#101413", borderRadius: 4, padding: "5px 5px 5px 16px",
                border: "1px solid #262626",
              }}>
                <svg width="18" height="18" fill="none" viewBox="0 0 24 24" style={{ flexShrink: 0, opacity: .35 }}>
                  <path d="M21 11.5a8.38 8.38 0 01-.9 3.8 8.5 8.5 0 01-7.6 4.7 8.38 8.38 0 01-3.8-.9L3 21l1.9-5.7a8.38 8.38 0 01-.9-3.8 8.5 8.5 0 014.7-7.6 8.38 8.38 0 013.8-.9h.5a8.48 8.48 0 018 8v.5z" stroke="#9ca3af" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
                <input
                  value={input}
                  onChange={e => setInput(e.target.value)}
                  onKeyDown={e => e.key === "Enter" && send()}
                  placeholder={t("Message BetMan...")}
                  style={{
                    flex: 1, padding: "11px 0", background: "transparent",
                    border: "none", color: "#f3f4f6", fontSize: 15, outline: "none",
                    fontFamily: "inherit", fontWeight: 400,
                  }}
                />
                <button onClick={() => send()} style={{
                  width: 40, height: 40, borderRadius: 4, border: "none",
                  background: input.trim() ? "#4ade80" : "#262626",
                  color: input.trim() ? "#000" : "#4b5563",
                  cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center",
                  transition: "all 0.2s", flexShrink: 0,
                }}>
                  <svg width="18" height="18" fill="none" viewBox="0 0 24 24">
                    <path d="M22 2L11 13M22 2l-7 20-4-9-9-4 20-7z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  </svg>
                </button>
              </div>
            </div>
          </div>
        )}

        {/* ═══ MATCHES ═══ */}
        {screen === "matches" && (
          <div style={{ flex: 1, overflow: "auto", padding: 14 }}>
            {!selectedMatch ? (
              <div style={{ display: "grid", gridTemplateColumns: "minmax(0, 1fr)", gap: 10 }}>
                <WorldCupBanner />
                <div style={{ fontSize: 10, color: "#4b5563", fontFamily: "var(--mono)", textTransform: "uppercase", letterSpacing: 0.4, marginBottom: 2, padding: "0 4px" }}>
                  {t("Upcoming Fixtures")}
                </div>
                {matchesLoading ? (
                  <div style={{ display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", padding: "60px 20px", gap: 16 }}>
                    <div style={{ display: "flex", gap: 6 }}>
                      {[0,1,2].map(i => (
                        <div key={i} style={{
                          width: 8, height: 8, borderRadius: "50%", background: "#4ade80",
                          animation: `dotPulse 1s ease ${i * 0.2}s infinite`,
                        }} />
                      ))}
                    </div>
                    <div style={{ fontSize: 13, color: "#6b7280" }}>{t("Fetching live fixtures...")}</div>
                  </div>
                ) : matches.length > 0 ? (
                  (() => {
                    // Build list of unique leagues present in fetched matches
                    const leaguesPresent = [...new Set(matches.map(m => m.league))];
                    const isWCName = (lg) => /world cup/i.test(lg) && !/club world cup/i.test(lg);
                    const hasWC = leaguesPresent.some(isWCName);
                    // WC 2026 group draws (December 2025).
                    // API-Football's round field is generic "Group Stage - 1/2/3" without
                    // a letter, so we derive the group from the team names instead.
                    const WC_2026_GROUPS = {
                      A: ["Mexico", "South Korea", "South Africa", "Czechia"],
                      B: ["Canada", "Qatar", "Bosnia and Herzegovina", "Switzerland"],
                      C: ["Brazil", "Morocco", "Haiti", "Scotland"],
                      D: ["USA", "Paraguay", "Australia", "Turkiye"],
                      E: ["Germany", "Ecuador", "Curacao", "Ivory Coast"],
                      F: ["Netherlands", "Japan", "Sweden", "Tunisia"],
                      G: ["Belgium", "Egypt", "Iran", "New Zealand"],
                      H: ["Spain", "Uruguay", "Saudi Arabia", "Cape Verde"],
                      I: ["France", "Norway", "Senegal", "Iraq"],
                      J: ["Argentina", "Algeria", "Austria", "Jordan"],
                      K: ["Portugal", "Colombia", "Uzbekistan", "DR Congo"],
                      L: ["England", "Croatia", "Ghana", "Panama"],
                    };
                    // Normalize a team name so different spellings (Czech Republic / Czechia,
                    // Bosnia & Herzegovina / Bosnia and Herzegovina, Türkiye / Turkiye / Turkey,
                    // United States / USA, Korea Republic / South Korea, etc.) all match.
                    const normTeam = (t) => (t || "")
                      .toLowerCase()
                      .normalize("NFD").replace(/[\u0300-\u036f]/g, "")  // strip diacritics
                      .replace(/&/g, "and")
                      .replace(/\bunited states( of america)?\b/g, "usa")
                      .replace(/\bkorea republic\b/g, "south korea")
                      .replace(/\bczech republic\b/g, "czechia")
                      .replace(/\bivoire\b/g, "ivory coast")
                      .replace(/\bcote d ivoire\b/g, "ivory coast")
                      .replace(/\bdr congo\b/g, "dr congo")
                      .replace(/\bdemocratic republic of (the )?congo\b/g, "dr congo")
                      .replace(/[^a-z0-9 ]/g, " ")
                      .replace(/\s+/g, " ")
                      .trim();
                    const teamToGroup = {};
                    Object.entries(WC_2026_GROUPS).forEach(([letter, teams]) => {
                      teams.forEach(t => { teamToGroup[normTeam(t)] = letter; });
                    });
                    const getGroupForTeam = (name) => teamToGroup[normTeam(name)] || null;
                    // Stage filter helper: map a match to a stage key.
                    // For group stage, derive the group letter from either team's name.
                    const matchToStage = (m) => {
                      const r = (m.round || "").toLowerCase();
                      if (!r) return null;
                      const groupMatch = r.match(/group\s+([a-l])/i);
                      if (groupMatch) return "group_" + groupMatch[1].toUpperCase();
                      if (/group stage/i.test(r)) {
                        const g = getGroupForTeam(m.home) || getGroupForTeam(m.away);
                        return g ? "group_" + g : null;
                      }
                      if (/round of 32|1\/16/i.test(r)) return "r32";
                      if (/round of 16|1\/8|8th final|achtste/i.test(r)) return "r16";
                      if (/quarter|1\/4|kwart/i.test(r)) return "qf";
                      if (/semi|1\/2|halve/i.test(r)) return "sf";
                      if (/3rd place|third place|brons/i.test(r)) return "bronze";
                      if (/^final|grand final/i.test(r) || r.trim() === "final") return "final";
                      return null;
                    };
                    // Apply league filter. The WC stage/group filters only apply
                    // when the World Cup league itself is selected — on "All" we
                    // just show all upcoming matches across every league.
                    let filtered = matches;
                    if (leagueFilter !== "all") filtered = filtered.filter(m => m.league === leagueFilter);
                    const wcSelected = isWCName(leagueFilter);
                    if (wcSelected) {
                      if (wcStageFilter !== "all") {
                        filtered = filtered.filter(m => isWCName(m.league) && matchToStage(m) === wcStageFilter);
                      } else {
                        // "Upcoming Fixtures": show each country only once (its earliest
                        // upcoming WC match). matches are already sorted by date.
                        const seenCountry = new Set();
                        filtered = filtered.filter(m => {
                          const h = normTeam(m.home), a = normTeam(m.away);
                          if (seenCountry.has(h) || seenCountry.has(a)) return false;
                          seenCountry.add(h); seenCountry.add(a);
                          return true;
                        });
                      }
                    }
                    // Group filtered matches by league
                    const grouped = {};
                    filtered.forEach(m => { (grouped[m.league] = grouped[m.league] || []).push(m); });
                    const saveLeague = (val) => { setLeagueFilter(val); try { localStorage.setItem("betman_league_filter", val); } catch (e) {} };
                    const saveStage = (val) => { setWcStageFilter(val); try { localStorage.setItem("betman_wc_stage", val); } catch (e) {} };
                    // Show WC stage row only when the World Cup league is selected
                    const showWcStageRow = hasWC && wcSelected;
                    const stages = [
                      { key: "all", label: t("Upcoming Fixtures") },
                      ...["A","B","C","D","E","F","G","H","I","J","K","L"].map(g => ({ key: "group_" + g, label: t("Group") + " " + g })),
                      { key: "r32", label: t("Round of 32") },
                      { key: "r16", label: t("Round of 16") },
                      { key: "qf",  label: t("Quarter Finals") },
                      { key: "sf",  label: t("Semi Finals") },
                      { key: "final", label: t("Final") },
                    ];
                    return (
                      <>
                        {/* League filter pills */}
                        <div style={{ display: "flex", gap: 6, flexWrap: "wrap", marginBottom: 8 }}>
                          {["all", ...leaguesPresent].map(lg => {
                            const active = leagueFilter === lg;
                            const label = lg === "all" ? t("All") : lg;
                            return (
                              <button key={lg} onClick={() => saveLeague(lg)} style={{
                                padding: "6px 12px", borderRadius: 4, fontSize: 11, fontWeight: 700,
                                background: active ? "#4ade8018" : "#101413",
                                border: `1px solid ${active ? "#4ade8040" : "#262626"}`,
                                color: active ? "#4ade80" : "#9ca3af",
                                cursor: "pointer", fontFamily: "var(--mono)",
                              }}>{label}</button>
                            );
                          })}
                        </div>

                        {/* WC stage / group filter row (wraps onto multiple lines) */}
                        {showWcStageRow && (
                          <div style={{ display: "flex", gap: 6, flexWrap: "wrap", paddingBottom: 6, marginBottom: 12 }}>
                            {stages.map(s => {
                              const active = wcStageFilter === s.key;
                              // Count fixtures available for this stage. For "all"
                              // (Upcoming Fixtures) count deduped-by-country matches.
                              let count;
                              if (s.key === "all") {
                                const seen = new Set();
                                count = matches.filter(m => {
                                  if (!isWCName(m.league)) return false;
                                  const h = normTeam(m.home), a = normTeam(m.away);
                                  if (seen.has(h) || seen.has(a)) return false;
                                  seen.add(h); seen.add(a);
                                  return true;
                                }).length;
                              } else {
                                count = matches.filter(m => isWCName(m.league) && matchToStage(m) === s.key).length;
                              }
                              const isEmpty = s.key !== "all" && count === 0;
                              return (
                                <button key={s.key} onClick={() => saveStage(s.key)} style={{
                                  padding: "6px 12px", borderRadius: 4, fontSize: 11, fontWeight: 700,
                                  background: active ? "#fbbf2418" : "#101413",
                                  border: `1px solid ${active ? "#fbbf2440" : "#262626"}`,
                                  color: active ? "#fbbf24" : (isEmpty ? "#4b5563" : "#9ca3af"),
                                  cursor: "pointer", fontFamily: "var(--mono)", whiteSpace: "nowrap",
                                }}>{s.label}{count > 0 ? ` · ${count}` : isEmpty ? " · TBD" : ""}</button>
                              );
                            })}
                          </div>
                        )}

                        {/* Grouped matches by league */}
                        {Object.keys(grouped).length === 0 ? (
                          <div style={{ textAlign: "center", padding: "40px 20px", color: "#6b7280", fontSize: 13 }}>
                            {wcStageFilter !== "all" && hasWC
                              ? t("No matches confirmed for this stage yet — fixtures will appear as the bracket fills in.")
                              : t("No matches in this league within the next 14 days.")}
                          </div>
                        ) : Object.entries(grouped).map(([lg, items]) => (
                          <div key={lg} style={{ marginBottom: 10 }}>
                            <div style={{
                              fontSize: 10, color: "#4ade80", fontFamily: "var(--mono)",
                              textTransform: "uppercase", letterSpacing: 0.4,
                              padding: "8px 4px 6px 4px", borderBottom: "1px solid #262626", marginBottom: 8,
                            }}>{lg} · {items.length}</div>
                            <div style={{ display: "grid", gridTemplateColumns: "minmax(0, 1fr)", gap: 8 }}>
                              {items.map(m => <MatchCard key={m.id} match={m} onSelect={selectMatch} />)}
                            </div>
                          </div>
                        ))}
                      </>
                    );
                  })()
                ) : matchesError ? (
                  <div style={{ textAlign: "center", padding: "60px 20px", color: "#6b7280", fontSize: 13 }}>
                    <div style={{ marginBottom: 12 }}>{t("Could not load fixtures")}</div>
                    <button onClick={() => { loadMatches(); }} style={{
                      padding: "8px 16px", borderRadius: 4, border: "1px solid #262626",
                      background: "#101413", color: "#4ade80", fontSize: 12, cursor: "pointer",
                      fontFamily: "var(--mono)",
                    }}>{t("Retry")}</button>
                  </div>
                ) : (
                  <div style={{ textAlign: "center", padding: "60px 20px", color: "#6b7280", fontSize: 13 }}>
                    <div style={{ display: "flex", justifyContent: "center", marginBottom: 16, color: "#4b5563" }}><Icon name="ball" size={40} strokeWidth={1.3} /></div>
                    <div style={{ fontSize: 15, color: "#e5e7eb", fontWeight: 700, marginBottom: 6 }}>{t("No upcoming matches")}</div>
                    <div style={{ fontSize: 12, color: "#4b5563", marginBottom: 16 }}>{t("No top-league fixtures found right now. Try refreshing.")}</div>
                    <button onClick={() => { loadMatches(); }} style={{
                      padding: "8px 16px", borderRadius: 4, border: "1px solid #262626",
                      background: "#101413", color: "#4ade80", fontSize: 12, cursor: "pointer",
                      fontFamily: "var(--mono)",
                    }}>{t("Refresh")}</button>
                  </div>
                )}
              </div>
            ) : (
              <div>
                <button onClick={() => { setSelectedMatch(null); }} style={{
                  background: "none", border: "none", color: "#4ade80", cursor: "pointer",
                  fontSize: 12, fontFamily: "var(--mono)", marginBottom: 14, display: "flex", alignItems: "center", gap: 4, padding: 0,
                }}>‹ {t("Back")}</button>
                <div style={{
                  background: "#060608", borderRadius: 4, padding: "26px 18px",
                  border: "1px solid #262626", marginBottom: 20,
                }}>
                  <div style={{ fontSize: 10, color: "#4b5563", fontFamily: "var(--mono)", textTransform: "uppercase", letterSpacing: 0.4, marginBottom: 22, textAlign: "center" }}>{selectedMatch.league} · {selectedMatch.date} · {selectedMatch.time}</div>
                  <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                    <div style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 12, flex: 1 }}>
                      {selectedMatch.homeLogo && <img src={selectedMatch.homeLogo} alt="" style={{ width: 50, height: 50, objectFit: "contain" }} />}
                      <div style={{ fontSize: 18, fontWeight: 700, color: "#f9fafb", textAlign: "center", lineHeight: 1.2 }}>{selectedMatch.home}</div>
                    </div>
                    <div style={{ fontSize: 13, fontWeight: 600, color: "#4b5563", fontFamily: "var(--mono)", padding: "0 14px" }}>VS</div>
                    <div style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 12, flex: 1 }}>
                      {selectedMatch.awayLogo && <img src={selectedMatch.awayLogo} alt="" style={{ width: 50, height: 50, objectFit: "contain" }} />}
                      <div style={{ fontSize: 18, fontWeight: 700, color: "#f9fafb", textAlign: "center", lineHeight: 1.2 }}>{selectedMatch.away}</div>
                    </div>
                  </div>
                </div>
                {detailLoading ? (
                  <div style={{ display: "flex", flexDirection: "column", alignItems: "center", padding: "40px 0", gap: 12 }}>
                    <div style={{ display: "flex", gap: 6 }}>
                      {[0,1,2].map(i => (
                        <div key={i} style={{ width: 8, height: 8, borderRadius: "50%", background: "#4ade80", animation: `dotPulse 1s ease ${i * 0.2}s infinite` }} />
                      ))}
                    </div>
                    <div style={{ fontSize: 12, color: "#6b7280", fontFamily: "var(--mono)" }}>{t("Loading odds, form & injuries...")}</div>
                  </div>
                ) : (
                  <MatchDetail match={selectedMatch} onReloadDetails={async (m) => {
                    setDetailLoading(true);
                    const fresh = await loadMatchDetails({ ...m, detailsLoaded: false });
                    detailCacheRef.current[fresh.id] = fresh;
                    setSelectedMatch(fresh);
                    setDetailLoading(false);
                  }} />
                )}
              </div>
            )}
          </div>
        )}

        {/* ═══ NEWS ═══ */}
        {screen === "news" && <NewsTab user={user} tier={subscription?.tier || "free"} requireLogin={() => { setMenuOpen(true); setAuthMode("signup"); }} openPaywall={() => setShowPaywall(true)} />}

      </main>

      {/* ─── Bottom Nav ─── */}
      <nav style={{
        display: "flex", padding: "6px 24px 16px", flexShrink: 0,
        borderTop: "1px solid #101413", background: "#000",
      }}>
        {[
          { id: "matches", label: t("Matches"), svg: <svg width="22" height="22" fill="none" viewBox="0 0 24 24"><circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="1.5"/><path d="M12 2a15.3 15.3 0 014 10 15.3 15.3 0 01-4 10 15.3 15.3 0 01-4-10 15.3 15.3 0 014-10zM2 12h20" stroke="currentColor" strokeWidth="1.5"/></svg> },
          { id: "home", label: t("Chat"), svg: <svg width="22" height="22" fill="none" viewBox="0 0 24 24"><path d="M21 11.5a8.38 8.38 0 01-.9 3.8 8.5 8.5 0 01-7.6 4.7 8.38 8.38 0 01-3.8-.9L3 21l1.9-5.7a8.38 8.38 0 01-.9-3.8 8.5 8.5 0 014.7-7.6 8.38 8.38 0 013.8-.9h.5a8.48 8.48 0 018 8v.5z" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/></svg> },
          { id: "news", label: t("News"), svg: <svg width="22" height="22" fill="none" viewBox="0 0 24 24"><path d="M4 5a1 1 0 011-1h11a1 1 0 011 1v13a2 2 0 002 2H6a2 2 0 01-2-2V5z" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/><path d="M17 8h2a1 1 0 011 1v9a2 2 0 01-2 2" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/><path d="M7 8h7M7 12h7M7 16h4" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/></svg> },
        ].map(n => (
          <button key={n.id} onClick={() => { setScreen(n.id); setSelectedMatch(null); }} style={{
            flex: 1, display: "flex", flexDirection: "column", alignItems: "center", gap: 4,
            background: "none", border: "none", cursor: "pointer",
            color: screen === n.id ? "#4ade80" : "#4b5563",
            transition: "color 0.2s", padding: "8px 0",
          }}>
            {n.svg}
            <span style={{ fontSize: 10, fontWeight: 600, fontFamily: "var(--mono)" }}>{n.label}</span>
          </button>
        ))}
      </nav>
    </div>
  );
}
